/*
 * Upgrade PDP -- product page interaction.
 *
 * Owns: sections/upgrade-pdp.liquid AND sections/upgrade-product.liquid --
 * one engine, two hosting sections. upgrade-pdp.liquid is the generic
 * /pages/device?device=SERIES page; upgrade-product.liquid is the real
 * Shopify /products/<handle> product template. Both render the identical
 * DOM contract (data-ug-pdp-* hooks); this file tells them apart at
 * runtime via the root's data-handle attribute (present only on the
 * product template) and picks a "page" vs "product" routing mode from it
 * -- see the UgPdp constructor and buildDeviceUrl. Does not touch
 * assets/upgrade-tradein.js, assets/upgrade-tradein.css or
 * sections/upgrade-tradein.liquid.
 *
 * DATA MODE (primary, always active): reads assets/upgrade-tradein-data.json
 * client-side, selects a device by the `?device=SERIES` URL parameter (or
 * the section's configured default), and renders the grade/storage buy box,
 * grade-comparison ladder, unit-identity panel, spec table and
 * related-devices rail from it. That file supplies device IDENTITY only --
 * series, label, brand, image, storage, code, the trade-in offers table and
 * the vs-brand-new market observation. It no longer supplies a sale price:
 * every sale price on this page comes from window.UpgradePrices (see
 * livePrice(), below).
 *
 * SHOPIFY-NATIVE MODE (progressive enhancement, best-effort): in the
 * background, tries GET /products/<handle>.js for the handle matching the
 * current device series (see SERIES_TO_HANDLE). If a real product exists
 * with Grade/Storage options matching the selected grade/storage, the buy
 * button goes live and posts to /cart/add.js for a genuine Shopify
 * checkout.
 *
 * THE BUY BUTTON IS ALWAYS RENDERED (BLK-4). It used to hide itself on
 * every failure path, with no message on the page and no warning in the
 * console, so a store whose product import had not run -- or whose option
 * had been renamed in the admin -- went commercially dark and looked
 * normal. It now always renders and always says why it cannot be used:
 *   - no product loaded, or no variant record for this grade and storage:
 *     disabled, labelled with the confirmed hotline. NOT "out of stock" --
 *     Upgrade holds the stock and this same page is quoting a price for it;
 *     what is missing is the online order route, not the phone.
 *   - variant exists but is unavailable: disabled, out of stock in this
 *     grade.
 * On every disabled path the variant id is REMOVED as well as the disabled
 * property set, so a permanently visible button can never carry a stale id
 * from a previous selection into /cart/add.js.
 *
 * INCOMING LINKS (BLK-2, read side). ?storage= and ?grade= are honoured on
 * arrival, so a customer coming from the trade-in page lands on the exact
 * variant they were quoted against instead of on variants[0]. ?grade= is
 * checked against the three-key whitelist before use -- GRADE_INFO[key] is
 * dereferenced in several places and an arbitrary URL value would throw.
 *
 * TRADE-IN CREDIT (ruling R1): an estimate honoured at inspection, never
 * money off this cart. The cart charges the full price. Since 28 Sep 2026
 * (LOG-021) a trade-in ends in an order: when a valuation is live, Add to
 * cart goes through UpgradeDeal.order(), which writes the trade-in onto the
 * phone's cart line as notes, checks the cart so exactly one phone carries it,
 * and parks the valuation (it comes back if that line is removed). The person who delivers checks the old phone at the door
 * and the customer pays only the difference.
 *
 * PRICING RULE (Omar Hussein, 22 Sep 2026): SHOPIFY IS THE ONLY SALE-PRICE
 * SOURCE. Every sale price shown anywhere in this file is read from
 * window.UpgradePrices -- the server-rendered map of Shopify variant SKU ->
 * price in whole EGP published into <head> by
 * snippets/upgrade-price-map.liquid. variant.list in
 * assets/upgrade-tradein-data.json is NOT read by this engine any more: it
 * was a second sale price that agreed with the one the customer is actually
 * charged only by luck. Change a price in the Shopify admin and this page
 * shows the new number on the next request, with no theme deploy.
 * See livePrice() for the accessor and for what a missing price renders as.
 *
 * PAINT, THEN REPAINT (22 Sep 2026). The price map is no longer guaranteed
 * to be finished when this file parses. Above 50 products
 * snippets/upgrade-price-map.liquid renders a Liquid seed and then walks
 * /collections/all-devices/products.json for the rest, so a device past the
 * seed reads as unpriced for as long as that walk takes. This page does NOT
 * wait for it: it paints immediately, exactly as it always has, and repaints
 * on the ug:prices:ready event -- see listenForPrices and repaintPrices. No
 * render on this page is gated behind UpgradePrices.whenReady(), on purpose;
 * the reasoning is written out at listenForPrices. Anything that painted as
 * "Call 19759 to order" only because the walk had not arrived becomes a real
 * price when it settles, and the two price-bearing renders -- updateAll()
 * and renderRelated() -- are the whole of what re-runs.
 *
 * COLOUR (Omar Hussein, 27 Sep 2026: "i wanna add the color variants so
 * that when people browse they can see the different colors"). Colour
 * variants, ported 27 Sep 2026 from the upgrade-i18n build, plain English.
 * An OPTIONAL third axis. When the Shopify product JSON carries an option
 * named Colour (or Color, any case), the variant map key becomes
 * grade|storage|colour and a colour radiogroup is mounted as a third row of
 * the option block, directly under the Storage row -- by this file, not by
 * the Liquid, so a product with only Grade and Storage renders only those two
 * rows and runs exactly the code path it always has. Availability per colour is the
 * Shopify variant's own `available` flag for the grade and storage on
 * screen; a colour with no orderable variant is disabled and says "Sold
 * out" in words. A colour the customer chose is never swapped for another
 * behind their back: if a grade or storage change sells it out, it stays
 * checked and Add to cart reads "Sold out in this colour" until they pick
 * again (review, 27 Sep 2026). Price does NOT vary by colour: every price
 * on this page is still UpgradePrices.getByCode(code, grade), which
 * collapses the colour suffix. See renderColours / resolveColour /
 * variantFor.
 *
 * Interface for the financing agent (not built here): this script keeps
 * #upgrade-finance-slot's data-series / data-grade / data-storage /
 * data-price attributes current, and dispatches a `ug:pdp:variant-changed`
 * CustomEvent on `document` on every change, detail:
 *   { series, device, grade, gradeLabel, storage, price, currency }
 * plus `colour` (display name) ONLY when the product has a Colour option and
 * an orderable colour is selected -- listeners must tolerate its absence.
 * `grade` and `price` are null until the shopper has picked a grade.
 */
(function () {
  'use strict';

  /* ---- ARABIC SLICE, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/00-PLAN.md 7.3)
     Every customer word this file writes is in EN below, EXACTLY as it was
     written inline before, and is read through T() / TN(). On an English page
     there is no dictionary (snippets/upgrade-i18n.liquid renders only on a
     non-primary language), so T() returns EN's own text: English output is
     identical by construction, and every test sandbox -- none of which has
     window.UpgradeI18n -- runs unchanged. On an Arabic page T() reads
     window.UpgradeI18n and falls back to EN for any key it does not hold.

     tools/build-i18n.js reads this table: it writes the upgrade.js.* keys of
     locales/en.default.json and the dictionary snippet from it. Change the
     English HERE, then run `node tools/build-i18n.js`. A key "area.name" is
     the locale key upgrade.js.area.name_html.
     {name} is a placeholder, filled by T(); a key whose text holds markup is
     written into innerHTML as it is, every other key is escaped first.
     Prices: money() is the ONE place a currency word is put beside a number
     (common.money), so the Arabic currency word (decision D2) is one value.
     Order notes, cart properties, the deal target and the variant-changed
     event stay ENGLISH DATA: none of them passes through T(). */
  var EN = {
    'common.grade_g': 'Gold',
    'common.grade_p': 'Platinum',
    'common.grade_d': 'Diamond',
    'common.battery_g': '85%+',
    'common.battery_p': '90%+',
    'common.battery_d': '95%+',
    'common.money': '{amount} EGP',
    'common.currency': 'EGP',
    'common.percent': '{n}%',
    'pdp.grade_g_cosmetic': 'Some scratches, visible close up.',
    'pdp.grade_p_cosmetic': 'Like new. No visible scratches.',
    'pdp.grade_d_cosmetic': 'No visible wear.',
    'pdp.spec_camera_dual': 'Dual, {lenses}',
    'pdp.spec_camera_triple': 'Triple, {lenses}',
    'pdp.spec_camera_quad': 'Quad, {lenses}',
    'pdp.spec_battery_approx': 'Li-Ion, approx. {capacity} mAh (factory spec)',
    'pdp.spec_battery_rated': 'Li-Ion, {capacity} mAh (factory spec)',
    'pdp.spec_os_ios': 'Shipped with {os}; supports current iOS updates',
    'pdp.spec_os_android': 'Shipped with {os}',
    'pdp.spec_brand': 'Brand',
    'pdp.spec_model': 'Model',
    'pdp.spec_storage_options': 'Storage options',
    'pdp.spec_ram': 'RAM',
    'pdp.spec_main_camera': 'Main camera',
    'pdp.spec_selfie_camera': 'Selfie camera',
    'pdp.spec_battery_capacity': 'Battery capacity (factory spec)',
    'pdp.spec_os': 'Operating system',
    'pdp.spec_connector': 'Connector',
    'pdp.spec_chipset': 'Chipset',
    'pdp.call_to_order': 'Call {hotline} to order',
    'pdp.colour_group_label': 'Colour',
    'pdp.colour_sold_out_value': '{colour}, sold out',
    'pdp.option_sold_out_in_grade': 'sold out in this grade',
    // One prompt per row, to agree with its noun (03-FIXES.md D7).
    'pdp.option_choose_one_grade': 'choose one',
    'pdp.option_choose_one_colour': 'choose one',
    'pdp.image_alt': '{device}, catalogue image',
    'pdp.grade_pill_battery': 'Battery {range}',
    'pdp.storage_sold_out': 'Sold out',
    'pdp.photo_n_of_total': 'Photo {n} of {total}',
    'pdp.related_from': 'From {price}',
    'pdp.related_grades_available': '3 grades available',
    'pdp.storage_sold_out_title': '{storage} is sold out in every grade.',
    'pdp.price_select_grade': 'Select a grade',
    'pdp.trade_covered_head': 'Your trade-in credit covers this phone in full',
    'pdp.trade_covered_note': '{value} estimated for your phone plus a guaranteed {bonus} trade-up bonus. Add to cart and your trade-in goes with the order. The cart shows the full price; when we deliver, we check your phone and pay you the difference.',
    'pdp.trade_covered_note_exact': '{value} estimated for your phone plus a guaranteed {bonus} trade-up bonus. Add to cart and your trade-in goes with the order. The cart shows the full price; when we deliver, we check your phone. Nothing to pay at delivery.',
    'pdp.trade_balance_head': '≈ <strong>{balance}</strong> after your trade-in credit',
    'pdp.trade_balance_note': '{value} estimated for your phone, confirmed when we check it at delivery, plus a guaranteed {bonus} trade-up bonus. Add to cart and your trade-in goes with the order. The cart shows the full price above; at delivery you pay only the difference.',
    'pdp.tradein_bonus_line': 'For this phone, that bonus is <strong>{amount}</strong>, guaranteed.',
    /* 31 Oct 2026 (LOG-069, Black Friday): the three trade notes above without the bonus, read only while the offer
       closes it. Each is its twin with "plus a guaranteed {bonus} trade-up bonus" taken out, nothing else. */
    'pdp.trade_covered_note_closed': '{value} estimated for your phone. Add to cart and your trade-in goes with the order. The cart shows the full price; when we deliver, we check your phone and pay you the difference.',
    'pdp.trade_covered_note_exact_closed': '{value} estimated for your phone. Add to cart and your trade-in goes with the order. The cart shows the full price; when we deliver, we check your phone. Nothing to pay at delivery.',
    'pdp.trade_balance_note_closed': '{value} estimated for your phone, confirmed when we check it at delivery. Add to cart and your trade-in goes with the order. The cart shows the full price above; at delivery you pay only the difference.',
    'pdp.vsnew_brand_new': 'Brand new <s>{price}</s>',
    'pdp.vsnew_save': 'You save {amount} · {percent}',
    'pdp.vsnew_source': 'New price observed at {source}, 3 Sep 2026.',
    'pdp.vsnew_source_fallback': 'authorised retail',
    'pdp.grade_meaning_label': '{grade}: ',
    'pdp.unit_grade_empty': 'Choose a grade above',
    'pdp.ladder_price': 'Price',
    'pdp.ladder_cosmetic': 'Cosmetic condition',
    'pdp.ladder_battery': 'Battery health',
    'pdp.ladder_identical': 'Identical on every grade: 70-step inspection · 12-month warranty · 14-day return · 30-day exchange · All taxes paid.',
    'pdp.offer_terms': 'Offer terms',
    'pdp.offer_name_fallback': 'Offer',
    'pdp.offer_pill': 'Offer',
    'pdp.offer_until_short': 'until {date}',
    'pdp.offer_until': 'Offer until {date}',
    'pdp.offer_save': 'You save {amount}',
    'pdp.offer_save_pct': 'You save {amount} ({percent})',
    'pdp.offer_sr_price': 'Offer price {price}',
    'pdp.offer_sr_was': ' Was {was}. You save {saving}.',
    'pdp.offer_sr_was_pct': ' Was {was}. You save {saving} ({percent}).',
    'pdp.offer_sr_ends': ', offer ends {date}.',
    'pdp.tradein_cta_title': 'Get a value for your current phone and put it towards this one.',
    'pdp.addcart_select_grade': 'Select a grade',
    'pdp.addcart_select_grade_title': 'Choose a grade above to add this phone to your cart.',
    'pdp.addcart_no_route_title': 'Online ordering is not available for this grade yet. Call {hotline} and we will help you.',
    'pdp.addcart_colour_sold_out': 'Sold out in this colour',
    'pdp.addcart_colour_sold_out_title': '{colour} is sold out in this grade and storage. Choose another colour, or call {hotline}.',
    'pdp.addcart_out_of_stock': 'Out of stock in this grade',
    'pdp.addcart_storage_sold_out_title': '{storage} is sold out in every grade. Choose another storage, or call {hotline}.',
    'pdp.addcart_out_of_stock_title': 'This grade is sold out. Another grade of the same phone may be available, or call {hotline}.',
    'pdp.addcart_no_price_title': 'We cannot show this phone’s price right now, so we will not take the order online. Call {hotline} and we will confirm the price and take it over the phone.',
    'pdp.add_to_cart': 'Add to cart',
    'pdp.adding': 'Adding…',
    'pdp.add_error_alert': 'We could not add that to your cart just now. Please try again in a moment, or call {hotline}.',
    'pdp.view_cart': 'View cart',
    'pdp.cart_has_tradein': 'Your cart already has a phone with a trade-in, and one trade-in goes with one phone.',
    'pdp.add_without_tradein': 'Add without trade-in',
    'pdp.announce_selected': '{grade} selected.',
    'pdp.announce_selected_storage': '{grade} selected, {storage}.',
    'pdp.announce_selected_colour': '{grade} selected, {colour}.',
    'pdp.announce_selected_storage_colour': '{grade} selected, {storage}, {colour}.',
    'pdp.announce_no_price': 'We cannot show a price for this grade right now.',
    'pdp.announce_price': '{price}.',
    'pdp.announce_orderable': 'Add to cart is available.',
    'pdp.announce_out_of_stock': 'Out of stock in this grade.',
    'pdp.announce_colour_sold_out': '{colour} is sold out in this grade and storage. Choose another colour.',
    'pdp.announce_no_grade': 'Choose a grade to add this phone to your cart.',
    'pdp.announce_call_to_order': 'Call {hotline} to order.',
    'pdp.announce_trade_covered': 'Your trade-in credit covers this phone in full on today’s estimate.',
    'pdp.announce_trade_balance': 'Estimated balance after your trade-in credit, {balance}. Your phone’s value is confirmed when we check it at delivery.',
    'pdp.not_found_whatsapp_message': 'Hi, I was looking for a device on the Upgrade site and could not find it.',
    'pdp.not_found_contact_whatsapp': 'message us on WhatsApp',
    'pdp.not_found_contact_call': 'call us on {hotline}',
    'pdp.not_found_heading': 'Device not found',
    'pdp.not_found_body': 'We could not find that device. {browse_link}, or {contact_link}.',
    'pdp.not_found_browse': 'Browse all devices',
    'pdp.error_whatsapp_message': 'Hi, I could not load pricing on the Upgrade site and would like help.',
    'pdp.error_contact_whatsapp': 'WhatsApp',
    'pdp.error_contact_call': 'call {hotline}',
    'pdp.error_heading': 'We could not load pricing right now',
    'pdp.error_body': 'Please refresh the page, or reach us directly on {contact_link}.'
  };
  var I18N = (typeof window !== 'undefined' && window.UpgradeI18n) || null;
  // {name} -> value; an unknown {name} is left as it is.
  function fill(str, vars) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? String(vars[name]) : whole;
    });
  }
  /* ---- EDIT EVERYTHING, 7 Oct 2026 (2026-10-06_lynkrs-tech-fixes/
     edit-everything-7oct/build/product.md). The content team edits the words
     below in the theme editor: the product section's "Words" blocks. The
     section prints each field that differs from the dictionary's own words
     for its key as <script type="application/json" data-ug-pdp-words>, keyed
     like EN (an unchanged page prints no tag), and T() reads it FIRST, then
     window.UpgradeI18n, then EN: EN and the locale keys stay the fallback.
     A field is used only when it keeps exactly its EN line's {placeholders}
     (none lost, none added) and, on a line that carries a computed figure
     (WORDS_NO_DIGITS), holds no digit: a price, a trade-in value or a bonus
     is never typed (LOG-044). On a right-to-left page each filled value is
     isolated (FSI/PDI) exactly as the dictionary isolates it. Read by the
     UgPdp constructor (readWords), from its own section. */
  var WORDS = null;
  var WORDS_LEAD_SPACE = { 'pdp.offer_sr_was': true, 'pdp.offer_sr_was_pct': true };
  var WORDS_NO_DIGITS = {
    'pdp.trade_covered_head': true, 'pdp.trade_covered_note': true, 'pdp.trade_covered_note_exact': true,
    'pdp.trade_balance_head': true, 'pdp.trade_balance_note': true, 'pdp.tradein_bonus_line': true,
    'pdp.related_from': true, 'pdp.offer_save': true, 'pdp.offer_save_pct': true, 'pdp.offer_sr_price': true,
    'pdp.offer_sr_was': true, 'pdp.offer_sr_was_pct': true, 'pdp.vsnew_brand_new': true, 'pdp.vsnew_save': true,
    'pdp.announce_price': true, 'pdp.announce_trade_balance': true, 'pdp.announce_trade_covered': true,
    // FIX PASS, 7 Oct 2026 (edit-everything-7oct/fixes/FIXES.md D18): the price slot's and the add-to-cart
    // button's prompt sit where the price shows; a typed figure there is refused too (LOG-044)
    'pdp.price_select_grade': true, 'pdp.addcart_select_grade': true
  };
  // FIX PASS (D21): a field keeps only these tags (a stray tag from an editor would break the markup); a field
  // with any other tag is ignored and the standard words show.
  var WORDS_TAGS_OK = /^<\/?(strong|s)>$|^<a href="[^"<>]*">$|^<\/a>$/;
  function tagsOk(v) {
    var tags = String(v).match(/<[^>]*>?/g) || [];
    for (var i = 0; i < tags.length; i++) { if (!WORDS_TAGS_OK.test(tags[i])) { return false; } }
    return true;
  }
  function placeholderSet(str) {
    var seen = {};
    (String(str).match(/\{\w+\}/g) || []).forEach(function (p) { seen[p] = true; });
    return Object.keys(seen).sort().join(' ');
  }
  function readWords(root) {
    WORDS = null;
    try {
      var el = (root && typeof root.querySelector === 'function') ? root.querySelector('script[data-ug-pdp-words]') : null;
      if (!el || String(el.tagName || '').toUpperCase() !== 'SCRIPT') { return; }
      var given = JSON.parse(el.textContent || '');
      if (!given || typeof given !== 'object') { return; }
      var out = {};
      var n = 0;
      Object.keys(given).forEach(function (key) {
        var v = given[key];
        if (!Object.prototype.hasOwnProperty.call(EN, key) || typeof EN[key] !== 'string') { return; }
        if (typeof v !== 'string' || !v.trim()) { return; }
        // a text field may lose a leading space; the screen-reader sentence follows the price after one
        if (WORDS_LEAD_SPACE[key] && v.charAt(0) !== ' ') { v = ' ' + v; }
        if (placeholderSet(v) !== placeholderSet(EN[key])) { return; }
        if (WORDS_NO_DIGITS[key] && /[0-9٠-٩۰-۹]/.test(v)) { return; }
        if (!tagsOk(v)) { return; }
        out[key] = v;
        n++;
      });
      if (n) { WORDS = out; }
    } catch (e) { WORDS = null; }
  }
  // {name} -> value, each value isolated on a right-to-left page (English: fill()).
  function fillWords(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? isolateText(vars[name]) : whole;
    });
  }
  // The section's words when it has the key, else the dictionary's, else EN's. Never throws.
  /* 31 Oct 2026 (LOG-069, Black Friday): the offer has closed the trade-up bonus -- same test as
     assets/upgrade-deal.js bonusClosed(), read from window.UpgradeFlags (printed in <head>). T() then reads a
     key's *_closed twin, before the section's own words, so no call site changes. */
  function bonusClosed() {
    var f = (typeof window !== 'undefined') ? window.UpgradeFlags : null;
    var o = f && typeof f === 'object' ? f.offer : null;
    return !!(o && typeof o === 'object' && o.enabled === true && o.bonusClosed === true);
  }
  function T(key, vars) {
    if (bonusClosed() && Object.prototype.hasOwnProperty.call(EN, key + '_closed')) { key = key + '_closed'; }
    if (WORDS && Object.prototype.hasOwnProperty.call(WORDS, key)) {
      try { return fillWords(WORDS[key], vars); } catch (e0) { /* fall through */ }
    }
    if (I18N) {
      try {
        if (I18N.has(key)) {
          var tr = I18N.t(key, vars);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    return fill(EN[key], vars);
  }
  // Plurals: EN[key] is { one, other } (English); the dictionary carries
  // key_zero ... key_other and picks by the page language's plural rules.
  function TN(key, n, vars) {
    var v = {};
    Object.keys(vars || {}).forEach(function (k) { v[k] = vars[k]; });
    if (v.count === undefined) { v.count = n; }
    if (I18N) {
      try {
        if (I18N.has(key + '_other')) {
          var tr = I18N.plural(key, n, v);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    var forms = EN[key];
    if (forms && typeof forms === 'object') { return fill(n === 1 ? forms.one : forms.other, v); }
    return fill(forms, v);
  }
  // Bidi isolation of a value set as text (a colour, a storage, a range), on
  // right-to-left pages only. English: the value, untouched.
  function isolateText(value) {
    var str = String(value == null ? '' : value);
    try {
      if (I18N && typeof I18N.dir === 'function' && I18N.dir() === 'rtl' && typeof I18N.isolate === 'function') {
        return I18N.isolate(str);
      }
    } catch (e) { /* English behaviour */ }
    return str;
  }
  // No bidi marks (U+2066-2069) in the tab title, a tooltip or a data- attribute
  // (03-FIXES.md D9). English never has them: a no-op there.
  function plainText(value) {
    return String(value == null ? '' : value).replace(/[⁦-⁩]/g, '');
  }
  // The merchant's bilingual fields ({ en, ar }), read in the page language.
  function pick(obj) {
    if (!obj || typeof obj !== 'object') { return ''; }
    try {
      if (I18N && typeof I18N.pick === 'function') {
        var p = I18N.pick(obj);
        if (typeof p === 'string') { return p; }
      }
    } catch (e) { /* English behaviour */ }
    return obj.en;
  }
  // The language prefix of links this file builds: '/' on English, '/ar/' on
  // Arabic. Same logic as assets/upgrade-deal.js routeRoot().
  function routeRoot() {
    var r = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  var ugAssetBase = (function () {
    var el = document.querySelector('[data-asset-base]');
    return el ? (el.getAttribute('data-asset-base') || '') : '';
  })();
  function deviceImage(dev) {
    if (!dev) return '';
    return (dev.image_cut && ugAssetBase) ? (ugAssetBase + dev.image_cut) : (dev.image || '');
  }

  // ADDED, 29 Sep 2026 (review fix, should-fix). PHOTOS requirement: the PDP
  // related rail should show Shopify product media when a product has it,
  // same as the homepage rail. Was never wired -- deviceImage() (used by
  // renderRelated()'s cards) only ever read the static cut-out/original
  // pair, never window.UpgradeFacts. Returns {src, isPhoto} so the caller
  // can apply the card contract's --photo modifier (no key light, no foot
  // shadow) only when the src is a real photograph, never on the cut-out.
  // F7 fix, 29 Sep 2026 (04-LIVE-LAYOUT-AUDIT.md; caldwell-theme): width/
  // height added to the return shape (additive -- every existing caller
  // that only reads .src/.isPhoto is unaffected). window.UpgradeFacts.
  // imageFor() (snippets/upgrade-catalogue-facts.liquid) already carries a
  // real photo's own intrinsic size; the fallback cut-out has none (it is
  // sized by upgrade-pdp.css's own 68%-inset rule, not by intrinsic
  // pixels), so those two fields stay 0 on that branch, same as before.
  function deviceImageInfo(dev) {
    if (!dev) return { src: '', isPhoto: false, width: 0, height: 0 };
    if (typeof window !== 'undefined' && window.UpgradeFacts && typeof window.UpgradeFacts.imageFor === 'function') {
      var photo = window.UpgradeFacts.imageFor(dev.series || '');
      if (photo && photo.src) {
        return {
          src: photo.src,
          isPhoto: true,
          width: (typeof photo.width === 'number' && photo.width > 0) ? photo.width : 0,
          height: (typeof photo.height === 'number' && photo.height > 0) ? photo.height : 0
        };
      }
    }
    return { src: deviceImage(dev), isPhoto: false, width: 0, height: 0 };
  }

  // Lynkrs batch 2, D4 / SEO-Images (W4, 6 Oct 2026). Every theme cut-out,
  // assets/ugdev-<name>.png, has a WebP twin beside it, assets/ugdev-<name>.webp.
  // Returns the twin's URL for a theme cut-out and '' for anything else: a real
  // Shopify photo already comes from Shopify's CDN in the browser's best format.
  // The PNG stays the <img> src, so a browser without WebP still gets a picture.
  function webpTwin(src) {
    var s = String(src || '');
    if (!ugAssetBase || s.indexOf(ugAssetBase) !== 0) { return ''; }
    var m = /^(.*\/ugdev-[a-z0-9-]+)\.png(\?.*)?$/i.exec(s);
    return m ? m[1] + '.webp' + (m[2] || '') : '';
  }
  // The gallery's fallback <img> sits in a <picture> (both product sections).
  // Writes, replaces or removes that picture's WebP <source> BEFORE the img's
  // src changes, so the browser fetches one file, not both. No <picture>
  // parent (a page built before this change, a test stub): does nothing.
  function setWebpSource(img, webp) {
    var pic = img && img.parentNode;
    if (!pic || String(pic.nodeName || '').toUpperCase() !== 'PICTURE') { return; }
    var source = pic.querySelector('source[type="image/webp"]');
    if (!webp) { if (source) { pic.removeChild(source); } return; }
    if (!source) {
      source = document.createElement('source');
      source.setAttribute('type', 'image/webp');
      pic.insertBefore(source, img);
    }
    source.setAttribute('srcset', webp);
  }
  // D4 for Shopify photos on the related rail (W4, checker C7, 6 Oct 2026).
  // UpgradeFacts.imageFor() hands over the photo at width 800 (image_url:
  // width: 800 in snippets/upgrade-catalogue-facts.liquid). Shopify's CDN
  // serves any width from the same URL's width= parameter, so the card gets
  // the same 400/600/800 srcset and sizes as snippets/upgrade-productcard
  // .liquid. A URL without a width= parameter gets no srcset.
  var PHOTO_CARD_WIDTHS = [400, 600, 800];
  var PHOTO_CARD_SIZES = '(min-width: 1200px) 280px, (min-width: 750px) 25vw, 50vw';
  function photoSrcset(src) {
    var s = String(src || '');
    if (!/[?&]width=\d+/.test(s)) { return ''; }
    return PHOTO_CARD_WIDTHS.map(function (w) {
      return s.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  // ---- Lynkrs batch 2 (W4, 6 Oct 2026): model order and the related rail --
  //
  // modelInfo() reads a device's family, generation and tier from its series
  // code in assets/upgrade-tradein-data.json (IPHONE15PROMAX, S24ULTRA, ...).
  // The data file's own order is never changed (it is pinned by the price
  // tests); every order below is computed here.
  //   tier: 0 base, 1 Plus, 2 Pro, 3 Pro Max / Ultra.
  // A series this does not recognise returns null and is left out of the rail
  // rather than placed by guesswork.
  var TIER_APPLE = { '': 0, PLUS: 1, PRO: 2, PROMAX: 3 };
  var TIER_SAMSUNG = { '': 0, PLUS: 1, ULTRA: 3 };
  function modelInfo(dev) {
    var s = String((dev && dev.series) || '').toUpperCase();
    var m = /^IPHONE(\d+)(PROMAX|PRO|PLUS)?$/.exec(s);
    if (m) { return { family: 'apple', familyRank: 0, gen: parseInt(m[1], 10), tier: TIER_APPLE[m[2] || ''] }; }
    m = /^S(\d+)(ULTRA|PLUS)?$/.exec(s);
    if (m) { return { family: 'samsung', familyRank: 1, gen: parseInt(m[1], 10), tier: TIER_SAMSUNG[m[2] || ''] }; }
    return null;
  }
  // The shared model order (batch 2 decision D3): Apple, then Samsung; the
  // newest generation first; within a generation the top model first (Pro Max
  // or Ultra, then Pro, then Plus, then the base model). Unknown series last,
  // in data-file order (Array.prototype.sort is stable).
  function compareModels(a, b) {
    var x = modelInfo(a), y = modelInfo(b);
    if (!x || !y) { return (x ? 0 : 1) - (y ? 0 : 1); }
    if (x.familyRank !== y.familyRank) { return x.familyRank - y.familyRank; }
    if (x.gen !== y.gen) { return y.gen - x.gen; }
    return y.tier - x.tier;
  }
  // PG-PDP-4: "You might also like" never suggests an older phone. Only the
  // same brand, the same generation or a newer one. In this order:
  //   1. the Pro sibling: the same generation's next Pro-class model up (Pro
  //      for a base or Plus iPhone, Pro Max for a Pro, Ultra for a base or
  //      Plus Galaxy);
  //   2. the same model in the next generations, the nearest first
  //      (iPhone 15 -> 16 -> 17);
  //   3. the other models of newer generations, the nearest generation first,
  //      the closest step up first;
  //   4. the other models of the same generation, the next step up first, then
  //      the next step down.
  // At most `count`; fewer when fewer qualify (the newest Pro Max shows its two
  // siblings only). Ties keep compareModels() order.
  function relatedFor(current, all, count) {
    var cur = modelInfo(current);
    if (!cur) { return []; }
    var sameGen = (all || []).filter(function (dv) {
      var x = modelInfo(dv);
      return x && x.family === cur.family && x.gen === cur.gen;
    }).map(function (dv) { return modelInfo(dv).tier; });
    var proTier = null;
    [2, 3].forEach(function (t) {
      if (proTier === null && t > cur.tier && sameGen.indexOf(t) !== -1) { proTier = t; }
    });
    function step(t) { return t > cur.tier ? t - cur.tier : 10 + (cur.tier - t); }
    function rank(x) {
      if (x.gen === cur.gen && x.tier === proTier) { return [0, 0, 0]; }
      if (x.gen > cur.gen && x.tier === cur.tier) { return [1, x.gen, 0]; }
      if (x.gen > cur.gen) { return [2, x.gen, step(x.tier)]; }
      return [3, 0, step(x.tier)];
    }
    var picked = (all || []).filter(function (dv) {
      var x = modelInfo(dv);
      return x && dv.series !== current.series && x.family === cur.family && x.gen >= cur.gen;
    });
    picked.sort(function (a, b) {
      var ra = rank(modelInfo(a)), rb = rank(modelInfo(b));
      for (var i = 0; i < ra.length; i++) { if (ra[i] !== rb[i]) { return ra[i] - rb[i]; } }
      return compareModels(a, b);
    });
    return picked.slice(0, Math.max(0, count | 0));
  }

  // Left-to-right order used everywhere on this page: buy box, ladder,
  // grade-meaning callout. Ascending by price.
  var GRADE_ORDER = ['g', 'p', 'd'];

  // REVIEW 8 OCT (2026-10-08_post-launch-fixes/theme/REVIEW-pdp.md R3): the
  // longest the buy button says it is loading before it falls back to the
  // hotline state. The live fetch took 1.2-1.7 s; this is the price walk's
  // own per-page wait (PAGE_TIMEOUT_MS), so a slow phone is not cut short.
  var LOADING_WAIT_MS = 8000;

  // These strings render into the "Cosmetic condition" row of the grade
  // ladder (renderLadder, below). Three rules govern what may go in them.
  //
  // 1. COSMETIC ONLY. The row is headed "Cosmetic condition"; battery has
  //    its own row. BATTERY, 26 September 2026 -- Omar: "we give a range
  //    from to for each grade, from 85-90 90-95 95-100" (superseded 7 Oct 2026 by LOG-045: minimums 85%+ / 90%+ / 95%+). The per-unit
  //    "measured and shown at handover" promise is withdrawn everywhere, and
  //    each grade states its minimum (GRADE_INFO[k].battery below), rendered in
  //    the ladder's Battery health row and the "The grade you chose" panel
  //    (was "This unit", launch punch list B10, 27 Sep 2026). Keep the
  //    ranges out of the cosmetic strings so the two rows never repeat.
  // 2. NO FUNCTIONAL GUARANTEE. Gold previously ended "Certified 100%
  //    functional." Nothing in 00-COMPANY/ supports an absolute functional
  //    guarantee, and no third party certifies anything here, so the word
  //    "certified" had no referent. The sourced fact is the 70-step
  //    inspection, which the ladder footer already states for every grade --
  //    nothing is lost by deleting the claim.
  // 3. NO ABSOLUTES WITHOUT A STANDARD. "No signs of use" / "no marks of any
  //    kind" are unfalsifiable promises an inspector cannot pass or fail a
  //    unit against. Observable wording only.
  var GRADE_INFO = {
    g: {
      label: 'Gold',
      code: 'RG',
      // Sourced: 00-COMPANY/05-products-and-grading.md -- ERP definition
      // for RG is "some scratches".
      cosmetic: 'Some scratches, visible close up.',
      // Minimum battery health by grade -- LOG-045, 7 Oct 2026 (replaces the 26 Sep 2026 ranges).
      battery: '85%+'
    },
    p: {
      label: 'Platinum',
      code: 'RP',
      // Sourced: 00-COMPANY/05-products-and-grading.md -- ERP definition
      // for RP is "like new, no scratches".
      cosmetic: 'Like new. No visible scratches.',
      battery: '90%+'
    },
    d: {
      label: 'Diamond',
      code: 'RD',
      // FLAG for serrano-qa: Diamond's cosmetic wording has no confirmed
      // ERP source (RD was added after the 2023 master-data guide). This
      // is the conservative placeholder the build ruling specifies --
      // do not sharpen it without serrano-qa sign-off. It is deliberately
      // the same phrase the grades page uses ("no visible wear") so the two
      // surfaces cannot drift apart again; the internal intake vocabulary
      // ("Flawless", "buy-grade") stays out of customer copy, because on
      // this site those words name what Upgrade BUYS at, not what it sells.
      cosmetic: 'No visible wear.',
      // Clarified 27 Sep 2026 -- Omar: "GOLD 85-90 PLATNIUM 90-95 DIAMOND 95 +". Minimums since 7 Oct 2026 (LOG-045).
      battery: '95%+'
    }
  };

  // The whitelist. Every grade key entering this engine from outside --
  // a URL parameter, a click handler, another surface's handoff link -- is
  // checked here first, because GRADE_INFO[key] is dereferenced downstream
  // (grade meaning, unit panel, variant-changed event, deal state) and an
  // unknown key would throw and take the page down rather than degrade.
  function isValidGrade(key) {
    return GRADE_ORDER.indexOf(key) !== -1;
  }

  // Arabic slice, 4 Oct 2026: what a customer READS for a grade. GRADE_INFO
  // above stays the English DATA (the deal target, gradeKeyFrom, the
  // variant-changed event); these return the same words through T().
  function gradeName(key) { return isValidGrade(key) ? T('common.grade_' + key) : ''; }
  function gradeCosmetic(key) { return isValidGrade(key) ? T('pdp.grade_' + key + '_cosmetic') : ''; }
  function gradeBattery(key) { return isValidGrade(key) ? T('common.battery_' + key) : ''; }

  // Manufacturer spec values (DEVICE_SPECS, below) are kept as the English
  // data they are; the three that carry words are read through T() by shape.
  // Anything that matches no shape (chipset, connector, selfie camera) is a
  // proper name or a figure and is shown as it is.
  function specText(value) {
    var str = String(value == null ? '' : value);
    var m;
    if ((m = /^(Dual|Triple|Quad), (.+)$/.exec(str))) { return T('pdp.spec_camera_' + m[1].toLowerCase(), { lenses: m[2] }); }
    if ((m = /^Li-Ion, approx\. (.+) mAh \(factory spec\)$/.exec(str))) { return T('pdp.spec_battery_approx', { capacity: m[1] }); }
    if ((m = /^Li-Ion, (.+) mAh \(factory spec\)$/.exec(str))) { return T('pdp.spec_battery_rated', { capacity: m[1] }); }
    if ((m = /^Shipped with (.+); supports current iOS updates$/.exec(str))) { return T('pdp.spec_os_ios', { os: m[1] }); }
    if ((m = /^Shipped with (.+)$/.exec(str))) { return T('pdp.spec_os_android', { os: m[1] }); }
    return str;
  }

  function gradeInfo(key) {
    return isValidGrade(key) ? GRADE_INFO[key] : null;
  }

  function gradeKeyFrom(value) {
    var v = String(value == null ? '' : value).trim().toLowerCase();
    if (isValidGrade(v)) { return v; }
    if (v === 'gold') { return 'g'; }
    if (v === 'platinum') { return 'p'; }
    if (v === 'diamond') { return 'd'; }
    return null;
  }

  // A store that goes dark commercially must say so somewhere. Every path
  // that leaves the buy button disabled writes one of these.
  function warn(msg) {
    try {
      if (window.console && typeof console.warn === 'function') {
        console.warn('[upgrade-pdp] ' + msg);
      }
    } catch (e) { /* console unavailable -- non-fatal */ }
  }

  // Option names this theme will accept for each axis. A merchandiser
  // renaming "Storage" to "Capacity" in the Shopify admin used to take the
  // buy button offline on every product page with no signal anywhere;
  // anything outside these lists still warns rather than guessing at
  // option positions.
  //
  // colour is the one OPTIONAL axis (Colour variants, ported 27 Sep 2026).
  // Grade and storage are required -- without them nothing can be ordered
  // and the page says so. A product without a colour option is the normal
  // state of every product imported before the colour write, and it is NOT
  // an error: the variant map stays keyed grade|storage and nothing about the
  // page changes.
  var OPTION_SYNONYMS = {
    grade: ['grade', 'condition'],
    storage: ['storage', 'capacity', 'size'],
    colour: ['colour', 'color']
  };

  function matchOptionIndex(names, synonyms) {
    for (var i = 0; i < names.length; i++) {
      if (synonyms.indexOf(String(names[i]).trim().toLowerCase()) !== -1) { return i; }
    }
    return -1;
  }

  // Option NAMES were normalised above; option VALUES were not, and the
  // storage value is half of the variant-map key. The CSV import decides how
  // the storage option is spelled on the product ("256 GB", "256gb") while
  // upgrade-tradein-data.json spells it "256GB" -- an exact-string key meant
  // any drift between the two missed on every grade of every device and
  // dropped the whole page to the disabled "Call ... to order" state with no
  // signal anywhere. Case and whitespace are not meaningful in a capacity
  // label, so neither is allowed to decide whether the store can take an
  // order.
  function storageKey(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, '');
  }

  // Colour variants, ported 27 Sep 2026. The colour half of a three-part
  // variant-map key. Same rule as the SKU's COLOUR CODE (upper-cased,
  // non-alphanumerics removed), lower-cased here, so "Natural Titanium",
  // "natural titanium" and the SKU's NATURALTITANIUM all meet on one key. A
  // value with no Latin letters or digits at all (a translated option value)
  // would strip to nothing, so it keys on itself.
  function colourKey(value) {
    var s = String(value == null ? '' : value).trim().toLowerCase();
    return s.replace(/[^a-z0-9]/g, '') || s.replace(/\s+/g, '');
  }

  // The swatch colour for a colour NAME, from window.UpgradeColours
  // (assets/upgrade-colours.js, loaded deferred in <head>). null -- an unknown
  // name, the file missing, or a value the browser would not paint -- means
  // the chip draws a neutral outlined dot. The name is always written beside
  // the dot in words, so the swatch is never the only thing saying the colour.
  function swatchColour(name) {
    var api = window.UpgradeColours;
    if (!api || typeof api.hex !== 'function') { return null; }
    var c = null;
    try { c = api.hex(name); } catch (e) { return null; }
    if (typeof c !== 'string' || !c.trim()) { return null; }
    c = c.trim();
    try {
      if (window.CSS && typeof window.CSS.supports === 'function' && !window.CSS.supports('color', c)) { return null; }
    } catch (e2) { /* CSS.supports unavailable -- let the browser drop it */ }
    return c;
  }

  // Maps a device series (as used in upgrade-tradein-data.json) to the
  // product handle upgrade-products.csv would create if imported. Used
  // only to *try* the Shopify-native enhancement; if the fetch 404s,
  // JSON-only mode continues unaffected.
  // All 27 devices as of 23 September 2026, matching the catalogue team's
  // Shopify import one for one. It was ten until then, and the ten were the
  // only ten the storefront had ever carried -- so an unmapped handle had
  // never actually occurred in production and the silent fallback below it
  // had never fired. Importing seventeen more products would have fired it
  // seventeen times: see the resolution comment in init().
  // ADDING A DEVICE: this map, assets/upgrade-tradein-data.json (via
  // private-data/build-public-data.js) and the Shopify product must all three
  // gain it. Two out of three renders the wrong phone or renders nothing.
  var SERIES_TO_HANDLE = {
    S22ULTRA: 'galaxy-s22-ultra',
    IPHONE13: 'iphone-13',
    IPHONE13PRO: 'iphone-13-pro',
    IPHONE13PROMAX: 'iphone-13-pro-max',
    IPHONE14: 'iphone-14',
    IPHONE14PLUS: 'iphone-14-plus',
    IPHONE14PRO: 'iphone-14-pro',
    IPHONE14PROMAX: 'iphone-14-pro-max',
    IPHONE15: 'iphone-15',
    IPHONE15PLUS: 'iphone-15-plus',
    IPHONE15PRO: 'iphone-15-pro',
    IPHONE15PROMAX: 'iphone-15-pro-max',
    IPHONE16: 'iphone-16',
    IPHONE16PLUS: 'iphone-16-plus',
    IPHONE16PRO: 'iphone-16-pro',
    IPHONE16PROMAX: 'iphone-16-pro-max',
    IPHONE17: 'iphone-17',
    IPHONE17PRO: 'iphone-17-pro',
    IPHONE17PROMAX: 'iphone-17-pro-max',
    S23: 'galaxy-s23',
    S23PLUS: 'galaxy-s23-plus',
    S23ULTRA: 'galaxy-s23-ultra',
    S24: 'galaxy-s24',
    S24PLUS: 'galaxy-s24-plus',
    S24ULTRA: 'galaxy-s24-ultra',
    S25: 'galaxy-s25',
    S25PLUS: 'galaxy-s25-plus',
    S25ULTRA: 'galaxy-s25-ultra'
  };

  // Reverse of SERIES_TO_HANDLE, built once. Lets a real Shopify product
  // template (sections/upgrade-product.liquid, /products/<handle>) resolve
  // its device series directly from product.handle, passed in via a
  // data-handle attribute -- no query parameter required. Single source of
  // truth: this is derived from SERIES_TO_HANDLE, never maintained by hand,
  // so the two maps cannot drift apart.
  var HANDLE_TO_SERIES = {};
  Object.keys(SERIES_TO_HANDLE).forEach(function (series) {
    HANDLE_TO_SERIES[SERIES_TO_HANDLE[series]] = series;
  });

  // Manufacturer-published specifications. These are general public
  // device facts (Apple/Samsung's own spec sheets), NOT sourced from an
  // Upgrade company file, and are NOT a business or market claim -- they
  // are included to match the incumbent site's real spec-table pattern
  // (confirmed on its own Galaxy S23 PDP during recon). Flagged for the
  // catalogue owner to verify against manufacturer spec sheets before
  // this goes live. Battery figures are the factory rating, never to be
  // confused with the per-unit measured "battery health" shown in the
  // unit-identity panel.
  var DEVICE_SPECS = {
    IPHONE13: {
      chipset: 'Apple A15 Bionic',
      mainCamera: 'Dual, 12MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, approx. 3,240 mAh (factory spec)',
      os: 'Shipped with iOS 15; supports current iOS updates',
      connector: 'Lightning'
    },
    IPHONE14: {
      chipset: 'Apple A15 Bionic (5-core GPU)',
      mainCamera: 'Dual, 12MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, approx. 3,279 mAh (factory spec)',
      os: 'Shipped with iOS 16; supports current iOS updates',
      connector: 'Lightning'
    },
    IPHONE15: {
      chipset: 'Apple A16 Bionic',
      mainCamera: 'Dual, 48MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, approx. 3,349 mAh (factory spec)',
      os: 'Shipped with iOS 17; supports current iOS updates',
      connector: 'USB-C'
    },
    IPHONE16: {
      chipset: 'Apple A18',
      mainCamera: 'Dual, 48MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, approx. 3,561 mAh (factory spec)',
      os: 'Shipped with iOS 18; supports current iOS updates',
      connector: 'USB-C'
    },
    IPHONE15PROMAX: {
      chipset: 'Apple A17 Pro',
      mainCamera: 'Triple, 48MP + 12MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, approx. 4,441 mAh (factory spec)',
      os: 'Shipped with iOS 17; supports current iOS updates',
      connector: 'USB-C'
    },
    S23: {
      chipset: 'Qualcomm Snapdragon 8 Gen 2',
      mainCamera: 'Triple, 50MP + 10MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, 3,900 mAh (factory spec)',
      os: 'Shipped with Android 13, One UI 5.1',
      connector: 'USB-C',
      ram: '8GB'
    },
    S23PLUS: {
      chipset: 'Qualcomm Snapdragon 8 Gen 2',
      mainCamera: 'Triple, 50MP + 10MP + 12MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, 4,700 mAh (factory spec)',
      os: 'Shipped with Android 13, One UI 5.1',
      connector: 'USB-C',
      ram: '8GB'
    },
    S24: {
      chipset: 'Qualcomm Snapdragon 8 Gen 3 for Galaxy',
      mainCamera: 'Triple, 50MP + 12MP + 10MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, 4,000 mAh (factory spec)',
      os: 'Shipped with Android 14, One UI 6.1',
      connector: 'USB-C',
      ram: '8GB'
    },
    S24ULTRA: {
      chipset: 'Qualcomm Snapdragon 8 Gen 3 for Galaxy',
      mainCamera: 'Quad, 200MP + 12MP + 50MP + 10MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, 5,000 mAh (factory spec)',
      os: 'Shipped with Android 14, One UI 6.1',
      connector: 'USB-C',
      ram: '12GB'
    },
    S25ULTRA: {
      chipset: 'Qualcomm Snapdragon 8 Elite for Galaxy',
      mainCamera: 'Quad, 200MP + 50MP + 50MP + 10MP',
      selfieCamera: '12MP',
      battery: 'Li-Ion, 5,000 mAh (factory spec)',
      os: 'Shipped with Android 15, One UI 7',
      connector: 'USB-C',
      ram: '12GB'
    }
  };

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  // Arabic slice, 4 Oct 2026: on a right-to-left page the figure is isolated
  // (FSI ... PDI) so a price, a sign or a range can never be reordered by the
  // Arabic around it. Only the NUMBER is isolated, never the currency word.
  // English: exactly the old output. Digits stay 0-9 in both (LOG-043).
  function formatMoney(n) {
    return isolateText(Number(n).toLocaleString('en-US'));
  }

  // "<figure> EGP" in the page language (common.money).
  function money(n) {
    return T('common.money', { amount: formatMoney(n) });
  }

  // The small currency mark after a big price: <span class="ug-price__ccy">.
  function ccyHtml() {
    return '<span class="ug-price__ccy">' + escapeHtml(T('common.currency')) + '</span>';
  }

  function digitsOnly(str) {
    return (str || '').replace(/\D/g, '');
  }

  // No verified WhatsApp number exists for this demo (house rule: never invent
  // one). When whatsapp_number is blank, every contact point below falls back
  // to the confirmed hotline (tel:) instead of emitting a dead wa.me link that
  // looks live once a grade is picked.
  function buildContact(config, message) {
    var waDigits = digitsOnly(config.whatsappNumber);
    if (waDigits) {
      return {
        href: 'https://wa.me/' + waDigits + '?text=' + encodeURIComponent(message),
        isWhatsapp: true
      };
    }
    var hotlineDigits = digitsOnly(config.hotlineNumber) || '19759';
    return { href: 'tel:' + hotlineDigits, isWhatsapp: false };
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ---- the live sale price (the only sale-price source) -----------------
  //
  // window.UpgradePrices is the contract published by
  // snippets/upgrade-price-map.liquid, mounted once per document in <head>
  // by layout/theme.liquid. It is a Liquid-rendered map of Shopify variant
  // SKU -> sale price in whole EGP, read out of the "all-devices"
  // collection at request time. It is defined synchronously before this
  // file parses, so there is nothing here to fetch, poll or wait for.
  //
  // SKU is <device-code>-{RG|RP|RD}; getByCode() builds it from the device
  // code this engine already holds (variant.code in
  // assets/upgrade-tradein-data.json) and the g/p/d grade key this file
  // already uses, so every call site below is a one-for-one substitution
  // for the variant.list[grade] read it replaces. Same integer EGP, same
  // grade keys, so formatMoney() is unchanged.
  function priceApi() {
    return (window && window.UpgradePrices) || null;
  }

  var warnedNoPriceMap = false;

  // ---- "is this null a fact, or just not loaded yet?" -------------------
  //
  // Above 50 products the map is no longer finished when this file parses:
  // snippets/upgrade-price-map.liquid renders a Liquid SEED of up to 50
  // products and then WALKS /collections/all-devices/products.json for the
  // rest. While that walk is in flight get() returns null for every device
  // past the seed, and a null from an unfinished walk means something
  // completely different from a null from a finished one:
  //
  //   covers(sku) === true   -> Shopify genuinely holds no price for this
  //                             SKU. A real outage. Somebody has to fix it.
  //   covers(sku) === false  -> the walk has not reached this SKU yet. A
  //                             repaint is coming (ug:prices:ready).
  //
  // What is rendered is IDENTICAL in both cases -- the hotline, never a
  // blank, never 0, never the figure this surface was holding a moment ago.
  // A shopper cannot act on "not yet", and payment is cash on delivery, so a
  // number this page cannot stand behind must not reach the screen either
  // way. What covers() changes is the WARNING: a genuine outage is said out
  // loud once, an unfinished walk stays silent. Without that split, every
  // page on a store past 50 products would fill the console with outages
  // that resolve themselves a few hundred milliseconds later, and the real
  // ones would be indistinguishable from the noise.
  //
  // EVERYTHING HERE IS FEATURE-DETECTED, and `complete` is compared with
  // `=== false` rather than tested for truthiness on purpose. A price map
  // that predates the walk -- and the stub tests/engines.test.js builds,
  // which carries neither `complete` nor `whenReady` nor `covers` -- has no
  // walk to wait for, so its nulls are already final and must read as
  // settled. `!api.complete` would read `undefined` as "still walking" and
  // silently change how those consumers behave.
  function pricesSettled(api) {
    if (!api) { return false; }
    return api.complete !== false && api.pending !== true;
  }

  function skuSettled(api, sku) {
    if (!api) { return false; }
    if (sku && typeof api.covers === 'function') {
      try { return api.covers(sku) === true; } catch (e) { return pricesSettled(api); }
    }
    return pricesSettled(api);
  }

  function skuOf(api, code, gradeKey) {
    if (!api || typeof api.skuFor !== 'function') { return null; }
    try { return api.skuFor(code, gradeKey) || null; } catch (e) { return null; }
  }

  // A store that goes dark on one SKU must say so somewhere a human looks --
  // the same convention as every other disabled path in this file. Once per
  // SKU per page load, and only once the map says the null is final.
  var warnedMissingSku = Object.create(null);

  function noteMissingPrice(api, sku) {
    if (!sku || warnedMissingSku[sku]) { return; }
    if (!skuSettled(api, sku)) { return; }
    warnedMissingSku[sku] = true;
    warn('no live price for SKU ' + sku + ' -- the price map has settled and does not ' +
      'hold it. That variant has no SKU, no price, or a price of zero in the Shopify ' +
      'admin. It renders as "' + ((api && api.FALLBACK_LABEL) || 'Call 19759 to order') +
      '" until it is fixed; it is never shown as a blank, a zero or a stale figure.');
  }

  // Returns a positive whole-EGP Number, or null.
  //
  // null means "this site does not know what this costs right now", and
  // EVERY caller below renders it as the hotline state -- never a blank,
  // never 0 EGP, and never the figure the surface happened to be holding
  // from the previous selection. Payment is cash on delivery: a stale price
  // on this page means a courier turns up asking the customer for a
  // different number than the page promised.
  function livePrice(variant, gradeKey) {
    var api = priceApi();
    if (!api) {
      // The map is mounted in the layout, so this is a theme-level failure
      // (the snippet was removed, or this route renders no layout), not a
      // per-device one. Say it once, not once per grade per render.
      if (!warnedNoPriceMap) {
        warnedNoPriceMap = true;
        warn('window.UpgradePrices is not defined -- snippets/upgrade-price-map.liquid ' +
          'did not render on this route. No sale price can be shown; every price on ' +
          'this page falls back to the hotline.');
      }
      return null;
    }
    if (!variant || !variant.code || !isValidGrade(gradeKey)) { return null; }
    var p = api.getByCode(variant.code, gradeKey);
    // The contract already guarantees a positive number or null. Re-checked
    // here because this is the last gate before a number reaches the DOM.
    if (typeof p === 'number' && isFinite(p) && p > 0) { return p; }
    noteMissingPrice(api, skuOf(api, variant.code, gradeKey));
    return null;
  }

  // The "From" price for a whole device: the cheapest grade (Gold) across
  // that device's storages. UpgradePrices.lowest() is deliberately strict --
  // null if ANY of the listed SKUs has no price -- because a "From" figure
  // computed over whatever survived is a number nobody can check.
  function lowestPrice(variants) {
    var api = priceApi();
    if (!api || !variants || !variants.length) { return null; }
    var skus = variants.map(function (v) { return api.skuFor(v && v.code, 'g'); });
    var p = api.lowest(skus);
    if (typeof p === 'number' && isFinite(p) && p > 0) { return p; }
    // lowest() is strict on purpose: ONE unpriced Gold SKU withholds the
    // whole "From" figure. That is right, but silent -- so name the SKUs
    // that did it. noteMissingPrice() holds its tongue while the walk is
    // still running, so a related card waiting for a repaint is not
    // reported as an outage.
    if (typeof api.missing === 'function') {
      api.missing(skus).forEach(function (sku) { noteMissingPrice(api, sku); });
    }
    return null;
  }

  // One wording for a price this site cannot supply, shared by all four
  // consuming surfaces via the contract so they cannot drift apart.
  // Falls back to the section's own hotline only if the map is absent.
  function noPriceLabel(config) {
    var api = priceApi();
    if (api && api.FALLBACK_LABEL) { return api.FALLBACK_LABEL; }
    return T('pdp.call_to_order', { hotline: (digitsOnly(config && config.hotlineNumber) || '19759') });
  }

  function UgPdp(root) {
    this.root = root;
    // Edit everything (7 Oct 2026): this section's own words, before anything is drawn.
    readWords(root);
    // data-handle is only present on sections/upgrade-product.liquid (the
    // real /products/<handle> template). Its presence is what tells this
    // engine it is running in "product" routing mode -- see buildDeviceUrl.
    // sections/upgrade-pdp.liquid (the generic /pages/device?device=SERIES
    // page) never sets it, so that surface is unaffected.
    var explicitHandle = (root.getAttribute('data-handle') || '').toLowerCase();
    this.config = {
      dataUrl: root.getAttribute('data-data-url'),
      defaultSeries: (root.getAttribute('data-default-series') || 'IPHONE15').toUpperCase(),
      explicitHandle: explicitHandle,
      routingMode: explicitHandle ? 'product' : 'page',
      whatsappNumber: root.getAttribute('data-whatsapp-number') || '',
      hotlineNumber: root.getAttribute('data-hotline-number') || '19759',
      tradeinUrl: root.getAttribute('data-tradein-url') || (routeRoot() + 'pages/trade-in'),
      collectionUrl: root.getAttribute('data-collection-url') || (routeRoot() + 'pages/shop'),
      selfUrl: (root.getAttribute('data-self-url') || window.location.pathname).split('?')[0],
      relatedCount: parseInt(root.getAttribute('data-related-count'), 10) || 4,
      // Batch E1 (6 Oct 2026): "Use the product title from admin", a section
      // setting that is OFF by default. The attribute is only printed when it
      // is on, so with it off this is false and the page is as before. See
      // renderStatic() and tryLoadShopifyProduct().
      titleFromAdmin: root.getAttribute('data-title-source') === 'admin',
      // POST-LAUNCH 8 OCT (TC-15): the buy button's words while
      // /products/<handle>.js is still loading, printed by
      // sections/upgrade-product.liquid from the locale key
      // upgrade.post8.pdp.addcart_loading. Absent (the /pages/device route),
      // the page behaves exactly as before. See updateAddToCart().
      loadingLabel: root.getAttribute('data-loading-label') || '',
      // 2.0 (13 Oct 2026): the success state's word, printed by
      // sections/upgrade-product.liquid (locale key upgrade.pdp.addcart_added).
      // Absent (the /pages/device route) the label keeps its words and only
      // the check mark shows. See handleAddToCart().
      addedLabel: root.getAttribute('data-added-label') || ''
    };
    this.state = {
      device: null,
      allDevices: [],
      selectedGrade: null,
      selectedStorage: null,
      // The colour option VALUE the shopper is on (display name, exactly as
      // the Shopify product spells it). Stays null for the life of the page
      // on a product with no Colour option. See resolveColour. (Colour
      // variants, ported 27 Sep 2026.)
      selectedColour: null,
      shopifyProduct: null,
      // True only between the first successful render and a render that
      // replaced the page with the not-found or error panel. It is the one
      // gate on the price repaint (repaintPrices, below):
      //  - before the device JSON resolves there is nothing to repaint, and
      //    the first paint will read the settled map anyway;
      //  - after renderNotFound/renderError, root.innerHTML has been
      //    replaced and updateAll() would be writing into DOM that no
      //    longer exists.
      painted: false
    };
  }

  UgPdp.prototype.init = function () {
    var self = this;

    // Registered FIRST, synchronously, before the device fetch starts.
    // The walk can settle at any point, including while that fetch is still
    // in flight; registering here means the only event this page can miss is
    // one that fired before the listener existed, and a paint that happens
    // after the walk settled reads a settled map anyway. Nothing below waits
    // on it -- see listenForPrices.
    this.listenForPrices();

    var params = new URLSearchParams(window.location.search);
    var seriesParam = (params.get('device') || '').toUpperCase();
    // Resolution order: 1) explicit ?device= query param (kept for the
    // generic page.device template and for anyone sharing a ?device= link),
    // 2) the product's own handle, reverse-mapped to a series (the
    // /products/<handle> template's normal path -- no query param needed),
    // 3) the section's configured fallback series.
    var handleSeries = this.config.explicitHandle ? HANDLE_TO_SERIES[this.config.explicitHandle] : null;

    // A PRODUCT URL MAY NEVER FALL BACK TO A DIFFERENT PRODUCT.
    //
    // This page runs in two modes. On /pages/device the series is chosen by
    // query param and defaultSeries is a sensible landing device -- falling
    // back there is correct. On /products/<handle> the URL names one specific
    // phone the customer asked for, and substituting another one is never an
    // improvement on saying "we could not find it".
    //
    // Until 23 September 2026 both modes shared one fallback chain, so an
    // unmapped handle resolved to config.defaultSeries (IPHONE15 in
    // templates/product.json). That had never fired, because every handle the
    // store carried was in the map. The catalogue expansion to 27 devices
    // would have fired it on 17 URLs at once: /products/iphone-17-pro-max
    // would have rendered a complete, confident iPhone 15 -- its title, image,
    // specs and prices -- and, because tryLoadShopifyProduct() is handed the
    // RESOLVED device's handle further down, wired Add to cart to an iPhone 15
    // variant id. On a cash-on-delivery store the courier arrives with the
    // wrong phone. Nothing was logged on that path.
    //
    // So: when the URL named a product, resolution is exact or it is nothing.
    var namedAProduct = !!this.config.explicitHandle && !seriesParam;
    if (namedAProduct && !handleSeries) {
      warn('handle "' + this.config.explicitHandle + '" is not in SERIES_TO_HANDLE — ' +
        'refusing to substitute ' + this.config.defaultSeries + '. Add it to the map in ' +
        'assets/upgrade-pdp.js and to assets/upgrade-tradein-data.json.');
      this.renderNotFound();
      return;
    }
    var series = seriesParam || handleSeries || this.config.defaultSeries;

    fetch(this.config.dataUrl)
      .then(function (r) {
        if (!r.ok) { throw new Error('data fetch failed: ' + r.status); }
        return r.json();
      })
      .then(function (json) {
        self.state.allDevices = (json && json.devices) || [];
        // Same rule on the data side: a handle that maps to a series the data
        // file does not carry is still a product URL with no product behind
        // it. Only /pages/device is allowed the defaultSeries / first-device
        // chain.
        var device = namedAProduct
          ? self.findDevice(series)
          : (self.findDevice(series) || self.findDevice(self.config.defaultSeries) || self.state.allDevices[0]);
        if (!device) {
          if (namedAProduct) {
            warn('series "' + series + '" (from handle "' + self.config.explicitHandle +
              '") has no record in ' + self.config.dataUrl + ' — rendering not-found rather ' +
              'than substituting another device.');
          }
          self.renderNotFound();
          return;
        }
        self.state.device = device;
        // BLK-2, read side. A customer arriving from the trade-in page was
        // dropped on variants[0] with the grade cleared, so the page they
        // landed on computed a different balance from the card they clicked
        // (worst observed gap 7,400 EGP). Honour what the handoff asked for.
        self.state.selectedStorage = self.resolveIncomingStorage(device, params.get('storage'));
        self.state.selectedGrade = self.resolveIncomingGrade(params.get('grade'));
        // ?colour= (or ?color=) is only a PREFERENCE, held until the Shopify
        // product says which colours exist -- see tryLoadShopifyProduct. On a
        // product with no Colour option it is never read again. (Colour
        // variants, ported 27 Sep 2026.)
        self._requestedColour = params.get('colour') || params.get('color') || null;
        // LF10 fix (29 Sep 2026, fix_now, audit w4k72rk5w). ?variant=<id>,
        // same deferral: held until the Shopify variant map exists (see
        // applyRequestedVariant, called from tryLoadShopifyProduct), because
        // the id is a Shopify variant id, unknowable before that fetch
        // resolves. /products/<handle> only, per this fix -- it is the cart
        // line's own link back ({{ item.url }}, sections/upgrade-cart.liquid)
        // that carries this param, and that link always points at a product
        // page, never /pages/device.
        self._requestedVariantId = (self.config.routingMode === 'product') ? params.get('variant') : null;

        // POST-LAUNCH 8 OCT (TC-15): known before the first paint, so a
        // ?grade= link paints its buy button as loading, never as the hotline,
        // while /products/<handle>.js is on its way (tryLoadShopifyProduct).
        var handle = SERIES_TO_HANDLE[device.series];
        if (handle) { self._spLoading = true; }

        self.renderStatic();
        self.updateAll();
        self.state.painted = true;
        self.wireEvents();
        self.observeSticky();
        self.observeGallery();

        // Lynkrs B7 (6 Oct 2026, T3): the selection the URL asked for is now
        // on screen -- unless it is a ?variant=, which applyRequestedVariant()
        // can only resolve once the Shopify product has loaded. See
        // releasePending().
        if (!(self._requestedVariantId && handle)) { self.releasePending(); }
        if (handle) {
          self.tryLoadShopifyProduct(handle);
        } else {
          // Round 3 (device route): no Shopify product for this series, so no
          // request will ever answer; the held Colour space goes back now.
          self.releaseColourReserve();
        }
      })
      .catch(function () {
        self.renderError();
      });
  };

  // ---- repaint when the price walk settles ---------------------------
  //
  // PAINT, THEN REPAINT. NEVER BLOCK. This page builds and paints on
  // exactly the tick it always has: init() runs, the device JSON resolves,
  // renderStatic() and updateAll() run, and whatever the price map can
  // answer at that moment is what the customer sees -- a real price for
  // every device in the Liquid seed, the hotline for anything the walk has
  // not reached. Then this listener corrects it.
  //
  // NOTHING HERE IS GATED BEHIND whenReady(), deliberately. whenReady()
  // holds its caller until the walk settles, and against a hanging endpoint
  // that is PAGE_TIMEOUT_MS 8000 x MAX_ATTEMPTS 2 -- about sixteen seconds
  // of a product page with no price, no grade ladder and no buy button, on
  // Egyptian mobile data. BUDGET_MS is assigned per walk() call rather than
  // across attempts, so on a single-page walk (which is exactly what ~100
  // products produces) it is never consulted and cannot cut that short. A
  // page that briefly shows the hotline is a smaller failure than a page
  // that shows nothing at all, and paint-then-repaint makes the sixteen
  // seconds structurally impossible rather than merely unlikely.
  //
  // The event fires at most twice: once when the document is parsed
  // (complete may be false there, mid-walk) and once when the walk settles.
  // Both repaints are unconditional and idempotent -- this is the same
  // pattern assets/upgrade-catalogue.js:273 already runs in production,
  // where it took seven hotline cards back to real prices with no other
  // change. The listener is not removed after the first event, because the
  // second one is the one that carries the final answer.
  UgPdp.prototype.listenForPrices = function () {
    var self = this;
    if (!document || typeof document.addEventListener !== 'function') { return; }
    document.addEventListener('ug:prices:ready', function () {
      self.repaintPrices();
    });
  };

  // The two price-bearing renders on this page, and only these two.
  //
  //  - updateAll() repaints every price-bearing element for the grade and
  //    storage currently selected: the three grade pills, the headline
  //    price, the vs-new comparison, the grade ladder's whole price row,
  //    the finance slot's data-price, the ug:pdp:variant-changed event, the
  //    UpgradeDeal target and the mobile sticky bar. Re-running it is how a
  //    headline that painted as "Call 19759 to order" becomes a number, and
  //    how a deal target that was CLEARED for want of a price gets set.
  //  - renderRelated() repaints the "From" figure on each related-device
  //    card, which comes from UpgradePrices.lowest() and nothing else.
  //
  // renderSpecs() and the rest of renderStatic() carry no price, so they are
  // deliberately not re-run: rebuilding the grade and storage buttons would
  // throw away the customer's selection mid-visit.
  //
  // No visible churn when nothing changed: updateAll() only re-triggers the
  // price-change animation when data-shown actually differs, and a repaint
  // that produces the same figures rewrites the same strings.
  UgPdp.prototype.repaintPrices = function () {
    if (!this.state.painted || !this.state.device) { return; }
    this.updateAll();
    this.renderRelated();
  };

  // ---- incoming handoff parameters (BLK-2, read side) ----------------
  //
  // Contract read here, written by the trade-in surface:
  //   ?storage=<exact storage label>   e.g. "256GB"
  //   ?grade=<g|p|d>                   (Gold/Platinum/Diamond also accepted)
  // Both are optional and both are validated against this device's own data
  // before use. Anything unrecognised falls back to the previous default and
  // warns -- it never selects a variant the customer did not ask for in
  // silence, and never puts an unchecked value where GRADE_INFO is indexed.

  UgPdp.prototype.resolveIncomingStorage = function (device, requested) {
    var variants = (device && device.variants) || [];
    var fallback = variants[0] ? variants[0].storage : null;
    if (!requested) { return fallback; }
    var wanted = String(requested).trim().toLowerCase();
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].storage).trim().toLowerCase() === wanted) {
        return variants[i].storage;
      }
    }
    warn('?storage=' + requested + ' matches no variant of ' + (device && device.series) +
      '. Showing ' + fallback + ' instead.');
    return fallback;
  };

  UgPdp.prototype.resolveIncomingGrade = function (requested) {
    if (!requested) { return null; }
    var key = gradeKeyFrom(requested);
    if (key) { return key; }
    warn('?grade=' + requested + ' is not one of g/p/d. Ignoring it; no grade is preselected.');
    return null;
  };

  UgPdp.prototype.findDevice = function (series) {
    if (!series) { return null; }
    for (var i = 0; i < this.state.allDevices.length; i++) {
      if (this.state.allDevices[i].series === series) { return this.state.allDevices[i]; }
    }
    return null;
  };

  UgPdp.prototype.currentVariant = function () {
    var d = this.state.device;
    var storage = this.state.selectedStorage;
    var match = d.variants.filter(function (v) { return v.storage === storage; })[0];
    return match || d.variants[0];
  };

  // ---- Shopify-native progressive enhancement -----------------------

  UgPdp.prototype.tryLoadShopifyProduct = function (handle) {
    var self = this;
    // POST-LAUNCH 8 OCT (TC-15): true until this fetch settles, either way.
    // updateAddToCart() reads it: loading is not "no online route".
    this._spLoading = true;
    // REVIEW 8 OCT (REVIEW-pdp.md R3): a fetch that never settles (a stalled
    // connection on mobile data) used to leave the button on the loading
    // words for good, where it once said "Call 19759 to order" at once. After
    // LOADING_WAIT_MS the loading words give way to the hotline state, as for
    // a failed fetch; if the product does arrive later, it paints the real
    // state as usual. window.setTimeout, so a page without timers (the test
    // harnesses) simply keeps waiting for the fetch.
    try {
      if (typeof window !== 'undefined' && window && typeof window.setTimeout === 'function') {
        window.setTimeout(function () {
          if (!self._spLoading || self.state.shopifyProduct) { return; }
          self._spLoading = false;
          if (self.state.painted) {
            try { self.updateAll(); } catch (e) { /* the page as painted stands */ }
          }
        }, LOADING_WAIT_MS);
      }
    } catch (e) { /* no timer: the fetch's own end repaints */ }
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) { throw new Error('HTTP ' + r.status); }
        return r.json();
      })
      .then(function (product) {
        if (!product || !product.variants || !product.variants.length) {
          warn('product "' + handle + '" loaded but has no variants. Online ordering stays off for this device; the buy button points at the hotline.');
          return;
        }
        var options = product.options || [];
        // Shopify returns product.options either as an array of strings
        // (older shape) or as objects {name, position, values} (current
        // shape). Handle both -- treating an object as a string throws,
        // and the throw is swallowed by our catch, which silently left the
        // page in JSON-only mode with the buy button hidden forever.
        var names = options.map(function (opt) {
          var raw = (opt && typeof opt === 'object') ? opt.name : opt;
          return String(raw == null ? '' : raw).trim();
        });
        var gradeIdx = matchOptionIndex(names, OPTION_SYNONYMS.grade);
        var storageIdx = matchOptionIndex(names, OPTION_SYNONYMS.storage);

        // Schema mismatch on the live product -- stay in JSON-only mode
        // rather than guess at option positions, but say so out loud. A
        // renamed option is a merchandising mistake that used to take the
        // whole store offline commercially with no trace anywhere.
        if (gradeIdx === -1 || storageIdx === -1) {
          var missing = [];
          if (gradeIdx === -1) { missing.push('Grade (accepted: ' + OPTION_SYNONYMS.grade.join(', ') + ')'); }
          if (storageIdx === -1) { missing.push('Storage (accepted: ' + OPTION_SYNONYMS.storage.join(', ') + ')'); }
          warn('product "' + handle + '" has no option matching ' + missing.join(' and ') +
            '. Option names found: [' + names.join(', ') + ']. Online ordering stays off for this device; the buy button points at the hotline.');
          return;
        }

        // OPTIONAL third axis (Colour variants, ported 27 Sep 2026). -1 on
        // every product imported before the colour write, and then everything
        // below runs exactly as it did before this axis existed: two-part
        // keys, no colour list, no colour row, no colour anywhere in shared
        // state.
        var colourIdx = matchOptionIndex(names, OPTION_SYNONYMS.colour);

        // Colour names in the PRODUCT'S option order -- the order the
        // merchandiser set in the admin, which is the order the chips render
        // in. The current product.js shape lists them on the option; the older
        // string shape does not, so anything only seen on a variant is added in
        // variant order after them.
        var colours = [];
        var colourSeen = {};
        function addColour(value) {
          var name = String(value == null ? '' : value).trim();
          var ck = colourKey(name);
          if (!name || !ck || colourSeen[ck]) { return; }
          colourSeen[ck] = true;
          colours.push(name);
        }
        if (colourIdx !== -1) {
          var colourOpt = options[colourIdx];
          if (colourOpt && typeof colourOpt === 'object' && colourOpt.values && colourOpt.values.forEach) {
            colourOpt.values.forEach(addColour);
          }
        }

        var map = {};
        var storageLabels = [];   // as the product spells them, for warnings
        var unmatched = 0;
        var noColour = 0;
        product.variants.forEach(function (v) {
          var gradeLabel = v['option' + (gradeIdx + 1)];
          var storageLabel = v['option' + (storageIdx + 1)];
          var gradeKey = self.gradeLabelToKey(gradeLabel);
          if (!gradeKey || !storageLabel) { unmatched++; return; }
          var rec = {
            id: v.id,
            price: v.price / 100, // Shopify's product.js returns the smallest currency unit
            available: !!v.available
          };
          var key = gradeKey + '|' + storageKey(storageLabel);
          if (colourIdx !== -1) {
            var colourLabel = String(v['option' + (colourIdx + 1)] == null ? '' : v['option' + (colourIdx + 1)]).trim();
            if (!colourKey(colourLabel)) { noColour++; return; }
            addColour(colourLabel);
            rec.colour = colourLabel;
            // The variant's own SKU (<code>-<RX>-<COLOUR CODE>), for the deal
            // target: sections/upgrade-book.liquid names the colour to staff
            // from its colour code. Colour products only -- a Grade/Storage
            // record keeps its exact three fields.
            if (typeof v.sku === 'string' && v.sku.trim()) { rec.sku = v.sku.trim(); }
            key += '|' + colourKey(colourLabel);
          }
          if (storageLabels.indexOf(String(storageLabel)) === -1) {
            storageLabels.push(String(storageLabel));
          }
          map[key] = rec;
        });
        if (unmatched) {
          warn('product "' + handle + '": ' + unmatched + ' of ' + product.variants.length +
            ' variants carry a grade value this theme does not recognise (expected Gold, Platinum or Diamond) and cannot be ordered online.');
        }
        if (noColour) {
          warn('product "' + handle + '": ' + noColour + ' of ' + product.variants.length +
            ' variants have an empty "' + names[colourIdx] + '" value and cannot be ordered online.');
        }

        // Batch E1 (6 Oct 2026): "Use the product title from admin" on the
        // /pages/device route, the one route with no `product` in Liquid. The
        // admin Title, as typed, replaces the label in the H1 and the crumb.
        // Text only. Off (the default), or on /products/<handle> (where the
        // section prints it), nothing happens here.
        if (self.config.titleFromAdmin && self.config.routingMode === 'page' && typeof product.title === 'string' && product.title.trim()) {
          var adminTitleEl = qs(self.root, '[data-ug-pdp-title]');
          if (adminTitleEl) { adminTitleEl.textContent = product.title.trim(); }
          var adminCrumbEl = qs(self.root, '[data-ug-pdp-title-crumb]');
          if (adminCrumbEl) { adminCrumbEl.textContent = product.title.trim(); }
          self.syncTabTitle(product.title.trim());
        }
        self.state.shopifyProduct = {
          id: product.id, handle: handle, variantMap: map, storageLabels: storageLabels,
          // [] on a product with no Colour option -- the one test everything
          // colour-related below makes before it does anything at all.
          colours: colours
        };
        // POST-LAUNCH 8 OCT (TC-15): loaded; the paint below is the real state.
        self._spLoading = false;
        // LF10 fix: a real, sellable ?variant= resolves grade/storage/
        // colour together and takes priority over ?colour= (more specific,
        // and the two could otherwise disagree); ?colour= only runs when
        // there was no ?variant= to apply, or it did not resolve.
        if (!self.applyRequestedVariant()) { self.applyRequestedColour(); }
        self.renderColours();
        // OBSIDIAN, 29 Sep 2026 (C1): real Shopify photos, taken from THIS
        // response, on /pages/device (routingMode "page") -- the one route
        // that has no `product` object at render time and so can only ever
        // start on the fallback cut-out. On /products/<handle> the section
        // has already rendered the real media directly in Liquid (see
        // sections/upgrade-product.liquid) and marked the gallery root
        // data-ug-pdp-gallery-source="shopify"; this never overwrites that
        // render a second time from a differently-sized set of URLs.
        var galleryRoot = qs(self.root, '[data-ug-pdp-gallery]');
        var alreadyShopify = galleryRoot && galleryRoot.getAttribute('data-ug-pdp-gallery-source') === 'shopify';
        if (!alreadyShopify && product.images && product.images.length) {
          var galleryLabel = (self.state.device && self.state.device.label) || '';
          // 7 Oct 2026 (the photo gallery, pdp-gallery-7oct/GALLERY.md): the
          // same gallery as /products/<handle> -- colour sets, thumbnails, the
          // large view -- built from this response by assets/upgrade-gallery.js.
          // The old single-strip gallery stays as the fallback if that script
          // cannot be had.
          if (!self.mountPhotoGallery(product, galleryLabel)) {
            self.applyShopifyGallery(product.images, galleryLabel);
          }
        }
        self.updateAll();
      })
      .catch(function (err) {
        // No matching product on this store yet is a legitimate state --
        // JSON-only mode still renders the full page and a price. But it is
        // not a silent one: without this line nobody ever finds out that the
        // store cannot take an order.
        warn('could not load Shopify product "' + handle + '" (' +
          ((err && err.message) || 'fetch failed') +
          '). Online ordering stays off for this device; the buy button points at the hotline.');
      })
      .then(function () {
        // POST-LAUNCH 8 OCT (TC-15): failed, empty or unmatched, the loading
        // words give way to the hotline state they used to show at once.
        // A product that loaded has already painted (above).
        var wasLoading = self._spLoading;
        self._spLoading = false;
        // REVIEW 8 OCT (REVIEW-pdp.md R3): also when the button still shows
        // the loading words for any other reason (a step after the product
        // was stored threw before its own repaint), so they never outlive
        // the fetch.
        if (((wasLoading && !self.state.shopifyProduct) || self._loadingShown) && self.state.painted) {
          try { self.updateAll(); } catch (e) { /* the page as painted stands */ }
        }
        self.releaseColourReserve();
        // Lynkrs B7 (6 Oct 2026, T3): loaded, failed or empty, a ?variant=
        // has now been applied or given up on. Idempotent.
        self.releasePending();
      });
  };

  // Round 3 (device route only). The section holds the Colour row's space from
  // the first paint (data-ug-pdp-colour-reserve) and used to give it back on a
  // fixed 8 s timer, whether or not /products/<handle>.js had answered: a
  // product that answered after 8 s then pushed the price box and the buy
  // button down by the row's height, in front of a visitor who might be
  // choosing a grade, after the timer had already pulled them up. The space
  // is now given back when that request SETTLES without a colour row to put
  // there (failed, empty, no Colour option). A request that answers builds the
  // row in the placeholder's place (renderColours), and one that never
  // settles is caught by the section's own, longer, timer. Same removal as
  // renderColours; the product page (no data-ug-pdp-route) keeps its timer.
  UgPdp.prototype.releaseColourReserve = function () {
    try {
      if (!this.root || !this.root.hasAttribute('data-ug-pdp-route')) { return; }
      var reserve = qs(this.root, '[data-ug-pdp-colour-reserve]');
      if (reserve && reserve.parentNode) { reserve.parentNode.removeChild(reserve); }
    } catch (e) { /* the failsafe timer still gives the space back */ }
  };

  // ---- Lynkrs B7 (6 Oct 2026, T3): the server render and the URL --------
  //
  // sections/upgrade-product.liquid renders the grade cards, the storage
  // choice and the grade table in Liquid, as this engine's first paint
  // draws them for the plain product URL: no grade chosen, the first
  // storage. Liquid cannot read a query string, so a URL that names a
  // grade, a storage or a variant (every internal link does: shop cards,
  // home rail, trade-in and sell cards, the cart's link back) would show
  // the default figures for a moment and then this engine's. On a
  // cash-on-delivery store a price that changes under the customer's eyes
  // is the one thing the page must not do. So an inline script in that
  // section marks the root data-ug-pdp-pending on such URLs
  // (assets/upgrade-pdp.css hides the selection-dependent blocks and keeps
  // their space), and this lifts it once the URL's selection is painted.
  // The inline script lifts it after 4 s on its own, so a page whose script
  // failed still shows its server-rendered default.
  UgPdp.prototype.releasePending = function () {
    try {
      if (this.root && typeof this.root.removeAttribute === 'function') {
        this.root.removeAttribute('data-ug-pdp-pending');
      }
    } catch (e) { /* nothing to lift */ }
  };

  // ---- POST-LAUNCH 8 OCT (PDP-10): the address bar follows the choice ------
  //
  // final-check-8oct/rendered/RENDERED-CHECK.md item 9. The page read
  // ?variant= on arrival (applyRequestedVariant) and never wrote it: a reload
  // lost the choice, and a customer who landed on ?variant=<Gold> and picked
  // Diamond still had the Gold id in the address bar, so a shared or reloaded
  // link showed another grade and price. Now every paint after the product
  // has loaded rewrites the query with history.replaceState -- never
  // pushState, so Back still leaves the page -- to say what is on screen:
  //   - the grade, storage and colour name one variant that can be ordered:
  //     ?variant=<id>, and nothing else of the choice (?grade=, ?storage=,
  //     ?colour=, ?color= are dropped: the id says all three, so the two can
  //     never disagree on the next load);
  //   - they do not (no grade yet, a sold-out colour the customer is holding,
  //     a grade sold out in this storage, no online route): no ?variant=, and
  //     the choice as ?grade= / ?storage= / ?colour=, the incoming-link
  //     parameters this file already reads, so a reload paints the same
  //     choice and price. applyRequestedVariant() refuses a sold-out id on
  //     purpose (LF10), which is why a sold-out choice is not written as one.
  //     With no grade picked only what the customer chose is written: a
  //     storage other than the first, a colour they picked or a link named.
  // Every other parameter (utm_*, fbclid, ...) is kept as it was typed, in
  // its place, and the #fragment with it. /products/<handle> only: the
  // /pages/device route never writes its address. Before the product loads
  // nothing is written -- an incoming ?variant= is still waiting to be read --
  // and a product that never loads leaves the address as it arrived.
  var URL_CHOICE_PARAMS = ['variant', 'grade', 'storage', 'colour', 'color'];

  // `search` without the choice parameters, then `pairs` ([[name, value]])
  // appended in order. '' when nothing is left.
  function choiceSearch(search, pairs) {
    var kept = String(search || '').replace(/^\?/, '').split('&').filter(function (part) {
      if (!part) { return false; }
      var name = part.split('=')[0];
      try { name = decodeURIComponent(name.replace(/\+/g, ' ')); } catch (e) { /* compared as typed */ }
      return URL_CHOICE_PARAMS.indexOf(name) === -1;
    });
    (pairs || []).forEach(function (p) {
      kept.push(encodeURIComponent(p[0]) + '=' + encodeURIComponent(p[1]));
    });
    return kept.length ? '?' + kept.join('&') : '';
  }

  // The choice on screen as URL parameters (see above).
  UgPdp.prototype.urlChoice = function (grade, storage, colour, heldColour) {
    var sp = this.state.shopifyProduct;
    var g = isValidGrade(grade) ? grade : null;
    var hasColours = this.hasColours();
    var on = colour || heldColour || null;
    if (g && sp && sp.variantMap && (!hasColours || on)) {
      var rec = sp.variantMap[g + '|' + storageKey(storage) + (hasColours ? '|' + colourKey(on) : '')];
      if (rec && rec.id && rec.available === true) { return [['variant', String(rec.id)]]; }
    }
    var out = [];
    if (g) { out.push(['grade', g]); }
    var d = this.state.device;
    var first = (d && d.variants && d.variants[0]) ? d.variants[0].storage : null;
    if (storage && (g || storage !== first)) { out.push(['storage', String(storage)]); }
    if (hasColours && this._colourChosen) {
      var c = on || this.state.selectedColour;
      if (c) { out.push(['colour', String(c)]); }
    }
    return out;
  };

  UgPdp.prototype.syncUrl = function (grade, storage, colour, heldColour) {
    if (this.config.routingMode !== 'product' || !this.state.shopifyProduct) { return; }
    try {
      var win = (typeof window !== 'undefined') ? window : null;
      var loc = win && win.location;
      var hist = win && win.history;
      if (!loc || !hist || typeof hist.replaceState !== 'function') { return; }
      var pathName = String(loc.pathname || '');
      if (pathName.indexOf('/products/') === -1) { return; }
      var was = String(loc.search || '');
      var next = choiceSearch(was, this.urlChoice(grade, storage, colour, heldColour));
      if (next === was || (next === '' && was === '?')) { return; }
      hist.replaceState(hist.state, '', pathName + next + String(loc.hash || ''));
    } catch (e) { /* a browser that refuses (rate limit, sandbox): the page itself is unchanged */ }
  };

  // Lynkrs batch 2, SEO-Titles / D9 (W4, 6 Oct 2026). The server writes every
  // title as "<page or product title> | <brand>" (layout/theme.liquid). On
  // /products/<handle> this engine never writes the tab title. On /pages/device
  // -- the generic page, titled for the page, not the phone -- it replaces the
  // part before the LAST " | " with the phone's name, so the pattern and the
  // brand stay the server's. A title without " | " is left as the server wrote
  // it: never a second pattern.
  UgPdp.prototype.syncTabTitle = function (name) {
    if (this.config.routingMode !== 'page' || !name) { return; }
    try {
      var t = String(document.title || '');
      var cut = t.lastIndexOf(' | ');
      // Arabic port, 7 Oct 2026 (pD): no bidi mark in a tab title (03-FIXES.md D9).
      if (cut > 0) { document.title = plainText(String(name)) + t.slice(cut); }
    } catch (e) { /* non-fatal: the server's title stands */ }
  };

  UgPdp.prototype.gradeLabelToKey = function (label) {
    return gradeKeyFrom(label);
  };

  // ---- colour (OPTIONAL third axis; Colour variants, ported 27 Sep 2026) --
  //
  // Everything in this block begins by asking whether the loaded product has
  // any colours, and does nothing at all when it does not. That is the whole
  // of the graceful-degradation guarantee: a Grade/Storage product never
  // reaches a line below that changes the page.

  UgPdp.prototype.hasColours = function () {
    var sp = this.state.shopifyProduct;
    return !!(sp && sp.colours && sp.colours.length);
  };

  // ?colour= / ?color= from the URL, matched against the product's own
  // colour names by the SKU colour-code rule. Unknown values warn and are
  // dropped, the same way an unknown ?storage= is -- the page never picks a
  // colour the customer did not ask for in silence.
  // LF10 fix (29 Sep 2026, fix_now, audit w4k72rk5w). ?variant=<Shopify
  // variant id>, matched against THIS product's own variant map (built in
  // tryLoadShopifyProduct). A real match preselects grade, storage and
  // colour together, before updateAll()'s first Shopify-priced paint --
  // closing the "price clicked is not the price seen" gap the cart's own
  // link back ({{ item.url }}?variant=..., sections/upgrade-cart.liquid)
  // depends on. An id that matches nothing, or matches a sold-out variant,
  // changes nothing (today's ?grade=/?storage=/?colour=/default
  // resolution stands) -- returns false either way so the caller knows
  // whether to fall back to ?colour=.
  UgPdp.prototype.applyRequestedVariant = function () {
    var wanted = this._requestedVariantId;
    this._requestedVariantId = null;
    if (!wanted) { return false; }
    var sp = this.state.shopifyProduct;
    if (!sp || !sp.variantMap) { return false; }
    var hitKey = null, hit = null;
    Object.keys(sp.variantMap).forEach(function (k) {
      var rec = sp.variantMap[k];
      if (!hit && rec && String(rec.id) === String(wanted)) { hitKey = k; hit = rec; }
    });
    if (!hit || !hit.available) { return false; }

    var gradeKey = hitKey.split('|')[0];
    var storagePart = hitKey.split('|')[1];
    var device = this.state.device;
    var variants = (device && device.variants) || [];
    var matchedStorage = null;
    for (var i = 0; i < variants.length; i++) {
      if (storageKey(variants[i].storage) === storagePart) { matchedStorage = variants[i].storage; break; }
    }
    if (!matchedStorage) { return false; }

    this.state.selectedGrade = gradeKey;
    this.state.selectedStorage = matchedStorage;
    if (hit.colour) {
      this.state.selectedColour = hit.colour;
      // Named by the id, so it is the customer's choice (see resolveColour),
      // exactly as applyRequestedColour() treats a matched ?colour=.
      this._colourChosen = true;
    }
    return true;
  };

  UgPdp.prototype.applyRequestedColour = function () {
    var wanted = this._requestedColour;
    this._requestedColour = null;
    if (!wanted || !this.hasColours()) { return; }
    var wk = colourKey(wanted);
    var hit = this.state.shopifyProduct.colours.filter(function (n) { return colourKey(n) === wk; })[0];
    if (hit) {
      this.state.selectedColour = hit;
      // Asked for by name, so it is the customer's choice (see resolveColour).
      this._colourChosen = true;
    } else {
      warn('?colour=' + wanted + ' matches no colour of "' + this.state.shopifyProduct.handle +
        '" [' + this.state.shopifyProduct.colours.join(', ') + ']. The first available colour is shown instead.');
    }
  };

  // One entry per colour of the product, in option order, for the grade and
  // storage on screen:
  //   { name, key, available }
  // `available` is the Shopify variant's own flag for grade+storage+colour;
  // a colour with no variant at all for this grade and storage is simply not
  // available. Before a grade is picked there is no grade to ask about, so a
  // colour counts as available if ANY grade of this storage has it in stock --
  // the chip is only greyed out when no choice of grade could sell it.
  UgPdp.prototype.colourChoices = function (grade, storage) {
    if (!this.hasColours()) { return []; }
    var sp = this.state.shopifyProduct;
    var sk = storageKey(storage);
    var grades = isValidGrade(grade) ? [grade] : GRADE_ORDER;
    return sp.colours.map(function (name) {
      var ck = colourKey(name);
      var available = grades.some(function (g) {
        var r = sp.variantMap[g + '|' + sk + '|' + ck];
        return !!(r && r.available);
      });
      return { name: name, key: ck, available: available };
    });
  };

  // Runs early in every updateAll(), so it covers a grade click, a
  // storage click, the product fetch landing and a price repaint alike.
  // Keeps the selected colour while it can still be sold in this grade and
  // storage. When it cannot, what happens depends on WHO picked it:
  //   - the page's own default (the first available colour on arrival, never
  //     touched by the customer) moves to the first colour that can be sold;
  //   - a colour the CUSTOMER chose (a chip click, an arrow key, a matching
  //     ?colour=) is never swapped for another one behind their back. It stays
  //     checked, reads "Sold out", and Add to cart says "Sold out in this
  //     colour" until they pick one that can be sold -- so nothing can reach
  //     the cart in a colour the customer did not choose (review, 27 Sep 2026).
  // If NO colour can be sold here the choice is left where it was, so
  // stepping back to a grade that has it restores it, and the page reads out
  // of stock in this grade.
  //
  // Returns the choices plus `colour`: the selected colour ONLY if it is
  // orderable right now, else null. That, not state.selectedColour, is what
  // "The grade you chose" panel, the deal state and the cart use. `held` is
  // the customer's own choice while it is sold out here and another colour is
  // not; it is shown checked, and never ordered.
  UgPdp.prototype.resolveColour = function (grade, storage) {
    var choices = this.colourChoices(grade, storage);
    if (!choices.length) { return { choices: choices, colour: null, held: null }; }
    var current = this.state.selectedColour;
    var ck = current ? colourKey(current) : '';
    var mine = ck ? choices.filter(function (c) { return c.key === ck; })[0] : null;
    if (mine && mine.available) { return { choices: choices, colour: mine.name, held: null }; }
    var first = choices.filter(function (c) { return c.available; })[0];
    if (!first) { return { choices: choices, colour: null, held: null }; }
    if (mine && this._colourChosen) { return { choices: choices, colour: null, held: mine.name }; }
    this.state.selectedColour = first.name;
    return { choices: choices, colour: first.name, held: null };
  };

  // The variant record the buy button may use for this grade and storage,
  // or null. A Grade/Storage product is looked up exactly as it always was.
  //
  // A colour product is looked up on the SELECTED colour and nothing else:
  // this function never hands back an orderable variant in a colour the
  // customer is not looking at. When no colour is orderable here but the
  // grade+storage does exist in some colour, it answers with a record that
  // carries no id and available:false, so the button reads "out of stock in
  // this grade" (true) rather than "call to order" (which means there is no
  // online route at all).
  UgPdp.prototype.variantFor = function (grade, storage, colour) {
    var sp = this.state.shopifyProduct;
    if (!sp) { return null; }
    var base = grade + '|' + storageKey(storage);
    if (!this.hasColours()) { return sp.variantMap[base] || null; }
    if (colour) {
      var rec = sp.variantMap[base + '|' + colourKey(colour)];
      if (rec) { return rec; }
    }
    for (var i = 0; i < sp.colours.length; i++) {
      if (sp.variantMap[base + '|' + colourKey(sp.colours[i])]) {
        return { id: null, price: null, available: false };
      }
    }
    return null;
  };

  UgPdp.prototype.selectColour = function (name) {
    if (!this.hasColours()) { return; }
    var ck = colourKey(name);
    var hit = this.colourChoices(this.state.selectedGrade, this.state.selectedStorage)
      .filter(function (c) { return c.key === ck && c.available; })[0];
    // A sold-out chip is disabled, so this is belt and braces: a colour that
    // cannot be sold in this grade and storage is never made the selection.
    if (!hit) { return; }
    this.state.selectedColour = hit.name;
    this._colourChosen = true;
    this._userActed = true;
    this.updateAll();
  };

  // Builds the colour radiogroup once per product load and the Colour row of
  // "The grade you chose" panel with it. On a product with no colours it
  // creates nothing.
  //
  // Mounted from here rather than from the Liquid on purpose (same approach
  // as [data-ug-pdp-vsnew]): the Liquid cannot know on /pages/device which
  // product the page is about, and a markup placeholder would change what a
  // two-option product renders. So the markup is created only once a real
  // Shopify product has said it has colours, directly after the storage row,
  // and its state is written by updateAll() like every other control.
  //
  // Swatches are real radio semantics on a real <button>, the same pattern
  // as the grade and storage controls: role="radio" + aria-checked inside a
  // role="radiogroup" named "Colour", arrow keys via handleRadioKeydown.
  //
  // THE OPTION BLOCK, 3 Oct 2026. The colour is a round swatch with no
  // name printed beside it: the name is said ONCE, in the row's label
  // ("Colour · Onyx Black", setOptionValue), instead of on every chip --
  // the chips used to wrap two to a row at different widths and read as
  // ragged. Colour is still never the only signal: every swatch carries its
  // name as its accessible name (aria-label) and as a tooltip (title), and a
  // sold-out swatch says so in both ("Blue, sold out" -- updateColourChips),
  // is `disabled`, and is struck through with a diagonal line by CSS.
  // The row is a third .ug-option, built here with the same label + group
  // shape as the Grade and Storage rows the Liquid renders, and placed
  // directly after the Storage row.
  UgPdp.prototype.renderColours = function () {
    // 2.0 (CLS): the section holds the Colour row's space with a hidden
    // placeholder from the first paint. It goes in the same tick as the real
    // row arrives (below), or here when the product turns out to have none.
    var reserve = qs(this.root, '[data-ug-pdp-colour-reserve]');
    if (!this.hasColours()) {
      if (reserve && reserve.parentNode && this.state.shopifyProduct) { reserve.parentNode.removeChild(reserve); }
      return;
    }
    var self = this;
    var root = this.root;
    var wrap = qs(root, '[data-ug-pdp-colours]');
    if (!wrap) {
      var storageWrap = qs(root, '[data-ug-pdp-storages]');
      if (!storageWrap || !storageWrap.parentNode) { return; }
      wrap = document.createElement('div');
      wrap.className = 'ug-colour-row';
      wrap.setAttribute('role', 'radiogroup');
      wrap.setAttribute('aria-label', T('pdp.colour_group_label'));
      wrap.setAttribute('data-ug-pdp-colours', '');

      // The Storage row is the .ug-option around the storage radiogroup.
      // On markup without the option block (none ships today) the group
      // goes directly after the storage radiogroup, as it always did.
      var storageRow = storageWrap.closest ? storageWrap.closest('.ug-option') : null;
      if (storageRow && storageRow.parentNode) {
        var row = document.createElement('div');
        row.className = 'ug-option ug-option--colour';
        var label = document.createElement('p');
        label.className = 'ug-option__label';
        label.setAttribute('aria-hidden', 'true');
        var labelName = document.createElement('span');
        labelName.className = 'ug-option__name';
        labelName.textContent = T('pdp.colour_group_label');
        var labelValue = document.createElement('span');
        labelValue.className = 'ug-option__value';
        labelValue.setAttribute('data-ug-pdp-option-value', 'colour');
        label.appendChild(labelName);
        label.appendChild(labelValue);
        row.appendChild(label);
        row.appendChild(wrap);
        storageRow.parentNode.insertBefore(row, storageRow.nextSibling);
        if (reserve && reserve.parentNode) { reserve.parentNode.removeChild(reserve); }
      } else {
        storageWrap.parentNode.insertBefore(wrap, storageWrap.nextSibling);
      }
      wrap.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-colour]') : null;
        if (!btn) {
          // 7 Oct 2026 (the photo gallery): on a page with photos a sold-out
          // swatch lets the click through (assets/upgrade-material.css 10.3,
          // pointer-events), so the customer can see that colour's photos.
          var soldOut = self.soldOutChipAt(wrap, e);
          if (soldOut) { self.previewColour(soldOut.getAttribute('data-colour')); }
          return;
        }
        if (btn.disabled) { return; }
        self.selectColour(btn.getAttribute('data-colour'));
      });
      wrap.addEventListener('keydown', function (e) { self.handleRadioKeydown(e, wrap, 'data-colour'); });
    }
    wrap.innerHTML = '';
    this.state.shopifyProduct.colours.forEach(function (name) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ug-colour-chip';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.setAttribute('data-colour', name);
      btn.setAttribute('aria-label', name);
      btn.setAttribute('title', name);

      var dot = document.createElement('span');
      dot.setAttribute('aria-hidden', 'true');
      var paint = swatchColour(name);
      if (paint) {
        dot.className = 'ug-colour-chip__dot';
        dot.style.setProperty('--ug-swatch', paint);
      } else {
        dot.className = 'ug-colour-chip__dot ug-colour-chip__dot--unknown';
      }

      btn.appendChild(dot);
      wrap.appendChild(btn);
    });

    // "The grade you chose" panel (launch punch list B10): a Colour row
    // directly after Grade, on a colour product only. Same dt/dd shape as the
    // rows the Liquid renders, so it takes their styling as it is.
    if (!qs(root, '[data-ug-pdp-unit-colour]')) {
      var gradeDd = qs(root, '[data-ug-pdp-unit-grade]');
      var gradeRow = gradeDd && gradeDd.parentNode;
      if (gradeRow && gradeRow.parentNode) {
        var row = document.createElement('div');
        row.setAttribute('data-ug-pdp-unit-colour-row', '');
        var dt = document.createElement('dt');
        dt.textContent = T('pdp.colour_group_label');
        var dd = document.createElement('dd');
        dd.setAttribute('data-ug-pdp-unit-colour', '');
        dd.textContent = '—';
        row.appendChild(dt);
        row.appendChild(dd);
        gradeRow.parentNode.insertBefore(row, gradeRow.nextSibling);
      }
    }
  };

  // Writes each swatch's state for the grade and storage on screen. Nothing
  // is rebuilt, so keyboard focus stays on the swatch it is on. The checked
  // swatch is the orderable colour, or the customer's own choice while it is
  // sold out here (resolved.held: checked AND disabled AND named "<colour>,
  // sold out", so the page shows what they picked and why it cannot be
  // ordered). The row's label says the same thing in words.
  UgPdp.prototype.updateColourChips = function (resolved) {
    var byKey = {};
    resolved.choices.forEach(function (c) { byKey[c.key] = c; });
    var selName = resolved.colour || resolved.held || '';
    var selKey = selName ? colourKey(selName) : '';
    qsa(this.root, '[data-ug-pdp-colours] [data-colour]').forEach(function (btn) {
      var name = btn.getAttribute('data-colour');
      var key = colourKey(name);
      var c = byKey[key];
      var available = !!(c && c.available);
      var selected = !!selKey && key === selKey;
      btn.disabled = !available;
      if (available) { btn.removeAttribute('aria-disabled'); } else { btn.setAttribute('aria-disabled', 'true'); }
      btn.classList.toggle('is-selected', selected);
      btn.classList.toggle('is-sold-out', !available);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      // The sold-out state in words, for a screen reader (the accessible
      // name) and on hover (the tooltip). The diagonal line is CSS.
      var spoken = available ? name : T('pdp.colour_sold_out_value', { colour: name });
      btn.setAttribute('aria-label', spoken);
      btn.setAttribute('title', plainText(spoken));
    });
    var anyAvailable = resolved.choices.some(function (c) { return c.available; });
    if (resolved.colour) {
      this.setOptionValue('colour', isolateText(resolved.colour), false);
    } else if (resolved.held) {
      this.setOptionValue('colour', T('pdp.colour_sold_out_value', { colour: resolved.held }), false);
    } else if (!anyAvailable) {
      this.setOptionValue('colour', T('pdp.option_sold_out_in_grade'), true);
    } else {
      this.setOptionValue('colour', T('pdp.option_choose_one_colour'), true);
    }
    var unitColour = qs(this.root, '[data-ug-pdp-unit-colour]');
    if (unitColour) { unitColour.textContent = resolved.colour ? isolateText(resolved.colour) : '—'; }
  };

  // The current choice in a row's label: "Grade · Platinum". `muted` is for
  // a prompt rather than a choice ("choose one"). Text only, never markup;
  // the separator is drawn by CSS. A row the page does not render (the
  // Colour row on a Grade/Storage product) has no value node, so this does
  // nothing there.
  UgPdp.prototype.setOptionValue = function (option, text, muted) {
    var el = qs(this.root, '[data-ug-pdp-option-value="' + option + '"]');
    if (!el) { return; }
    var next = text == null ? '' : String(text);
    if (el.textContent !== next) { el.textContent = next; }
    el.classList.toggle('is-muted', !!muted);
  };

  // A storage is sold out only when the Shopify product has loaded, has at
  // least one variant in that storage, and NONE of them -- no grade, no
  // colour -- can be ordered. A storage with no variant at all is not sold
  // out: that is the "Call 19759 to order" route (no online order route, not
  // no phone), and it stays exactly as it was. Before the product loads
  // nothing is sold out.
  UgPdp.prototype.storageSoldOut = function (storage) {
    var sp = this.state.shopifyProduct;
    if (!sp || !sp.variantMap) { return false; }
    var want = storageKey(storage);
    var seen = false;
    var open = false;
    Object.keys(sp.variantMap).forEach(function (k) {
      if (k.split('|')[1] !== want) { return; }
      seen = true;
      if (sp.variantMap[k] && sp.variantMap[k].available) { open = true; }
    });
    return seen && !open;
  };

  // ---- One-time render per device load -------------------------------

  UgPdp.prototype.renderStatic = function () {
    var d = this.state.device;
    var root = this.root;

    qsa(root, '[data-ug-pdp-brand], [data-ug-pdp-brand-eyebrow]').forEach(function (el) {
      el.textContent = d.brand;
    });
    // Batch E1 (6 Oct 2026). With "Use the product title from admin" on, on
    // /products/<handle> the section has already printed the admin Title in
    // the H1 and the crumb, and the browser tab carries Shopify's own title
    // (the SEO title, or the product title): this engine writes neither.
    // On /pages/device the device list's label goes in first, as always, and
    // tryLoadShopifyProduct() swaps in the admin Title once it has it. Off
    // (the default): exactly as before.
    var adminTitleHere = this.config.titleFromAdmin && this.config.routingMode === 'product';
    if (!adminTitleHere) {
      var titleEl = qs(root, '[data-ug-pdp-title]');
      if (titleEl) { titleEl.textContent = d.label; }
      var crumbEl = qs(root, '[data-ug-pdp-title-crumb]');
      if (crumbEl) { crumbEl.textContent = d.label; }
    }
    // Lynkrs batch 2, SEO-Titles / D9 (W4, 6 Oct 2026). This engine used to
    // write its own tab title here, "<label> — Refurbished, graded,
    // warrantied — UPGRADE", over the server's. The server's title now wins
    // on /products/<handle>: Shopify's SEO title when the content lead sets
    // one, else the product title, in layout/theme.liquid's one pattern. Only
    // the generic /pages/device route, whose server title names no phone,
    // gets the phone's name put in front of that same pattern (syncTabTitle).
    this.syncTabTitle(d.label);

    // FIXED, 29 Sep 2026 (review fix, should-fix). PHOTOS requirement: this
    // is the fallback gallery slide's image -- the one every /pages/device
    // route renders (that template has no Liquid `product` object, so it
    // can never render product.media server-side the way
    // sections/upgrade-product.liquid does) and the one
    // sections/upgrade-product.liquid itself falls back to when a product
    // has no media yet. It only ever read the static cut-out before this
    // fix, so a real Shopify photo could never appear on this route even
    // after the catalogue team uploads one. window.UpgradeFacts sources
    // from the SAME "all-devices" collection either template is on, so it
    // works here identically to the homepage rail and the related cards.
    var img = qs(root, '[data-ug-pdp-image]');
    if (img) {
      var mainImgInfo = deviceImageInfo(d);
      // D4 (W4): the cut-out's WebP twin first, then the PNG as the fallback.
      setWebpSource(img, mainImgInfo.isPhoto ? '' : webpTwin(mainImgInfo.src));
      img.src = mainImgInfo.src;
      img.alt = mainImgInfo.isPhoto ? d.label : T('pdp.image_alt', { device: d.label });
      // F7 fix, 29 Sep 2026 (04-LIVE-LAYOUT-AUDIT.md; caldwell-theme): a
      // real photo's true intrinsic size, when UpgradeFacts has it, so
      // .ug-pdp__gallery-slide--photo img's object-fit:scale-down (assets/
      // upgrade-pdp.css) can tell a low-resolution source from the frame
      // it sits in and hold it at its own size instead of stretching it --
      // same fix as sections/upgrade-product.liquid's product.media loop,
      // applied here because this branch fills the SAME slide from JS
      // instead of Liquid. The markup's hard-coded 800x800 stays as the
      // fallback for the cut-out branch (isPhoto false): that PNG is sized
      // by the 68%-inset rule, not by intrinsic pixels, and is not the F7
      // defect.
      if (mainImgInfo.isPhoto && mainImgInfo.width && mainImgInfo.height) {
        img.width = mainImgInfo.width;
        img.height = mainImgInfo.height;
      }
      var mainSlide = img.closest ? img.closest('[data-ug-pdp-gallery-slide]') : null;
      if (mainSlide) { mainSlide.classList.toggle('ug-pdp__gallery-slide--photo', mainImgInfo.isPhoto); }
      var mainCap = qs(root, '.ug-pdp__gallery-cap');
      if (mainCap) { mainCap.hidden = mainImgInfo.isPhoto; }
      // "Catalogue image" is also promised in the paragraph below the
      // gallery ([data-ug-pdp-media-note]) -- hide that too when a real
      // photo was found, same reasoning as mainCap above.
      var mainNote = qs(root, '[data-ug-pdp-media-note]');
      if (mainNote) { mainNote.hidden = mainImgInfo.isPhoto; }
    }

    // THE OPTION BLOCK, 3 Oct 2026 (Omar: "reorganize the variants to make
    // them look better and more organized and structured"). A grade card
    // carries the grade's name, its one-line cosmetic condition and its
    // minimum battery health -- the same GRADE_INFO strings the ladder and "The grade
    // you chose" panel already print, never new copy -- and, last, the price
    // for the storage on screen. The price sits inside its own foot so the
    // "Offer" tag (offerPillFor, inserted after the price) stays on the
    // price's line. On a narrow buy box the condition and battery lines are
    // dropped by CSS alone (assets/upgrade-pdp.css section 20) and the card
    // is name + price; nothing here depends on the width.
    var gradesWrap = qs(root, '[data-ug-pdp-grades]');
    if (gradesWrap) {
      gradesWrap.innerHTML = '';
      GRADE_ORDER.forEach(function (key) {
        var info = GRADE_INFO[key];
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ug-grade-pill';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.setAttribute('data-grade', key);
        btn.innerHTML =
          '<span class="ug-grade-pill__name">' + escapeHtml(gradeName(key)) + '</span>' +
          '<span class="ug-grade-pill__cond">' + escapeHtml(gradeCosmetic(key)) + '</span>' +
          '<span class="ug-grade-pill__battery">' + escapeHtml(T('pdp.grade_pill_battery', { range: gradeBattery(key) })) + '</span>' +
          '<span class="ug-grade-pill__foot">' +
            '<span class="ug-grade-pill__price ug-price ug-price--sm" data-ug-pdp-grade-price></span>' +
          '</span>';
        gradesWrap.appendChild(btn);
      });
    }

    // Storage segments: the size, and a "Sold out" line that updateAll()
    // shows only when no grade (and no colour) of that storage can be ordered
    // online -- see storageSoldOut().
    var storageWrap = qs(root, '[data-ug-pdp-storages]');
    if (storageWrap) {
      storageWrap.innerHTML = '';
      d.variants.forEach(function (v, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ug-storage-chip';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', i === 0 ? 'true' : 'false');
        btn.setAttribute('data-storage', v.storage);
        var size = document.createElement('span');
        size.className = 'ug-storage-chip__size';
        size.textContent = v.storage;
        var note = document.createElement('span');
        note.className = 'ug-storage-chip__note';
        note.setAttribute('data-ug-pdp-storage-note', '');
        note.textContent = T('pdp.storage_sold_out');
        note.hidden = true;
        btn.appendChild(size);
        btn.appendChild(note);
        storageWrap.appendChild(btn);
      });
    }

    this.renderSpecs();
    this.renderRelated();
    this.wireGallery();
  };

  // ---- Gallery (OBSIDIAN, 29 Sep 2026, C1) -----------------------------
  //
  // Two renderers can fill [data-ug-pdp-gallery-track]: sections/upgrade-
  // product.liquid writes real Shopify photos directly at render time when
  // `product.media` is not empty (marking the gallery root data-ug-pdp-
  // gallery-source="shopify"); this file writes the ONE fallback cut-out on
  // both templates, and -- on /pages/device only, via tryLoadShopifyProduct
  // above -- can replace it with real photos fetched in the background.
  // wireGallery() is the one place either path's dots/thumbs get their
  // click behaviour, so it runs after both: at the end of renderStatic()
  // (covers the fallback and the Liquid-rendered real-media case) and again
  // at the end of applyShopifyGallery() (covers the late-arriving case).
  // FIXED, 29 Sep 2026 (review fix, should-fix): wireGallery() used to be
  // idempotent on `track.__ugWired`, set the FIRST time it ran -- so the
  // renderStatic() call (against the fallback slide, before any dots/thumbs
  // exist) silently prevented the SECOND call here from wiring the buttons
  // buildGalleryNav() had just built. It is now idempotent per delegated
  // listener on the dots/thumbs CONTAINERS (which persist across a
  // buildGalleryNav() rebuild), so calling it twice remains harmless and the
  // late-arriving real photos' nav buttons actually work. See wireGallery()
  // itself for the fix.

  UgPdp.prototype.applyShopifyGallery = function (images, altText) {
    var root = this.root;
    var track = qs(root, '[data-ug-pdp-gallery-track]');
    var galleryRoot = qs(root, '[data-ug-pdp-gallery]');
    if (!track || !galleryRoot || !images || !images.length) { return; }
    track.innerHTML = '';
    images.forEach(function (src, i) {
      var slide = document.createElement('div');
      // ug-pdp__gallery-slide--photo: same card-contract modifier as the
      // catalogue and related-rail cards (review fix, should-fix) -- a real
      // photo does not get the cut-out's 68%-inset --lift filter treatment.
      // ug-glow: the soft glow moved from the gallery root onto each slide
      // (review fix, minor) -- see sections/upgrade-product.liquid's
      // matching comment for why the root-level placement never showed.
      slide.className = 'ug-pdp__gallery-slide ug-pdp__gallery-slide--photo ug-glow';
      slide.style.setProperty('--ug-glow-inset', '6% 8% 16% 8%');
      slide.setAttribute('data-ug-pdp-gallery-slide', '');
      var img = document.createElement('img');
      img.src = typeof src === 'string' ? src : String(src);
      img.alt = altText || '';
      img.width = 800;
      img.height = 800;
      if (i === 0) {
        img.loading = 'eager';
        img.setAttribute('fetchpriority', 'high');
      } else {
        img.loading = 'lazy';
      }
      img.decoding = 'async';
      slide.appendChild(img);
      track.appendChild(slide);
    });
    // Real Shopify photos, not the "no photo yet" placeholder -- no caption.
    galleryRoot.setAttribute('data-ug-pdp-gallery-source', 'shopify');
    this.buildGalleryNav(images.length);
    this.wireGallery();
  };

  // ---- The photo gallery (7 Oct 2026; pdp-gallery-7oct/GALLERY.md) ---------
  //
  // assets/upgrade-gallery.js runs the gallery when the phone has photos. On
  // /products/<handle> the section prints it and loads that script; on
  // /pages/device (no `product` in Liquid) this builds it from the product's
  // own /products/<handle>.js. Either way this engine only ever tells it which
  // colour is on screen: no price, variant or cart value goes the other way.

  // /pages/device: true when the gallery took the photos (or is loading its
  // script to take them); false sends the caller to applyShopifyGallery().
  UgPdp.prototype.mountPhotoGallery = function (product, label) {
    var self = this;
    var root = this.root;
    var galleryRoot = qs(root, '[data-ug-pdp-gallery]');
    var usable = (product && product.media || []).filter(function (m) { return m && m.media_type !== 'model'; });
    if (!galleryRoot || !usable.length) { return false; }
    var G = (typeof window !== 'undefined') ? window.UpgradeGallery : null;
    if (G && typeof G.fromProduct === 'function') {
      var g = null;
      try {
        g = G.fromProduct(galleryRoot, product, { model: label, title: product.title, colour: this.galleryColour(this.peekColour()), words: { photo: plainText(T('pdp.photo_n_of_total', { n: '[n]', total: '[total]' })) } });
      } catch (e) { g = null; }
      if (!g) { return false; }
      var note = qs(root, '[data-ug-pdp-media-note]');
      if (note) { note.hidden = true; }
      return true;
    }
    if (this._galleryLoading || typeof document === 'undefined' || !document.createElement) { return false; }
    var mine = document.querySelector ? document.querySelector('script[src*="upgrade-pdp.js"]') : null;
    var src = mine ? String(mine.getAttribute('src') || '').replace('upgrade-pdp.js', 'upgrade-gallery.js') : '';
    if (!src || src.indexOf('upgrade-gallery.js') === -1) { return false; }
    this._galleryLoading = true;
    var tag = document.createElement('script');
    tag.src = src;
    tag.async = true;
    tag.onload = function () {
      if (!self.mountPhotoGallery(product, label)) { self.applyShopifyGallery(product.images, label); }
      self.syncGalleryColour(self.galleryColour(self.peekColour()));
    };
    tag.onerror = function () { self.applyShopifyGallery(product.images, label); };
    (document.head || document.body).appendChild(tag);
    return true;
  };

  // The colour on screen, handed to the gallery. Nothing without photos.
  UgPdp.prototype.syncGalleryColour = function (name) {
    var galleryRoot = qs(this.root, '[data-ug-pdp-gallery]');
    if (!galleryRoot || galleryRoot.getAttribute('data-ug-gal') === null) { return; }
    var G = (typeof window !== 'undefined') ? window.UpgradeGallery : null;
    if (G && typeof G.setColour === 'function') {
      G.setColour(galleryRoot, name || '');
    } else {
      galleryRoot.setAttribute('data-ug-gal-want', name || '');
    }
  };

  // The colour whose photos the gallery shows (check D1, 7 Oct 2026): the
  // orderable colour, else the customer's own sold-out choice, else the
  // colour already selected. When the grade and storage on screen sell no
  // colour at all (a smallest storage sold out in every colour), the first
  // colour, in option order, in stock in any grade and storage: the rule
  // sections/upgrade-product.liquid prints the page with. Else the colour
  // the page was printed with. Never '' while the phone has colours, so the
  // gallery never falls back to another colour's photo. Reads state only.
  UgPdp.prototype.galleryColour = function (resolved) {
    var name = resolved ? (resolved.colour || resolved.held) : null;
    if (name) { return name; }
    if (this.state.selectedColour) { return this.state.selectedColour; }
    var sp = this.state.shopifyProduct;
    if (sp && sp.colours && sp.variantMap) {
      var keys = Object.keys(sp.variantMap);
      for (var i = 0; i < sp.colours.length; i++) {
        var tail = '|' + colourKey(sp.colours[i]);
        for (var k = 0; k < keys.length; k++) {
          var rec = sp.variantMap[keys[k]];
          if (rec && rec.available && keys[k].slice(-tail.length) === tail) { return sp.colours[i]; }
        }
      }
    }
    var galleryRoot = qs(this.root, '[data-ug-pdp-gallery]');
    return (galleryRoot && galleryRoot.getAttribute('data-ug-gal-colour')) || '';
  };

  // What resolveColour() will answer for the grade and storage on screen,
  // without changing the selection (it may move a default colour; that is
  // put back). For the gallery only, before updateAll() runs.
  UgPdp.prototype.peekColour = function () {
    if (!this.hasColours()) { return null; }
    var keep = this.state.selectedColour;
    var grade = isValidGrade(this.state.selectedGrade) ? this.state.selectedGrade : null;
    var r = this.resolveColour(grade, this.state.selectedStorage);
    this.state.selectedColour = keep;
    return r;
  };

  // The sold-out swatch under a click that reached the colour row itself
  // (a disabled button takes no click; on a page with photos the stylesheet
  // lets it through). Only on a page whose gallery has photos.
  UgPdp.prototype.soldOutChipAt = function (wrap, e) {
    if (!qs(this.root, '[data-ug-gal]') || !e || typeof e.clientX !== 'number') { return null; }
    var hit = null;
    qsa(wrap, '[data-colour]').forEach(function (chip) {
      if (hit || !chip.disabled || typeof chip.getBoundingClientRect !== 'function') { return; }
      var r = chip.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) { hit = chip; }
    });
    return hit;
  };

  // A sold-out colour the customer picked to look at. It becomes their own
  // choice, shown checked and "sold out" (the same held state a grade change
  // can leave): Add to cart reads "Sold out in this colour", nothing in that
  // colour can reach the cart, and the gallery shows its photos. Picking an
  // available colour, or a grade or storage that has it, works as before.
  UgPdp.prototype.previewColour = function (name) {
    if (!this.hasColours() || !name) { return; }
    var ck = colourKey(name);
    var hit = this.state.shopifyProduct.colours.filter(function (n) { return colourKey(n) === ck; })[0];
    if (!hit) { return; }
    this.state.selectedColour = hit;
    this._colourChosen = true;
    this._userActed = true;
    this.updateAll();
  };

  // ---- POST-LAUNCH 8 OCT (PDP-18): the colour's photo beside its label -----
  //
  // final-check-8oct/rendered/RENDERED-CHECK.md item 11. On a phone the
  // swatches sit about 500px below the gallery, so a tap on a colour changed
  // a photo the customer could not see (the label changed, the picture did
  // not, as far as they could tell). The page is NOT scrolled for them.
  // Instead the photo the gallery now shows for that colour -- the same one,
  // sold-out colours included (Omar: a sold-out colour still shows its
  // photos) -- is drawn small inside the row's label, after "Colour · <name>":
  // <img class="ug-option__peek" data-ug-pdp-colour-peek alt="<that photo's
  // own alt>">. It is part of the label's text flow, so it sits after the
  // name on either side of a right-to-left page with no rule of its own, and
  // it is inside the label's aria-hidden on purpose: the label repeats, for
  // the eye, what the radiogroup already says (the swatch's name, "sold
  // out"), and the gallery photo it repeats carries the same alt. It shows
  // only while the gallery is mostly out of view on a phone
  // (data-ug-pdp-gallery-away on the root, observeGallery(); CSS: the
  // POST-LAUNCH 8 OCT block at the end of assets/upgrade-pdp.css), and only
  // for a colour that has its own photo -- a colour shown with the general
  // photos has no photo of its own to show. A product without photos, or
  // without colours, never gets one.

  // The colour's own first photo, as the gallery holds it: { src, alt }, or
  // null. Admin order (data-ug-gal-i), images only -- the rule
  // UpgradeGallery.pickFor() uses for a card, here without its fallbacks.
  UgPdp.prototype.colourPhoto = function (name) {
    var want = String(name == null ? '' : name).trim();
    var gallery = qs(this.root, '[data-ug-pdp-gallery]');
    if (!want || !gallery || gallery.getAttribute('data-ug-gal') === null) { return null; }
    var best = null;
    var bestI = Infinity;
    qsa(gallery, '[data-ug-gal-slide]').forEach(function (slide) {
      var type = slide.getAttribute('data-ug-gal-t') || 'image';
      if (type !== 'image' || String(slide.getAttribute('data-ug-gal-c') || '').trim() !== want) { return; }
      var i = parseInt(slide.getAttribute('data-ug-gal-i'), 10);
      if (isNaN(i)) { i = Infinity; }
      if (!best || i < bestI) { best = slide; bestI = i; }
    });
    var img = best ? qs(best, 'img') : null;
    if (!img) { return null; }
    // The gallery's own thumbnail of that photo (160px wide) when it printed
    // one; else the slide's photo, asked for at that width from the CDN.
    var thumb = isFinite(bestI) ? qs(gallery, '[data-ug-gal-thumb][data-ug-gal-i="' + bestI + '"] img') : null;
    var src = (thumb && thumb.getAttribute('src')) || String(img.getAttribute('src') || '').replace(/([?&])width=\d+/, '$1width=160');
    if (!src) { return null; }
    return { src: src, alt: plainText(img.getAttribute('alt') || want) };
  };

  UgPdp.prototype.updateColourPeek = function (name) {
    var value = qs(this.root, '[data-ug-pdp-option-value="colour"]');
    var label = value && value.parentNode;
    if (!label || typeof label.appendChild !== 'function') { return; }
    var peek = qs(label, '[data-ug-pdp-colour-peek]');
    var photo = this.colourPhoto(name);
    // REVIEW 8 OCT (2026-10-08_post-launch-fixes/theme/REVIEW-pdp.md R1, R2).
    // R2: the label is marked ug-option__label--peek while it holds a
    // thumbnail that can show; the stylesheet keeps the thumbnail's room at
    // the end of the label for as long as the mark is on, so the thumbnail
    // coming and going as the gallery scrolls in and out of view never
    // re-wraps the label and never moves the swatches. The mark changes only
    // here, on a paint (a tap, a load), never on a scroll.
    // R1: a photo that failed to load stays hidden on every later paint; it
    // used to come back as a broken box on the next tap.
    var shown = !!photo && !(peek && peek._ugPeekFailed && peek._ugPeekFailed === photo.src);
    if (label.classList && typeof label.classList.toggle === 'function') { label.classList.toggle('ug-option__label--peek', shown); }
    if (!photo) {
      if (peek) { peek.hidden = true; }
      return;
    }
    if (!peek) {
      peek = document.createElement('img');
      peek.className = 'ug-option__peek';
      peek.setAttribute('data-ug-pdp-colour-peek', '');
      peek.setAttribute('width', '64');
      peek.setAttribute('height', '64');
      peek.setAttribute('decoding', 'async');
      // A photo that fails to load is not left as a broken box in the label,
      // now or on any later paint (R1).
      peek.onerror = function () {
        peek._ugPeekFailed = peek.getAttribute('src') || '';
        peek.hidden = true;
        var owner = peek.parentNode;
        if (owner && owner.classList && typeof owner.classList.remove === 'function') { owner.classList.remove('ug-option__label--peek'); }
      };
      label.appendChild(peek);
    }
    if (peek.getAttribute('src') !== photo.src) { peek.setAttribute('src', photo.src); }
    peek.setAttribute('alt', photo.alt);
    peek.hidden = !shown;
  };

  // Marks the root data-ug-pdp-gallery-away while less than half of the
  // gallery is on screen. Only the stylesheet acts on it (phones only).
  UgPdp.prototype.observeGallery = function () {
    var self = this;
    var gallery = qs(this.root, '[data-ug-pdp-gallery]');
    var IO = (typeof window !== 'undefined') ? window.IntersectionObserver : null;
    if (!gallery || typeof IO !== 'function') { return; }
    try {
      new IO(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target !== gallery) { return; }
          var away = !entry.isIntersecting || entry.intersectionRatio < 0.5;
          if (away) { self.root.setAttribute('data-ug-pdp-gallery-away', ''); } else { self.root.removeAttribute('data-ug-pdp-gallery-away'); }
        });
      }, { threshold: [0, 0.5, 1] }).observe(gallery);
    } catch (e) { /* no observer: the label carries no photo, as before */ }
  };

  // Dots (below 990px) and thumbnails (from 990px) both need 2+ images --
  // with exactly one photo neither mount ever shows (README, "the states
  // and how each page maps to the theme": dots/thumbnails are a navigation
  // aid, not decoration on a single image).
  UgPdp.prototype.buildGalleryNav = function (count) {
    var root = this.root;
    var track = qs(root, '[data-ug-pdp-gallery-track]');
    var dots = qs(root, '[data-ug-pdp-gallery-dots]');
    var thumbs = qs(root, '[data-ug-pdp-gallery-thumbs]');
    var srcs = track ? qsa(track, 'img').map(function (im) { return im.getAttribute('src') || ''; }) : [];
    if (dots) {
      dots.innerHTML = '';
      if (count >= 2) {
        for (var i = 0; i < count; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ug-pdp__gallery-dot';
          b.setAttribute('data-ug-pdp-gallery-dot', '');
          b.setAttribute('data-index', String(i));
          b.setAttribute('aria-label', T('pdp.photo_n_of_total', { n: i + 1, total: count }));
          if (i === 0) { b.setAttribute('aria-current', 'true'); }
          dots.appendChild(b);
        }
      }
      dots.hidden = count < 2;
    }
    if (thumbs) {
      thumbs.innerHTML = '';
      if (count >= 2) {
        srcs.forEach(function (src, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ug-pdp__gallery-thumb';
          b.setAttribute('data-ug-pdp-gallery-thumb', '');
          b.setAttribute('data-index', String(i));
          b.setAttribute('aria-label', T('pdp.photo_n_of_total', { n: i + 1, total: count }));
          if (i === 0) { b.setAttribute('aria-current', 'true'); }
          var t = document.createElement('img');
          t.src = src;
          t.alt = '';
          t.width = 160;
          t.height = 160;
          t.loading = 'lazy';
          b.appendChild(t);
          thumbs.appendChild(b);
        });
      }
      thumbs.hidden = count < 2;
    }
  };

  // Click-to-scroll for whichever dots/thumbs exist, however they were
  // built. No auto-advance, no scroll-linked animation (house rule: nothing
  // moves on its own after load) -- this only responds to a tap.
  //
  // FIXED, 29 Sep 2026 (review fix, should-fix). Was idempotent on
  // `track.__ugWired`, set the FIRST time this ran -- renderStatic() calls
  // it once against the fallback single slide (no dots/thumbs exist yet),
  // then applyShopifyGallery() calls buildGalleryNav() (which REBUILDS the
  // dots/thumbs from scratch once real photos arrive) and wireGallery()
  // again, which returned immediately on the track's now-stale flag and
  // left the new buttons with no click handler at all. Delegated listeners
  // on the two container elements fix this: each container is wired once
  // (idempotent per container, not per rebuild), so a later buildGalleryNav()
  // call can freely replace the buttons inside without losing the handler.
  UgPdp.prototype.wireGallery = function () {
    var root = this.root;
    var track = qs(root, '[data-ug-pdp-gallery-track]');
    if (!track) { return; }
    // 7 Oct 2026: a photo gallery ([data-ug-gal]) is run by assets/upgrade-gallery.js.
    var photoGallery = qs(root, '[data-ug-pdp-gallery]');
    if (photoGallery && photoGallery.getAttribute('data-ug-gal') !== null) { return; }
    function goTo(index) {
      var slides = qsa(track, '[data-ug-pdp-gallery-slide]');
      var slide = slides[index];
      if (slide && typeof track.scrollTo === 'function') {
        track.scrollTo({ left: slide.offsetLeft || 0, behavior: 'auto' });
      } else if (slide) {
        track.scrollLeft = slide.offsetLeft || 0;
      }
      qsa(root, '[data-ug-pdp-gallery-dot], [data-ug-pdp-gallery-thumb]').forEach(function (btn) {
        if (Number(btn.getAttribute('data-index')) === index) {
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.removeAttribute('aria-current');
        }
      });
    }
    var containers = [qs(root, '[data-ug-pdp-gallery-dots]'), qs(root, '[data-ug-pdp-gallery-thumbs]')];
    containers.forEach(function (container) {
      if (!container || container.__ugWired) { return; }
      container.__ugWired = true;
      container.addEventListener('click', function (evt) {
        var btn = evt.target && typeof evt.target.closest === 'function'
          ? evt.target.closest('[data-ug-pdp-gallery-dot], [data-ug-pdp-gallery-thumb]')
          : null;
        if (!btn || !container.contains(btn)) { return; }
        goTo(parseInt(btn.getAttribute('data-index'), 10) || 0);
      });
    });
  };

  UgPdp.prototype.renderSpecs = function () {
    var d = this.state.device;
    var specs = DEVICE_SPECS[d.series];
    var tbody = qs(this.root, '[data-ug-pdp-specs-body]');
    if (!tbody) { return; }
    // Lynkrs batch 2, SEO-Content (W4, 6 Oct 2026). When the phone's spec
    // metafields have values, sections/upgrade-product.liquid has already
    // printed the table (and the battery sentence) from them, for crawlers and
    // for everyone: they are the source, so this table is not drawn over them.
    // DEVICE_SPECS below is the fallback for a phone whose fields are empty.
    if (typeof tbody.getAttribute === 'function' && tbody.getAttribute('data-ug-pdp-specs-source') === 'metafields') { return; }
    var rows = [
      [T('pdp.spec_brand'), d.brand],
      [T('pdp.spec_model'), d.label],
      [T('pdp.spec_storage_options'), d.variants.map(function (v) { return v.storage; }).join(' · ')]
    ];
    if (specs) {
      if (specs.ram) { rows.push([T('pdp.spec_ram'), specs.ram]); }
      rows.push([T('pdp.spec_main_camera'), specText(specs.mainCamera)]);
      rows.push([T('pdp.spec_selfie_camera'), specs.selfieCamera]);
      rows.push([T('pdp.spec_battery_capacity'), specText(specs.battery)]);
      rows.push([T('pdp.spec_os'), specText(specs.os)]);
      rows.push([T('pdp.spec_connector'), specs.connector]);
      rows.push([T('pdp.spec_chipset'), specs.chipset]);
    }
    tbody.innerHTML = rows.map(function (r) {
      return '<tr><th>' + escapeHtml(r[0]) + '</th><td>' + escapeHtml(r[1]) + '</td></tr>';
    }).join('');
    // Lynkrs Q20 (6 Oct 2026, T2): the note's "Battery capacity above"
    // sentence shows only when the battery row above is drawn -- that is,
    // only for a model with an entry in DEVICE_SPECS. The other models show
    // Brand, Model and Storage options, and the note stops at its first
    // sentence.
    var batteryNote = qs(this.root, '[data-ug-pdp-specs-battery-note]');
    if (batteryNote) { batteryNote.hidden = !specs; }
  };

  UgPdp.prototype.renderRelated = function () {
    var self = this;
    var wrap = qs(this.root, '[data-ug-pdp-related]');
    if (!wrap) { return; }
    var current = this.state.device;
    // Lynkrs batch 2, PG-PDP-4 (W4, 6 Oct 2026). Was: the first phones of the
    // same brand in data-file order, so the iPhone 17 page offered the iPhone
    // 13, 14, 15 and 16. Now relatedFor(): the same generation or newer only,
    // the Pro sibling first, then the next generation; fewer cards when fewer
    // qualify, and none at all hides the rail with its heading.
    var picked = relatedFor(current, this.state.allDevices, this.config.relatedCount);
    var rail = wrap.closest ? wrap.closest('.ug-pdp__related') : null;
    if (rail) { rail.hidden = !picked.length; }
    wrap.innerHTML = '';
    // 2.0 (D7): the section printed skeleton cards and aria-busy; both go now.
    wrap.removeAttribute('aria-busy');
    // Thumbnails go through deviceImage(), like the main gallery: the theme's
    // own cut-out when there is one, not a hotlink to the old upgrade.me site
    // (launch punch list S12, 27 Sep 2026).
    picked.forEach(function (dv) {
      // Live "From" price for the related device: the cheapest Gold SKU
      // across its storages, read from Shopify. Math.min over the static
      // file used to do this; if any one of that device's Gold SKUs has no
      // live price the whole figure is withheld rather than quoted off a
      // partial set (UpgradePrices.lowest is strict, deliberately).
      var fromPrice = lowestPrice(dv.variants);
      var fromHtml = (fromPrice === null)
        ? escapeHtml(noPriceLabel(self.config))
        // 2.0 (QA N-3): a real space before the currency, as on every other card, so the link's name reads "78,899 EGP".
        : T('pdp.related_from', { price: '<span class="ug-price ug-price--md">' + formatMoney(fromPrice) + '<span class="ug-price__ccy ug-price__ccy--spaced"> ' + escapeHtml(T('common.currency')) + '</span></span>' });
      var imgInfo = deviceImageInfo(dv);
      // D4 (W4): a theme cut-out goes out as its WebP twin, the PNG as the fallback.
      // A Shopify photo gets widths, srcset and sizes (photoSrcset, C7).
      var photoSet = imgInfo.isPhoto ? photoSrcset(imgInfo.src) : '';
      var imgHtml = '<img src="' + escapeHtml(imgInfo.src) + '"' +
        (photoSet ? ' srcset="' + escapeHtml(photoSet) + '" sizes="' + PHOTO_CARD_SIZES + '"' : '') +
        // 2.0 (QA N-3): decorative - the card link already carries the title text.
        ' alt="" width="600" height="600" loading="lazy" decoding="async">';
      var twin = imgInfo.isPhoto ? '' : webpTwin(imgInfo.src);
      if (twin) { imgHtml = '<picture><source type="image/webp" srcset="' + escapeHtml(twin) + '">' + imgHtml + '</picture>'; }
      var a = document.createElement('a');
      a.className = 'ug-card';
      a.href = self.buildDeviceUrl(dv.series);
      a.innerHTML =
        '<div class="ug-card__media' + (imgInfo.isPhoto ? ' ug-card__media--photo' : '') + '">' + imgHtml + '</div>' +
        '<div class="ug-card__body">' +
          '<p class="ug-card__brand">' + escapeHtml(dv.brand) + '</p>' +
          '<h3 class="ug-card__title">' + escapeHtml(dv.label) + '</h3>' +
          '<p class="ug-card__from">' + fromHtml + '</p>' +
          '<p class="ug-card__grades">' + escapeHtml(T('pdp.related_grades_available')) + '</p>' +
        '</div>';
      wrap.appendChild(a);
    });
  };

  UgPdp.prototype.buildDeviceUrl = function (series) {
    // Lynkrs B6 (6 Oct 2026, T3). Every device has its own canonical URL,
    // /products/<handle>, on BOTH routing modes: window.UpgradeRoutes
    // (snippets/upgrade-routes.liquid) reads it from Shopify, and
    // SERIES_TO_HANDLE is the fallback if that map is missing. If neither
    // knows the series, the collection page -- never a wrong or broken URL.
    // The "page" mode used to link ?device=<series> on the device page's own
    // path; that page now forwards to the product (sections/upgrade-pdp
    // .liquid), so no link built here goes to it any more.
    var routes = (typeof window !== 'undefined') ? window.UpgradeRoutes : null;
    var routed = (routes && typeof routes.productUrl === 'function') ? routes.productUrl(series) : null;
    if (routed) { return routed; }
    var handle = SERIES_TO_HANDLE[series];
    // Arabic port, 7 Oct 2026 (pD): the fallback keeps the language root, as the
    // Arabic slice did ('/' in English, '/ar/' on /ar; rule R7).
    return handle ? routeRoot() + 'products/' + handle : this.config.collectionUrl;
  };

  // ---- Event wiring ----------------------------------------------------

  UgPdp.prototype.wireEvents = function () {
    var self = this;
    var gradesWrap = qs(this.root, '[data-ug-pdp-grades]');
    var storageWrap = qs(this.root, '[data-ug-pdp-storages]');

    if (gradesWrap) {
      gradesWrap.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-grade]') : null;
        if (!btn) { return; }
        self.selectGrade(btn.getAttribute('data-grade'));
      });
      gradesWrap.addEventListener('keydown', function (e) { self.handleRadioKeydown(e, gradesWrap, 'data-grade'); });
    }
    if (storageWrap) {
      storageWrap.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-storage]') : null;
        if (!btn) { return; }
        self.selectStorage(btn.getAttribute('data-storage'));
      });
      storageWrap.addEventListener('keydown', function (e) { self.handleRadioKeydown(e, storageWrap, 'data-storage'); });
    }

    // Both buy controls -- the one in the buy box and the one in the mobile
    // sticky bar -- run the same handler, so they can never behave
    // differently from each other.
    this.addToCartButtons().forEach(function (btn) {
      btn.addEventListener('click', function () { self.handleAddToCart(btn); });
    });
    // 2.0 (13 Oct 2026): keyboard and state polish for the option rows, and
    // the buy button's way back when the browser restores this page from its
    // back/forward cache with the "Added" state still on it.
    this.enhanceRadios();
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted && self._atcRestore) { self._atcRestore(); }
    });
  };

  // 2.0 (13 Oct 2026, D8; PDP audit #10; AUDIT 6.6 d5). The Grade, Storage and
  // Colour groups are radiogroups whose every button was a tab stop and which
  // had no Home/End. A radiogroup is one tab stop: the checked radio, or the
  // first one that can be chosen. Arrow keys are handleRadioKeydown()'s and are
  // untouched (they move focus AND choose, as the pattern asks); Home/End are
  // added here the same way. A MutationObserver re-points the stop whenever the
  // script redraws a row or changes aria-checked / disabled, so no existing
  // code path needed a change. Also marks the page as touched by the customer
  // (data-ug-acted), which is what lets the CSS play a selection motion for a
  // choice they made and not for the default one the page opens on.
  UgPdp.prototype.enhanceRadios = function () {
    var self = this;
    var box = qs(this.root, '[data-ug-pdp-options]');
    if (!box || this._radiosEnhanced) { return; }
    this._radiosEnhanced = true;
    var radios = function (group) {
      return qsa(group, '[role="radio"]').filter(function (el) { return !el.disabled; });
    };
    var sync = function () {
      qsa(box, '[role="radiogroup"]').forEach(function (group) {
        var all = qsa(group, '[role="radio"]');
        var live = radios(group);
        var stop = live.filter(function (el) { return el.getAttribute('aria-checked') === 'true'; })[0] || live[0] || null;
        all.forEach(function (el) {
          var want = el === stop ? '0' : '-1';
          if (el.getAttribute('tabindex') !== want) { el.setAttribute('tabindex', want); }
        });
      });
    };
    sync();
    // A storage or colour that has just BECOME the checked one, after the
    // customer has touched the page, gets its one-shot seat (CSS .is-seated).
    var seat = function (records) {
      if (!self.root.hasAttribute('data-ug-acted')) { return; }
      records.forEach(function (r) {
        var el = r.target;
        if (r.type !== 'attributes' || r.attributeName !== 'aria-checked' || r.oldValue === 'true' || el.getAttribute('aria-checked') !== 'true') { return; }
        if (!el.classList || !(el.classList.contains('ug-storage-chip') || el.classList.contains('ug-colour-chip'))) { return; }
        el.classList.remove('is-seated');
        void el.offsetWidth;
        el.classList.add('is-seated');
        el.addEventListener('animationend', function done() { el.classList.remove('is-seated'); el.removeEventListener('animationend', done); });
      });
    };
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) { sync(); seat(records); }).observe(box, {
        childList: true, subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['aria-checked', 'disabled']
      });
    }
    box.addEventListener('keydown', function (e) {
      if (e.key !== 'Home' && e.key !== 'End') { return; }
      var group = e.target && e.target.closest ? e.target.closest('[role="radiogroup"]') : null;
      if (!group) { return; }
      var items = radios(group);
      var to = e.key === 'Home' ? items[0] : items[items.length - 1];
      if (!to) { return; }
      to.focus();
      to.click();
      e.preventDefault();
    });
    var acted = function () { self.root.setAttribute('data-ug-acted', ''); };
    box.addEventListener('pointerdown', acted, { once: true });
    box.addEventListener('keydown', acted, { once: true });
  };

  UgPdp.prototype.handleRadioKeydown = function (e, wrap, attr) {
    var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (keys.indexOf(e.key) === -1) { return; }
    // Disabled options are stepped over: a sold-out colour chip cannot take
    // focus or a click, so landing on it would strand the arrow keys. Grade
    // chips are never disabled. A storage segment is disabled only when no
    // grade of it can be ordered and it is not the selected one (3 Oct 2026,
    // storageSoldOut), so on every storage that can be sold this list is the
    // same list it always was. (Colour variants, ported 27 Sep 2026.)
    var items = qsa(wrap, '[' + attr + ']').filter(function (el) { return !el.disabled; });
    if (!items.length) { return; }
    var idx = items.indexOf(document.activeElement);
    if (idx === -1) { idx = 0; }
    // Right-to-left pages (Arabic) put the next item on the LEFT, so the
    // horizontal arrows swap meaning there; down/up are unchanged. Ported
    // from upgrade-i18n (Phase 1), reading the page's own dir attribute.
    var docEl = (typeof document !== 'undefined' && document) ? document.documentElement : null;
    var rtl = !!docEl && String(docEl.dir || '').toLowerCase() === 'rtl';
    var forwardKey = rtl ? 'ArrowLeft' : 'ArrowRight';
    var dir = (e.key === forwardKey || e.key === 'ArrowDown') ? 1 : -1;
    var next = (idx + dir + items.length) % items.length;
    items[next].focus();
    items[next].click();
    e.preventDefault();
  };

  UgPdp.prototype.selectGrade = function (key) {
    // Validated here as well as at the URL boundary: this is the one place
    // state.selectedGrade is set from a DOM attribute, and everything
    // downstream indexes GRADE_INFO with it.
    this.state.selectedGrade = isValidGrade(key) ? key : null;
    // These two functions (and selectColour, on a product with colours --
    // ported 27 Sep 2026) are the ONLY customer-initiated entries into
    // updateAll(). The other callers are the boot paint, the Shopify product
    // fetch resolving, and repaintPrices() on ug:prices:ready -- none of which
    // the customer caused. This flag is what keeps the page silent on load and
    // on a late price settle that changes nothing they can perceive.
    this._userActed = true;
    this.updateAll();
  };

  UgPdp.prototype.selectStorage = function (storage) {
    this.state.selectedStorage = storage;
    this._userActed = true;
    this.updateAll();
  };

  // ---- Reactive render, called on every grade/storage change ----------

  UgPdp.prototype.updateAll = function () {
    var d = this.state.device;
    var storage = this.state.selectedStorage;
    // One validated read for the whole render. `grade` is only ever truthy
    // here when it is a real key, so nothing below can index GRADE_INFO with
    // something that arrived from a URL.
    var grade = isValidGrade(this.state.selectedGrade) ? this.state.selectedGrade : null;
    var info = gradeInfo(grade);
    var variant = this.currentVariant();
    // The one live read for this render: the Shopify price of the grade and
    // storage actually selected. null = unknown; every consumer of it below
    // renders the hotline state instead of a number.
    var shown = grade ? livePrice(variant, grade) : null;
    var noPrice = noPriceLabel(this.config);

    // Colour: only a product with a Colour option gets past hasColours().
    // `colour` is the orderable selected colour for this grade and storage,
    // or null; `heldColour` is the customer's own choice while it is sold out
    // here (never ordered -- see resolveColour). Both are always null on a
    // Grade/Storage product. (Colour variants, ported 27 Sep 2026.)
    // MOVED here, 29 Sep 2026 (review fix, minor): `colour` used to be
    // declared further down, AFTER the gradeBtns.forEach loop below already
    // read it (`self.variantFor(key, storage, colour)`) to build each
    // grade pill's "Offer" match. `var` is function-scoped and hoisted, so
    // that read saw `undefined`, not the resolved colour -- on a colour
    // product this made variantFor() fail closed (available: false) even
    // for an in-stock, on-offer SKU, so the pill never showed. Latent while
    // the offer switch is off (today).
    var colour = null;
    var heldColour = null;
    if (this.hasColours()) {
      var resolved = this.resolveColour(grade, storage);
      colour = resolved.colour;
      heldColour = resolved.held;
      this.updateColourChips(resolved);
      // 7 Oct 2026 (the photo gallery): its photos follow the colour shown --
      // the orderable one, or the customer's own sold-out choice (galleryColour).
      this.syncGalleryColour(this.galleryColour(resolved));
      // POST-LAUNCH 8 OCT (PDP-18): and the same photo, small, beside the
      // row's label, for a phone whose gallery has scrolled away.
      // galleryColour() reads state only, so asking twice is the same answer.
      this.updateColourPeek(this.galleryColour(resolved));
    }

    var self = this;
    var gradeBtns = qsa(this.root, '[data-ug-pdp-grades] [data-grade]');
    gradeBtns.forEach(function (btn) {
      var key = btn.getAttribute('data-grade');
      var priceEl = btn.querySelector('[data-ug-pdp-grade-price]');
      if (priceEl) {
        var gp = livePrice(variant, key);
        if (gp === null) {
          // textContent, not innerHTML: no figure means no currency mark
          // either, so the pill cannot read as "<blank> EGP".
          priceEl.textContent = noPrice;
        } else {
          priceEl.innerHTML = formatMoney(gp) + ccyHtml();
        }
      }
      // OBSIDIAN, 29 Sep 2026 (C2): "Offer" only for the SKU forSku() itself
      // returns for THIS grade -- '' with the offer switch off, so this is
      // a no-op node removal on the shipped state.
      var pillEl = btn.querySelector('[data-ug-pdp-grade-offer]');
      if (pillEl) { pillEl.remove(); }
      if (variant && variant.code && priceEl && priceEl.insertAdjacentHTML) {
        var gradeMatch = self.variantFor(key, storage, colour);
        var pillHtml = self.offerPillFor(variant.code, key, gradeMatch && gradeMatch.available === true);
        if (pillHtml) { priceEl.insertAdjacentHTML('afterend', pillHtml); }
      }
      var selected = key === grade;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    // A storage nothing can be ordered in (storageSoldOut) is muted, says
    // "Sold out" in the segment itself, and is disabled -- except while it is
    // the selected one, which stays checked and operable so the page never
    // strands a selection; Add to cart already says "Out of stock in this
    // grade" for it. Every other storage behaves exactly as it always has.
    var storageBtns = qsa(this.root, '[data-ug-pdp-storages] [data-storage]');
    storageBtns.forEach(function (btn) {
      var value = btn.getAttribute('data-storage');
      var selected = value === storage;
      var soldOut = self.storageSoldOut(value);
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      btn.classList.toggle('is-sold-out', soldOut);
      btn.disabled = soldOut && !selected;
      if (btn.disabled) { btn.setAttribute('aria-disabled', 'true'); } else { btn.removeAttribute('aria-disabled'); }
      if (soldOut) {
        btn.setAttribute('title', plainText(T('pdp.storage_sold_out_title', { storage: value })));
      } else {
        btn.removeAttribute('title');
      }
      var note = btn.querySelector('[data-ug-pdp-storage-note]');
      if (note) { note.hidden = !soldOut; }
    });

    // The rows' labels: "Grade · Platinum", "Storage · 256GB". (Colour is
    // written by updateColourChips, above, on a colour product only.)
    this.setOptionValue('grade', info ? gradeName(grade) : T('pdp.option_choose_one_grade'), !info);
    this.setOptionValue('storage', storage ? isolateText(storage) : '', false);

    var priceEl = qs(this.root, '[data-ug-pdp-price]');
    if (priceEl) {
      if (grade && shown !== null) {
        var nextPrice = formatMoney(shown);
        var changed = priceEl.getAttribute('data-shown') !== plainText(nextPrice);
        priceEl.innerHTML = nextPrice + ccyHtml();
        priceEl.className = 'ug-price ug-price--lg';
        priceEl.setAttribute('data-shown', plainText(nextPrice));
        // Re-trigger the price-change cue: the class has to leave the element
        // and a reflow be forced before it will animate a second time.
        if (changed) {
          priceEl.classList.remove('is-changed');
          void priceEl.offsetWidth;
          priceEl.classList.add('is-changed');
        }
      } else if (grade) {
        // A grade IS selected and Shopify has no price for its SKU. The
        // existing placeholder styling is reused deliberately -- this is the
        // same "there is no number here" state as "Select a grade", and no
        // new styling ships with this change. data-shown is cleared so the
        // next real price still animates as a change.
        priceEl.textContent = noPrice;
        priceEl.className = 'ug-pdp__price-placeholder';
        priceEl.removeAttribute('data-shown');
      } else {
        priceEl.textContent = T('pdp.price_select_grade');
        priceEl.className = 'ug-pdp__price-placeholder';
        priceEl.removeAttribute('data-shown');
      }
    }

    this.updateOfferLabel(variant, grade, storage, colour);

    /* WHAT THEY WOULD PAY, given a valuation they are already carrying, shown
       beside the price and never instead of it. Omar, 24 September 2026.

       The same rule as the catalogue card: under R1 the cart charges the full
       amount and the courier collects it, and the credit is settled at the
       inspection -- so the figure above stays the price, and this is a second,
       clearly-estimated line beneath it. The device's own bonus is folded in by
       UpgradeDeal.creditFor(), which is the single implementation of this
       arithmetic for both surfaces.

       This page has more room than a card, so it states the split the store's
       whole trust argument rests on: the phone's value is an estimate settled
       at inspection, the bonus is not. Nothing renders unless the valuation,
       the published rate and a live price all exist. */
    var tradeEl = qs(this.root, '[data-ug-pdp-trade]');
    if (tradeEl) {
      var dealApi = (typeof window !== 'undefined') ? window.UpgradeDeal : null;
      // 29 Sep 2026 (LOG-029): the sku actually shown, so creditFor() can use
      // the offer's own bonus rate when this exact grade is on offer.
      var tradeSku = (variant && variant.code && grade && window.UpgradePrices &&
          typeof window.UpgradePrices.skuFor === 'function')
        ? window.UpgradePrices.skuFor(variant.code, grade)
        : null;
      var cr = (grade && shown !== null && dealApi && typeof dealApi.creditFor === 'function')
        ? dealApi.creditFor(shown, tradeSku)
        : null;
      if (!cr) {
        tradeEl.hidden = true;
        tradeEl.innerHTML = '';
      } else {
        tradeEl.hidden = false;
        // 28 Sep 2026 (LOG-021): the trade-in goes with the order, and the
        // phone is checked at the door by the person who delivers.
        // 29 Sep 2026 (LOG-028, review fix, dated per finding 10): both
        // branches below dropped ", plus delivery" -- delivery is free, its
        // cost is already in the phone's price.
        // F6, 29 Sep 2026 (fix_now, prices-on-page P7): cr.surplus > 0 keeps
        // the existing "pay you the difference" sentence; an exact match
        // (surplus === 0) has no difference to pay, so it says that instead.
        // Arabic slice: each sentence is ONE template with its figures as
        // placeholders (word order differs in Arabic); the English is the
        // same sentence, word for word.
        tradeEl.innerHTML = cr.covered
          ? '<span class="ug-pdp__trade-head">' + escapeHtml(T('pdp.trade_covered_head')) + '</span>' +
            '<span class="ug-pdp__trade-note">' +
              escapeHtml(T(cr.surplus > 0 ? 'pdp.trade_covered_note' : 'pdp.trade_covered_note_exact',
                { value: money(cr.value), bonus: money(cr.bonus) })) +
            '</span>'
          : '<span class="ug-pdp__trade-head">' + T('pdp.trade_balance_head', { balance: escapeHtml(money(cr.balance)) }) + '</span>' +
            '<span class="ug-pdp__trade-note">' +
              escapeHtml(T('pdp.trade_balance_note', { value: money(cr.value), bonus: money(cr.bonus) })) +
            '</span>';
      }
    }

    // LC1(b) fix (29 Sep 2026, carried, LOG-033 #5; not from these audits).
    // The dark trade-in pitch block (.ug-pdp__tradein, above the accordion)
    // states the bonus is guaranteed but never a figure -- this fills one
    // in, once a grade (and so a price and a sku) is known, through the
    // SAME window.UpgradeDeal.bonusFor(price, sku) every other bonus figure
    // on the site already uses (never a second implementation of the rate/
    // cap arithmetic). Hidden whenever there is no grade yet, no live
    // price, or bonusFor() itself has nothing to report (no rate
    // published) -- never "up to", never a guess.
    var tradeinBonusEl = qs(this.root, '[data-ug-pdp-tradein-bonus]');
    if (tradeinBonusEl) {
      var tibSku = (variant && variant.code && grade && window.UpgradePrices &&
          typeof window.UpgradePrices.skuFor === 'function')
        ? window.UpgradePrices.skuFor(variant.code, grade)
        : null;
      var tibDealApi = (typeof window !== 'undefined') ? window.UpgradeDeal : null;
      var tibBonus = (grade && shown !== null && tibDealApi && typeof tibDealApi.bonusFor === 'function')
        ? tibDealApi.bonusFor(shown, tibSku)
        : null;
      if (typeof tibBonus === 'number' && isFinite(tibBonus) && tibBonus > 0) {
        tradeinBonusEl.hidden = false;
        tradeinBonusEl.innerHTML = T('pdp.tradein_bonus_line', { amount: escapeHtml(money(tibBonus)) });
      } else {
        tradeinBonusEl.hidden = true;
        tradeinBonusEl.innerHTML = '';
      }
    }

    // Saving against brand new, for the grade and storage actually selected.
    // Shown only when the gap is genuine (>=5%); on grades priced at or near
    // new it is omitted rather than spun. Placeholder new prices are marked.
    var vsNew = qs(this.root, '[data-ug-pdp-vsnew]');
    if (!vsNew && priceEl && priceEl.parentNode) {
      // Own our mount point rather than requiring a Liquid edit, so this can
      // ship without touching markup another pass may be editing.
      vsNew = document.createElement('p');
      vsNew.className = 'ug-vsnew ug-vsnew--pdp';
      vsNew.setAttribute('data-ug-pdp-vsnew', '');
      vsNew.hidden = true;
      priceEl.parentNode.insertBefore(vsNew, priceEl.nextSibling);
    }
    if (vsNew) {
      // A struck-through "brand new" price beside a live price is a price
      // COMPARISON CLAIM, not decoration: it asserts what the device costs new
      // today, and it is the number the saving and the percentage are both
      // computed from. Only 4 of the 27 priced variants in
      // upgrade-tradein-data.json carry an observation anyone actually made
      // (new_price_real: true -- iPhone 15 128/256GB and iPhone 16 128GB at 2B
      // Egypt, Galaxy S25 Ultra 256GB at Amazon.eg). The other 23 are
      // generated by that file's own placeholder_rule -- Diamond x 1.2385,
      // rounded to ...999 -- which the file labels "NOT a sourced market
      // price".
      //
      // The old code rendered all 27 and appended "Illustrative new price for
      // this demo" to the 23. That did not save it: the block still printed a
      // hard EGP saving and a percentage computed off a number invented one
      // line earlier, which is self-contradictory rather than honest, and the
      // data file's own display_rule already forbids it ("Never present a
      // placeholder as sourced"). So the gate is on the SOURCE, not on the
      // disclosure: no observation, no comparison, no saving, no percentage.
      //
      // Gating np itself (rather than the render branch) means pct falls to 0
      // and the existing else below does the hiding, so nothing else moves.
      //
      // `ours` is now the LIVE Shopify price. new_price stays where it is --
      // it is a dated market observation of what the device costs brand new,
      // not a Shopify number, and Shopify holds no copy of it. When Shopify
      // has no price for this SKU, `ours` is null, pct falls to 0 and the
      // block hides itself through the existing else: a saving computed
      // against an unknown sale price is exactly the unverifiable number
      // this change exists to remove.
      //
      // And behind the storefront switch (layout/theme.liquid, Theme settings >
      // Upgrade). Off by default: on every phone or on none, Omar, 26 Sep 2026.
      var vsNewOn = !!(window.UpgradeFlags && window.UpgradeFlags.vsNew === true);
      var np = (vsNewOn && variant && variant.new_price_real === true &&
                typeof variant.new_price === 'number') ? variant.new_price : null;
      var ours = shown;
      var pct = (np && ours && np > ours) ? Math.round(((np - ours) / np) * 100) : 0;
      if (pct >= 5) {
        vsNew.hidden = false;
        vsNew.innerHTML =
          '<span class="ug-vsnew__new">' + T('pdp.vsnew_brand_new', { price: escapeHtml(money(np)) }) + '</span>' +
          '<span class="ug-vsnew__save">' + escapeHtml(T('pdp.vsnew_save', { amount: money(np - ours), percent: T('common.percent', { n: pct }) })) + '</span>' +
          '<span class="ug-vsnew__src">' + escapeHtml(T('pdp.vsnew_source', { source: variant.new_price_source || T('pdp.vsnew_source_fallback') })) + '</span>';
      } else {
        vsNew.hidden = true;
        vsNew.innerHTML = '';
      }
    }

    var meaningWrap = qs(this.root, '[data-ug-pdp-grade-meaning]');
    if (meaningWrap) {
      if (info) {
        meaningWrap.hidden = false;
        var nameEl = qs(this.root, '[data-ug-pdp-grade-meaning-name]');
        var textEl = qs(this.root, '[data-ug-pdp-grade-meaning-text]');
        if (nameEl) { nameEl.textContent = T('pdp.grade_meaning_label', { grade: gradeName(grade) }); }
        if (textEl) { textEl.textContent = gradeCosmetic(grade); }
      } else {
        meaningWrap.hidden = true;
      }
    }

    var unitGrade = qs(this.root, '[data-ug-pdp-unit-grade]');
    var unitCosmetic = qs(this.root, '[data-ug-pdp-unit-cosmetic]');
    var unitBattery = qs(this.root, '[data-ug-pdp-unit-battery]');
    // "The grade you chose" panel: grade name only. The RG/RP/RD code is an
    // internal ERP code, not customer copy (launch punch list B10, 27 Sep 2026).
    if (unitGrade) { unitGrade.textContent = info ? gradeName(grade) : T('pdp.unit_grade_empty'); }
    if (unitCosmetic) { unitCosmetic.textContent = info ? gradeCosmetic(grade) : '—'; }
    if (unitBattery) { unitBattery.textContent = info ? isolateText(gradeBattery(grade)) : '—'; }

    this.renderLadder(variant, grade);

    var financeSlot = qs(this.root, '#upgrade-finance-slot');
    if (financeSlot) {
      financeSlot.setAttribute('data-series', d.series);
      financeSlot.setAttribute('data-grade', grade || '');
      financeSlot.setAttribute('data-storage', storage || '');
      // Empty string, never 0, when there is no live price: '' is already
      // this slot's documented "no price yet" signal (it is what a page with
      // no grade selected has always published), and upgrade-finance.js
      // self-hides on it. A 0 here would render a 0 EGP instalment plan.
      financeSlot.setAttribute('data-price', shown === null ? '' : String(shown));
    }
    try {
      var changedDetail = {
        series: d.series,
        device: d.label,
        grade: grade,
        gradeLabel: info ? info.label : null,
        storage: storage,
        // Contract unchanged for the financing agent: a Number in whole
        // EGP, or null. null now also covers "Shopify has no price for
        // this SKU", which listeners must treat exactly as they treat
        // "no grade picked yet" -- show nothing.
        price: shown,
        currency: 'EGP'
      };
      // Added only when there is one, so a Grade/Storage product publishes
      // exactly the detail it always has. (Colour variants, ported 27 Sep 2026.)
      if (colour) { changedDetail.colour = colour; }
      document.dispatchEvent(new CustomEvent('ug:pdp:variant-changed', { detail: changedDetail }));
    } catch (e) { /* CustomEvent unsupported -- non-fatal, slot attributes still updated */ }

    this.updateTradeinCta(d, variant, grade, storage, shown, colour);
    var cartState = this.updateAddToCart(grade, storage, colour, heldColour);
    this.updateSticky(grade, shown);
    // Last, and after updateTradeinCta() -- that call has just written the new
    // target into UpgradeDeal, so UpgradeDeal.totals() inside is fresh.
    this.announceState(info, storage, shown, cartState, colour || heldColour);
    // POST-LAUNCH 8 OCT (PDP-10): the address bar names what is on screen.
    this.syncUrl(grade, storage, colour, heldColour);
  };

  // The grades page's address: the section's own "How we grade" link carries
  // it (grades_url, default /pages/grades), so the two can never differ.
  UgPdp.prototype.gradesUrl = function () {
    var link = qs(this.root, '.ug-pdp__grade-link a');
    var href = (link && typeof link.getAttribute === 'function') ? link.getAttribute('href') : '';
    // Arabic port, 7 Oct 2026 (pD): the fallback keeps the language root (R7).
    return href || (routeRoot() + 'pages/grades');
  };

  UgPdp.prototype.renderLadder = function (variant, grade) {
    var wrap = qs(this.root, '[data-ug-pdp-ladder]');
    if (!wrap) { return; }
    var noPrice = noPriceLabel(this.config);
    // One live Shopify read per grade, for the storage on screen. A grade
    // whose SKU has no price shows the hotline wording in its own cell and
    // the other two grades keep their real prices -- one missing SKU is not
    // a reason to take the whole ladder down, and it is never filled in from
    // the static file it used to come from.
    /* data-label carries the grade word into the cell itself. It is what the
       phone layout renders through ::after (upgrade-core.css section 07): under
       750px the table stops being a grid and becomes stacked row cards, and a
       cell that has left its column has to say which grade it belongs to.
       The PDP used to omit it and side-scroll a 640px table instead, which put
       the third grade off-screen behind a horizontal drag most people never
       made. The Grades page has always rendered this same table as cards; both
       now do. Omar, 24 September 2026. */
    var self = this;
    var priceRow = GRADE_ORDER.map(function (k) {
      var cls = k === grade ? ' class="is-selected"' : '';
      var p = livePrice(variant, k);
      var cell = (p === null) ? escapeHtml(noPrice) : escapeHtml(money(p));
      // OBSIDIAN, 29 Sep 2026 (C2): "Offer" only for the SKU forSku() itself
      // returns for this grade at the storage on screen -- '' with the
      // offer switch off.
      if (variant && variant.code) {
        var gradeMatch = self.variantFor(k, self.state.selectedStorage, self.state.selectedColour);
        cell += self.offerPillFor(variant.code, k, gradeMatch && gradeMatch.available === true);
      }
      return '<td' + cls + ' data-label="' + escapeHtml(gradeName(k)) + '">' + cell + '</td>';
    }).join('');
    var condRow = GRADE_ORDER.map(function (k) {
      var cls = k === grade ? ' class="is-selected"' : '';
      return '<td' + cls + ' data-label="' + escapeHtml(gradeName(k)) + '">' + escapeHtml(gradeCosmetic(k)) + '</td>';
    }).join('');
    var batteryRow = GRADE_ORDER.map(function (k) {
      var cls = k === grade ? ' class="is-selected"' : '';
      return '<td' + cls + ' data-label="' + escapeHtml(gradeName(k)) + '">' + escapeHtml(isolateText(gradeBattery(k))) + '</td>';
    }).join('');
    // Lynkrs batch 2, SEO-Internal (W4, 6 Oct 2026): each grade's name links to
    // the grades page, the same address as the "How we grade" link below the
    // grade cards (the section's grades_url setting). The words are unchanged.
    var gradesHref = escapeHtml(this.gradesUrl());
    var headRow = GRADE_ORDER.map(function (k) {
      var cls = k === grade ? ' class="is-selected"' : '';
      return '<th' + cls + '><a class="ug-ladder__grade" href="' + gradesHref + '">' + escapeHtml(gradeName(k)) + '</a></th>';
    }).join('');
    wrap.innerHTML =
      // .ug-ladder__corner: the empty top-left cell. Named so the phone layout
      // can drop it -- the grade heads become a full-width strip of three.
      // Round 3 (device route): the corner has no words, so it is announced as an
      // empty cell instead of an empty column header (axe empty-table-header).
      // role="cell" is allowed on <th>; the CSS keys on the class and the tag.
      '<thead><tr><th class="ug-ladder__corner"' + (this.root.hasAttribute('data-ug-pdp-route') ? ' role="cell"' : '') + '></th>' + headRow + '</tr></thead>' +
      '<tbody>' +
        '<tr><th>' + escapeHtml(T('pdp.ladder_price')) + '</th>' + priceRow + '</tr>' +
        '<tr><th>' + escapeHtml(T('pdp.ladder_cosmetic')) + '</th>' + condRow + '</tr>' +
        '<tr><th>' + escapeHtml(T('pdp.ladder_battery')) + '</th>' + batteryRow + '</tr>' +
      '</tbody>' +
      '<tfoot><tr><td colspan="4" class="ug-ladder__identical">' + escapeHtml(T('pdp.ladder_identical')) + '</td></tr></tfoot>';
  };

  // ---- Offer label (OBSIDIAN, 29 Sep 2026, C2) --------------------------
  //
  // [data-ug-pdp-offer] exists in the DOM at all only when the theme
  // setting settings.ug_offer_enabled is checked (see sections/upgrade-pdp
  // .liquid / upgrade-product.liquid) -- with it unchecked (the shipped
  // state) this method finds nothing and returns on its first line, exactly
  // the zero-trace behaviour every other offer mount in this theme keeps.
  // Even with the mount present, nothing renders unless window.UpgradeOfferUI
  // exists AND forSku() returns a real, in-stock offer for the EXACT SKU on
  // screen -- "sold out" means no offer, because `available` must be
  // exactly `true` (contract 8.8 / spec C3's fail-closed rule), never
  // merely "not known yet".
  UgPdp.prototype.updateOfferLabel = function (variant, grade, storage, colour) {
    var mount = qs(this.root, '[data-ug-pdp-offer]');
    if (!mount) { return; }
    var wasRow = qs(this.root, '[data-ug-pdp-was]');
    var srText = qs(this.root, '[data-ug-pdp-offer-sr]');
    var offerUi = (typeof window !== 'undefined') ? window.UpgradeOfferUI : null;
    var hide = function () {
      mount.hidden = true;
      mount.innerHTML = '';
      if (wasRow) { wasRow.hidden = true; }
      if (srText) { srText.textContent = ''; }
    };
    if (!offerUi || !grade || !variant || !variant.code) { hide(); return; }
    var prices = (typeof window !== 'undefined') ? window.UpgradePrices : null;
    var sku = (prices && typeof prices.skuFor === 'function') ? prices.skuFor(variant.code, grade) : null;
    if (!sku) { hide(); return; }
    var match = this.variantFor(grade, storage, colour);
    var available = !!(match && match.available === true);
    var res = offerUi.forSku(sku, null, { available: available });
    if (!res) { hide(); return; }
    var offer = (typeof offerUi.offer === 'function') ? offerUi.offer() : null;
    var end = offer ? offer.endShort : '';
    var hasWas = typeof res.was === 'number' && res.was !== null;

    mount.hidden = false;
    mount.innerHTML =
      '<span class="ug-opill" data-ug-pdp-offer-pill></span>' +
      '<span class="ug-olabel__until" data-ug-pdp-offer-until></span>' +
      '<span class="ug-cd" data-ug-pdp-offer-cd></span>' +
      (res.termsUrl ? '<a class="ug-olabel__terms" data-ug-pdp-offer-terms href="' + escapeHtml(res.termsUrl) + '">' + escapeHtml(T('pdp.offer_terms')) + '</a>' : '');
    var pillEl = qs(mount, '[data-ug-pdp-offer-pill]');
    var untilEl = qs(mount, '[data-ug-pdp-offer-until]');
    // The merchant's offer name in the page language (pick() reads { en, ar }).
    var name = pick(res.name) || pick(offer && offer.name) || T('pdp.offer_name_fallback');
    if (pillEl) { pillEl.textContent = name; }
    if (untilEl) { untilEl.textContent = end ? (hasWas ? T('pdp.offer_until_short', { date: end }) : T('pdp.offer_until', { date: end })) : ''; }
    var cdEl = qs(mount, '[data-ug-pdp-offer-cd]');
    if (cdEl && typeof offerUi.mountCountdown === 'function') { offerUi.mountCountdown(cdEl, { variant: 'inline' }); }

    if (wasRow) {
      if (hasWas) {
        var figureEl = qs(wasRow, '[data-ug-pdp-was-figure]');
        var saveEl = qs(wasRow, '[data-ug-pdp-save]');
        if (figureEl) { figureEl.textContent = money(res.was); }
        if (saveEl) {
          saveEl.textContent = (typeof res.pct === 'number')
            ? T('pdp.offer_save_pct', { amount: money(res.saving), percent: T('common.percent', { n: res.pct }) })
            : T('pdp.offer_save', { amount: money(res.saving) });
        }
        wasRow.hidden = false;
      } else {
        wasRow.hidden = true;
      }
    }
    if (srText) {
      // FIXED, 29 Sep 2026 (review fix, should-fix): the visual "Was"/"You
      // save" row (above) is now aria-hidden, on the basis that this sentence
      // carries the equivalent information -- which it did not, before this
      // fix, for the was/saving figures specifically. Reuses the exact same
      // "Was"/"You save ... EGP (N%)" fragments the visual row already
      // renders (formatMoney(res.was), res.saving, res.pct), not new copy.
      var wasSentence = hasWas
        ? ((typeof res.pct === 'number')
          ? T('pdp.offer_sr_was_pct', { was: money(res.was), saving: money(res.saving), percent: T('common.percent', { n: res.pct }) })
          : T('pdp.offer_sr_was', { was: money(res.was), saving: money(res.saving) }))
        : '';
      srText.textContent = T('pdp.offer_sr_price', { price: money(res.price) }) + wasSentence +
        (end ? T('pdp.offer_sr_ends', { date: end }) : '.');
    }
  };

  // Grade pills and the grade ladder show "Offer" only for the SKUs
  // forSku() actually returns (contract: "Grade pills and the grade table
  // show 'Offer' only for SKUs forSku returns"). Returns '' with the switch
  // off or with no live UpgradeOfferUI, so every caller can append this
  // unconditionally.
  UgPdp.prototype.offerPillFor = function (variantCode, grade, available) {
    var offerUi = (typeof window !== 'undefined') ? window.UpgradeOfferUI : null;
    var prices = (typeof window !== 'undefined') ? window.UpgradePrices : null;
    if (!offerUi || !prices || typeof prices.skuFor !== 'function' || !variantCode || !grade) { return ''; }
    var sku = prices.skuFor(variantCode, grade);
    if (!sku) { return ''; }
    var res = offerUi.forSku(sku, null, { available: available === true });
    return res ? '<span class="ug-opill ug-opill--sm" data-ug-pdp-grade-offer>' + escapeHtml(T('pdp.offer_pill')) + '</span>' : '';
  };

  // The trade-in control. It is the ONLY thing this function touches now:
  // the buy control is updateAddToCart's, and the two are separate buttons
  // in the markup. Previously one button shipped labelled "Continue on
  // WhatsApp", was rewritten by JS into a trade-in link, and no control on
  // the page bought the phone.
  //
  // The selector accepts the retired hook as well so any surface still
  // carrying the old attribute keeps working rather than losing its CTA.
  UgPdp.prototype.tradeinCta = function () {
    return qs(this.root, '[data-ug-pdp-tradein-cta], [data-ug-pdp-whatsapp]');
  };

  UgPdp.prototype.updateTradeinCta = function (d, variant, grade, storage, price, colour) {
    var mainBtn = this.tradeinCta();
    if (!mainBtn) { return; }
    if (!isValidGrade(grade)) {
      mainBtn.setAttribute('aria-disabled', 'true');
      mainBtn.classList.add('is-disabled');
      mainBtn.removeAttribute('href');
      /* 28 Sep 2026 (LOG-021 review): a phone pinned on ANOTHER product page
         must not stay one tap from ordering here -- the bar clips its name,
         so the customer could order a phone they are not looking at. It stays
         pinned (the deal survives browsing) but without its variant, so the
         bar offers a link back to that phone ("View <phone>" since Lynkrs T2,
         6 Oct 2026; it read "Choose your phone"). */
      try {
        var dealHere = window.UpgradeDeal;
        var pinnedHere = dealHere && dealHere.get ? dealHere.get().target : null;
        if (pinnedHere && pinnedHere.variantId && d && pinnedHere.series !== d.series) {
          var unordered = {};
          Object.keys(pinnedHere).forEach(function (k) { if (k !== 'variantId') { unordered[k] = pinnedHere[k]; } });
          dealHere.setTarget(unordered);
        }
      } catch (e) { /* shared state unavailable -- nothing pinned to guard */ }
      return;
    }
    // Record what they want, so the trade-in page can pin it and the sticky
    // deal bar can show what they'd actually pay. Params are named `target*`
    // so they can never be confused with the trade-in tool's own `?device=`,
    // which means the phone being handed IN.
    if (window.UpgradeDeal) {
      if (price === null || typeof price !== 'number') {
        // No live Shopify price for this SKU, so there is no target to
        // publish. A null price MUST NOT be written into the shared deal
        // state: UpgradeDeal.totals() reads `state.target.price || 0`
        // (assets/upgrade-deal.js:136) and would put a 0 EGP device, a 0 EGP
        // bonus and a 0 EGP balance on the deal bar and carry them to the
        // trade-in page. Clearing the target shows nothing, which is the
        // honest state, and also drops any target left from an earlier
        // selection so nothing stale survives on screen.
        if (window.UpgradeDeal.clearTarget) { window.UpgradeDeal.clearTarget(); }
      } else {
        var target = {
          series: d.series,
          label: d.label,
          image: deviceImage(d),
          grade: grade,
          gradeLabel: gradeInfo(grade) ? gradeInfo(grade).label : '',
          storage: storage,
          code: variant.code,
          price: price   // live Shopify variant price, whole EGP
        };
        // Colour variants, ported 27 Sep 2026. The colour's display name, for
        // the booking form. Present only when the product has colours and one
        // is orderable; a Grade/Storage product writes the same target it
        // always has, with no colour key. The price above does not depend on
        // it.
        if (colour) {
          target.colour = colour;
          // The colour variant's SKU travels with it, so the booking form can
          // name the colour to staff from the SKU's colour code
          // (sections/upgrade-book.liquid staffColour), whatever the option
          // value itself says.
          var colourRec = this.variantFor(grade, storage, colour);
          if (colourRec && colourRec.sku) { target.sku = colourRec.sku; }
        }
        // 28 Sep 2026 (LOG-021): a trade-in ends in an order. The deal bar and
        // the trade-in page order THIS phone, so the target carries the Shopify
        // variant its Add to cart would use -- only when that variant can be
        // sold, the same test the buy button applies. Without it they offer
        // a link to this phone's page ("View <phone>"), which comes back here.
        var orderRec = this.variantFor(grade, storage, colour);
        if (orderRec && orderRec.id && orderRec.available) { target.variantId = String(orderRec.id); }
        var spHere = this.state.shopifyProduct;
        // Lynkrs B6 (6 Oct 2026, T3): with no Shopify product loaded, the
        // phone's own product page from window.UpgradeRoutes, before this
        // page's own address.
        var routesHere = (typeof window !== 'undefined') ? window.UpgradeRoutes : null;
        var routedHere = (routesHere && typeof routesHere.productUrl === 'function') ? routesHere.productUrl(d.series) : null;
        target.url = (spHere && spHere.handle)
          ? routeRoot() + 'products/' + spHere.handle
          : (routedHere || ((typeof location !== 'undefined') ? location.pathname + location.search : ''));
        window.UpgradeDeal.setTarget(target);
      }
    }

    // Trade-in is the store's primary goal, so it keeps the primary button on
    // every product page. The hotline/WhatsApp CTA lives on the contact line
    // below the buy box, where it is still one tap away; the buy control sits
    // beside this one and is never relabelled by this function.
    mainBtn.href = this.config.tradeinUrl +
      (this.config.tradeinUrl.indexOf('?') === -1 ? '?' : '&') +
      'target=' + encodeURIComponent(d.series || '') +
      '&targetGrade=' + encodeURIComponent(grade) +
      '&targetStorage=' + encodeURIComponent(storage || '');
    mainBtn.removeAttribute('aria-disabled');
    mainBtn.classList.remove('is-disabled');
    mainBtn.removeAttribute('target');
    mainBtn.removeAttribute('rel');
    mainBtn.setAttribute('title', plainText(T('pdp.tradein_cta_title')));
  };

  // ---- the buy control (BLK-4) ----------------------------------------
  //
  // Both copies of it: the one in the buy box and the one in the mobile
  // sticky bar. They are updated together by one function so they can never
  // show different states of the same variant.
  UgPdp.prototype.addToCartButtons = function () {
    return qsa(this.root, '[data-ug-pdp-addcart], [data-ug-pdp-sticky-addcart]');
  };

  UgPdp.prototype.hotline = function () {
    return digitsOnly(this.config.hotlineNumber) || '19759';
  };

  // LF5 fix (29 Sep 2026, fix_now, audit w4k72rk5w). One shared, visible,
  // role="status" message for the buy control -- a real reason a refused
  // add or a same-cart-trade-in conflict cannot be silently invisible, the
  // way window.alert()/window.confirm() were wherever a webview (or this
  // build's own audit tooling) suppresses native dialogs. Created once,
  // inserted right after the main CTA row (in-flow, unlike the sticky bar,
  // which is position:fixed and cannot usefully host a sibling message),
  // and scrolled into view when the STICKY button was the one pressed, so
  // it is discoverable either way.
  UgPdp.prototype.addToCartMsg = function () {
    if (this._addToCartMsg && this._addToCartMsg.parentNode) { return this._addToCartMsg; }
    // Anchored on the MAIN buy button specifically (not the sticky copy,
    // and not the shared `.ug-pdp__cta-row` wrapper it sits in) -- the one
    // element every product-page layout is guaranteed to render exactly
    // once, so this cannot come back null on a page whose CTA row markup
    // ever changes shape.
    // 2.0 (D1 / 13 Oct PDP audit #6): sections/upgrade-product.liquid prints
    // an empty role="alert" region that is never hidden, so the text a refused
    // add puts in it is announced (a region created and filled in one tick is
    // often missed). Use it when it is there; the mirror route has none and
    // gets the node below, as before.
    var printed = qs(this.root, '[data-ug-pdp-addcart-msg]');
    if (printed) { this._addToCartMsg = printed; return printed; }
    var anchor = qs(this.root, '[data-ug-pdp-addcart]');
    if (!anchor || !anchor.parentNode) { return null; }
    var msg = document.createElement('div');
    // Styled entirely in assets/upgrade-pdp.css -- this page never loads
    // upgrade-tradein.css, so the "ug-note" family of classes (defined only
    // there) would style nothing here; this class carries its own colour.
    msg.className = 'ug-pdp__addcart-msg';
    msg.setAttribute('role', 'status');
    msg.setAttribute('aria-live', 'polite');
    msg.hidden = true;
    anchor.parentNode.insertBefore(msg, anchor.nextSibling);
    this._addToCartMsg = msg;
    return msg;
  };

  UgPdp.prototype.showAddToCartMsg = function (text, actions, fromSticky) {
    var msg = this.addToCartMsg();
    if (!msg) { return; }
    msg.textContent = '';
    var span = document.createElement('span');
    span.textContent = text;
    msg.appendChild(span);
    (actions || []).forEach(function (a) {
      var node = document.createElement(a.href ? 'a' : 'button');
      if (a.href) { node.href = a.href; } else { node.type = 'button'; }
      node.className = a.href ? 'ug-pdp__addcart-msg-link' : 'ug-btn ug-btn--ghost ug-btn--sm ug-pdp__addcart-msg-btn';
      node.textContent = a.label;
      if (a.onClick) { node.addEventListener('click', a.onClick); }
      msg.appendChild(node);
    });
    msg.hidden = false;
    if (fromSticky && typeof msg.scrollIntoView === 'function') {
      var reduceMotion = false;
      try { reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { reduceMotion = false; }
      try { msg.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' }); } catch (e) { /* older browsers: no-op, the region still shows */ }
    }
  };

  UgPdp.prototype.hideAddToCartMsg = function () {
    var msg = this.addToCartMsg();
    if (!msg) { return; }
    // The printed region stays in the page (emptied, not hidden) so the next
    // text is announced; the created one is hidden as before.
    if (msg.hasAttribute('data-ug-pdp-addcart-msg')) { msg.textContent = ''; return; }
    msg.hidden = true; msg.textContent = '';
  };

  // The single writer of buy-button state. `variantId` is set ONLY on the
  // enabled path and removed on every other one: the button is permanently
  // visible now, so leaving a previous selection's id on a disabled button
  // would open a stale-variant-id path into /cart/add.js that was only ever
  // unreachable because the button used to hide itself.
  // `busy` (POST-LAUNCH 8 OCT, TC-15): the loading state only; it marks both
  // buttons aria-busy, and every other call clears it.
  UgPdp.prototype.setAddToCartState = function (label, variantId, title, busy) {
    this.addToCartButtons().forEach(function (btn) {
      btn.hidden = false;
      btn.textContent = label;
      if (variantId) {
        btn.disabled = false;
        btn.setAttribute('data-variant-id', variantId);
      } else {
        btn.disabled = true;
        btn.removeAttribute('data-variant-id');
      }
      if (title) { btn.setAttribute('title', plainText(title)); } else { btn.removeAttribute('title'); }
      if (busy) { btn.setAttribute('aria-busy', 'true'); } else { btn.removeAttribute('aria-busy'); }
    });
    // 2.0 (D2): the reason, in words, under the button (it was only a title).
    this.syncAddcartHelp(title);
  };

  // 2.0 (13 Oct 2026, D2; PDP audit #4). The buy button's reason ("Choose a
  // grade above...", "This grade is sold out...") was a hover-only title, which
  // a phone never shows. The section prints a helper line under the button;
  // this keeps it equal to the title in every state and ties it to the main
  // button (aria-describedby) only while it has words, so a screen reader is
  // not told a reason that no longer applies. No new words: it is the title.
  UgPdp.prototype.syncAddcartHelp = function (tip) {
    var help = qs(this.root, '[data-ug-pdp-addcart-help]');
    if (!help) { return; }
    var main = qs(this.root, '[data-ug-pdp-addcart]');
    var text = tip ? plainText(tip) : '';
    if (help.textContent !== text) { help.textContent = text; }
    help.hidden = !text;
    if (!main) { return; }
    if (text) {
      main.setAttribute('aria-describedby', help.id);
      main.removeAttribute('title');
    } else {
      main.removeAttribute('aria-describedby');
    }
  };

  /* Returns a state token naming the branch it took. announceState() reads it
     rather than reading the button's label back out of the DOM: a DOM read
     would be a second source of truth and would desynchronise silently the
     moment anyone edits a label string. setAddToCartState() remains the single
     writer of button state -- nothing below writes the button twice. */
  UgPdp.prototype.updateAddToCart = function (grade, storage, colour, heldColour) {
    var hotline = this.hotline();
    this._loadingShown = false;

    if (!isValidGrade(grade)) {
      this.setAddToCartState(T('pdp.addcart_select_grade'), null,
        T('pdp.addcart_select_grade_title'));
      return 'no-grade';
    }

    var sp = this.state.shopifyProduct;

    /* POST-LAUNCH 8 OCT (TC-15; final-check-8oct/rendered/RENDERED-CHECK.md
       item 13). A ?grade= link (shop cards, the trade-in handoff) paints a
       grade before /products/<handle>.js has answered, and this used to fall
       straight into the no-online-route branch below: "Call 19759 to order"
       for the 0.5 s or more the fetch takes, then "Add to cart". Nothing is
       known yet, so the button says it is loading -- disabled, no variant id,
       aria-busy -- in the section's own words (config.loadingLabel, a locale
       key). The hotline label stays for what it means: the fetch failed, or
       no variant matches. tryLoadShopifyProduct() repaints when it settles. */
    // REVIEW 8 OCT (R3): _loadingShown says whether the last paint left the
    // loading words on the button; tryLoadShopifyProduct() repaints at the
    // end of the fetch while it is true.
    if (!sp && this._spLoading && this.config.loadingLabel) {
      this._loadingShown = true;
      this.setAddToCartState(this.config.loadingLabel, null, '', true);
      return 'loading';
    }

    // grade|storage on a Grade/Storage product, exactly as before;
    // grade|storage|colour on a colour product, for the colour on screen and
    // no other (see variantFor). (Colour variants, ported 27 Sep 2026.)
    var match = sp ? this.variantFor(grade, storage, colour) : null;

    if (!match && sp) {
      // The product loaded, its options matched and its grades were
      // recognised, but this grade+storage is not in the map. If NO variant
      // of the product carries this storage at all, the likely cause is a
      // storage label on the imported product that does not correspond to
      // any storage in upgrade-tradein-data.json -- the last silent failure
      // path left in BLK-4. Say so, once per storage, naming both spellings.
      var wantKey = storageKey(storage);
      var known = (sp.storageLabels || []).some(function (s) { return storageKey(s) === wantKey; });
      if (!known) {
        this._warnedStorage = this._warnedStorage || {};
        if (!this._warnedStorage[wantKey]) {
          this._warnedStorage[wantKey] = true;
          warn('product "' + sp.handle + '" has no variant for storage "' + storage +
            '". Storage values found on the product: [' + (sp.storageLabels || []).join(', ') +
            ']. These must match the storage labels in upgrade-tradein-data.json (case and spacing are ignored). ' +
            'Online ordering stays off for this storage; the buy button points at the hotline.');
        }
      }
    }

    if (!match) {
      // Deliberately NOT "out of stock". Upgrade holds this phone and this
      // same page is quoting a price for it; what is missing is the online
      // order route (no product imported, or no variant record for this
      // grade and storage), not the stock. Saying "out of stock" here would
      // tell the customer a lie the price on screen contradicts.
      this.setAddToCartState(T('pdp.call_to_order', { hotline: hotline }), null,
        T('pdp.addcart_no_route_title', { hotline: hotline }));
      return 'no-online-route';
    }

    // Colour products only: the colour the customer CHOSE is sold out in this
    // grade and storage while another colour is not (resolveColour's `held`).
    // The button says so in its own words and takes nothing -- the page never
    // quietly swaps in a colour they did not pick. heldColour is always null
    // on a Grade/Storage product, so this branch cannot be reached there.
    // (Colour variants, ported 27 Sep 2026.)
    if (heldColour && !match.available) {
      this.setAddToCartState(T('pdp.addcart_colour_sold_out'), null,
        T('pdp.addcart_colour_sold_out_title', { colour: heldColour, hotline: hotline }));
      return 'colour-sold-out';
    }

    if (!match.available) {
      // 3 Oct 2026 (the option block): when the whole storage is sold out --
      // every grade, every colour (storageSoldOut, the same test that mutes
      // its segment) -- "another grade may be available" is false, and it
      // contradicted the segment's own "<storage> is sold out in every
      // grade." The reason then points at the storage, in the segment's
      // words. The button's label and its state are unchanged.
      this.setAddToCartState(T('pdp.addcart_out_of_stock'), null,
        this.storageSoldOut(storage)
          ? T('pdp.addcart_storage_sold_out_title', { storage: storage, hotline: hotline })
          : T('pdp.addcart_out_of_stock_title', { hotline: hotline }));
      return 'out-of-stock';
    }

    /* THE PAGE MUST BE ABLE TO SHOW A PRICE BEFORE IT WILL TAKE AN ORDER.
       Added 24 September 2026 after the UI/UX audit.

       Every other state above is about the VARIANT. This one is about the
       PRICE, and it was missing: when the price map is dark, every figure on
       this page falls back to "Call 19759 to order" - and Add to cart stayed
       live, because a Shopify variant existed and that was the only thing
       being asked. A customer could commit to a phone the page had just told
       them it could not price.

       The cart would have charged the correct Shopify price, so this is not an
       overcharge. It is worse in a quieter way: on cash on delivery the first
       time anyone sees the amount is when the courier is at the door with it.
       That is the exact failure this store is built to avoid.

       A price we cannot show is a price we cannot take an order against. */
    var shownPrice = livePrice(this.currentVariant(), grade);
    if (shownPrice === null || shownPrice === undefined) {
      this.setAddToCartState(T('pdp.call_to_order', { hotline: hotline }), null,
        T('pdp.addcart_no_price_title', { hotline: hotline }));
      return 'no-price';
    }

    this.setAddToCartState(T('pdp.add_to_cart'), match.id, '');
    return 'orderable';
  };

  /* 2.0 (13 Oct 2026, D1; PDP audit #2). The buy buttons used to be made
     `disabled` on click, which drops keyboard focus to <body>, and the page
     then hard-navigated with no sign it had worked. Now, from one place:
       busy    aria-disabled + aria-busy (never `disabled`: focus stays), the
               label "Adding..." in a button whose width is pinned, so the
               in-button progress bar (upgrade-ds.css .ug-btn[aria-busy]) moves
               nothing;
       done    the button shows "Added" with a check mark (.is-done) as soon as
               the add has succeeded, and ug:cart:added is fired at once;
               with a cart drawer (window.UgCartDrawer.enabled) the drawer opens
               on the same tick (it takes focus; closing it returns focus to
               the button) and the button is put back after ~500 ms; with no
               drawer the page goes to /cart after a 250 ms "Added", as before;
       failed  the button is restored and takes focus back.
     Nothing about WHICH variant is added, its price, the trade-in path or the
     request itself changed. */
  UgPdp.prototype.atcBusy = function (btns) {
    btns.forEach(function (b) {
      b.style.setProperty('--ug-btn-w', b.offsetWidth + 'px');
      b.setAttribute('aria-disabled', 'true');
      b.setAttribute('aria-busy', 'true');
      b.textContent = T('pdp.adding');
    });
  };

  // Puts every button back as it was (text and state), clears the busy and
  // done marks, and lets the next press through.
  UgPdp.prototype.atcReset = function (restore, focusEl) {
    this._adding = false;
    clearTimeout(this._atcTimer);
    restore.forEach(function (r) {
      var b = r.el;
      b.textContent = r.text;
      b.classList.remove('is-done');
      b.removeAttribute('aria-busy');
      b.removeAttribute('aria-disabled');
      b.style.removeProperty('--ug-btn-w');
      // Round 3 (device route): an icon-only "done" button (below) was named
      // by an aria-label for the moment it said nothing; its text is back.
      if (b.hasAttribute('data-ug-done-icon')) { b.removeAttribute('aria-label'); }
      b.disabled = r.disabled;
    });
    this._atcRestore = null;
    if (focusEl && typeof focusEl.focus === 'function') {
      try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); }
    }
  };

  UgPdp.prototype.atcDone = function (btns, restore, data) {
    var self = this;
    var label = this.config.addedLabel;
    btns.forEach(function (b) {
      b.removeAttribute('aria-busy');
      b.removeAttribute('aria-disabled');
      b.classList.add('is-done');
      // Round 3 (device route): a button that carries data-ug-done-icon is too
      // narrow for the success label (the sticky bar's, 97px at 390, when the
      // label is the long fallback sentence): it shows the check mark alone
      // (.ug-btn.is-done::before) and the label is its accessible name for
      // the ~500 ms it lasts, so nothing re-flows. The page's status region
      // below says the label aloud in every case. Only the device section
      // prints the attribute, and only when the label is long.
      if (label && b.hasAttribute('data-ug-done-icon')) {
        b.setAttribute('aria-label', label);
        b.textContent = '';
      } else if (label) { b.textContent = label; }
    });
    // Heard as well as seen: the page's own status region, with the theme's
    // existing "Item added to your cart" sentence when the label is that one.
    var live = qs(this.root, '[data-ug-pdp-status]');
    if (live && label) { live.textContent = label; this._lastAnnounced = label; }
    var items = (data && data.items) ? data.items : (data ? [data] : []);
    this._atcRestore = function () { self.atcReset(restore, null); };
    try {
      document.dispatchEvent(new CustomEvent('ug:cart:added', { detail: { items: items, source: 'pdp' } }));
    } catch (e) { /* a listener must never stop the add */ }
    var drawer = window.UgCartDrawer;
    var opened = false;
    if (drawer && drawer.enabled && typeof drawer.open === 'function') {
      try { drawer.open(); opened = true; } catch (e) { opened = false; }
    }
    clearTimeout(this._atcTimer);
    this._atcTimer = setTimeout(function () {
      if (opened) { self.atcReset(restore, null); } else { window.location.href = routeRoot() + 'cart'; }
    }, opened ? 500 : 250);
  };

  UgPdp.prototype.handleAddToCart = function (clicked) {
    var self = this;
    if (this._adding) { return; }
    var btns = this.addToCartButtons();
    var live = btns.filter(function (b) { return !b.disabled && b.getAttribute('data-variant-id'); })[0];
    if (!live) { return; }
    var variantId = live.getAttribute('data-variant-id');
    // 2.0: `clicked` is the button that was pressed (the buy box's or the
    // sticky bar's); it gets the focus back on a failure and decides whether
    // the message is scrolled into view. Without it, as before: the first live one.
    var pressed = clicked || live;
    var fromSticky = !!(pressed.getAttribute && pressed.hasAttribute('data-ug-pdp-sticky-addcart'));
    this.hideAddToCartMsg();

    var restore = btns.map(function (b) {
      return { el: b, text: b.textContent, disabled: b.disabled };
    });
    this._adding = true;
    this.atcBusy(btns);

    /* 28 Sep 2026 (LOG-021): a trade-in ends in an order. When a valuation is
       live and the deal's target IS the variant being added, this add goes
       through UpgradeDeal.order() -- the one "Order with trade-in" action --
       which writes the trade-in onto the line as notes (never changing the
       price, ruling R1), checks the cart so one trade-in goes with one phone,
       and parks the valuation. The bonus in the notes is computed on this
       phone's own price, which is why the target must match: updateAll()
       re-pins it on every selection, so a mismatch means stale state, and
       then this is a plain add that leaves the valuation for the bar. */
    var dealApi = null;
    var withTradein = false;
    try {
      dealApi = window.UpgradeDeal;
      var dealState = dealApi && dealApi.get ? dealApi.get() : null;
      withTradein = !!(dealState && dealState.tradein && dealState.target &&
        String(dealState.target.variantId || '') === String(variantId) &&
        typeof dealApi.order === 'function' && typeof dealApi.orderProperties === 'function' &&
        dealApi.orderProperties(dealState.target));
    } catch (e) { withTradein = false; /* shared state unavailable -- a plain add */ }

    // LF5 fix (29 Sep 2026, fix_now, audit w4k72rk5w): the generic
    // add-failure message moves inline too -- same wording as before,
    // window.alert() replaced with the shared status region plus a "View
    // cart" link.
    var failed = function (e) {
      self.atcReset(restore, pressed);
      var text = (withTradein && dealApi && dealApi.orderFailMessage)
        ? dealApi.orderFailMessage(e)
        : T('pdp.add_error_alert', { hotline: self.hotline() });
      self.showAddToCartMsg(text, [{ label: T('pdp.view_cart'), href: routeRoot() + 'cart' }], fromSticky);
    };

    var plainAdd = function () {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) { throw new Error('add failed'); }
          return r.json();
        })
        .then(function (data) { self.atcDone(btns, restore, data); })
        .catch(function (e) { withTradein = false; failed(e); });
    };

    if (withTradein) {
      // order() goes to the cart itself on success. When the cart already has
      // a trade-in (one trade-in, one phone), the customer can still buy this
      // phone -- without a trade-in, and only if they say so (28 Sep audit).
      // LF5 fix (29 Sep 2026, fix_now, audit w4k72rk5w): window.confirm()
      // replaced with an inline "Add without trade-in" button -- it is the
      // confirm's own question, restated as a button instead of a native
      // dialog, so no policy change: saying nothing (leaving the button
      // alone) is the same as Cancel always was.
      dealApi.order().catch(function (e) {
        if (e && e.code === 'already') {
          self.atcReset(restore, pressed);
          self.showAddToCartMsg(
            T('pdp.cart_has_tradein'),
            [{
              label: T('pdp.add_without_tradein'),
              onClick: function () {
                self.hideAddToCartMsg();
                self._adding = true;
                self.atcBusy(btns);
                plainAdd();
              }
            }],
            fromSticky
          );
          return;
        }
        failed(e);
      });
      return;
    }

    plainAdd();
  };

  // The sticky bar carries the same two controls as the buy box: the buy
  // button (kept in step by setAddToCartState, which writes both copies) and
  // the trade-in link, mirrored here. Only the href and the enabled state are
  // mirrored -- each button owns its own label in the markup, so neither can
  // be relabelled into the other's job.
  // `price` is the same live figure the buy box rendered, passed in rather
  // than read again, so the sticky bar and the buy box cannot show two
  // different numbers for one variant.
  UgPdp.prototype.updateSticky = function (grade, price) {
    var priceEl = qs(this.root, '[data-ug-pdp-sticky-price]');
    var stickyCta = qs(this.root, '[data-ug-pdp-sticky-tradein-cta], [data-ug-pdp-sticky-whatsapp]');
    var mainCta = this.tradeinCta();
    if (priceEl) {
      if (isValidGrade(grade) && price !== null) {
        priceEl.innerHTML = formatMoney(price) + ccyHtml();
      } else if (isValidGrade(grade)) {
        priceEl.textContent = noPriceLabel(this.config);
      } else {
        priceEl.textContent = T('pdp.price_select_grade');
      }
      // POST-LAUNCH 8 OCT (PDP-14): a slot with no figure only repeats the
      // buy button's own words ("Select a grade", "Call 19759 to order") and
      // squeezed both buttons until their labels spilled. Marked here,
      // hidden by the POST-LAUNCH 8 OCT block of assets/upgrade-pdp.css; the
      // words stay for anything that reads them.
      var noFigure = !(isValidGrade(grade) && price !== null);
      if (noFigure) { priceEl.setAttribute('data-ug-pdp-sticky-price-empty', ''); } else { priceEl.removeAttribute('data-ug-pdp-sticky-price-empty'); }
    }
    if (stickyCta && mainCta) {
      var href = mainCta.getAttribute('href');
      if (href) {
        stickyCta.href = href;
        stickyCta.removeAttribute('target');
        stickyCta.removeAttribute('rel');
        if (mainCta.hasAttribute('title')) { stickyCta.setAttribute('title', mainCta.getAttribute('title')); } else { stickyCta.removeAttribute('title'); }
        stickyCta.removeAttribute('aria-disabled');
        stickyCta.classList.remove('is-disabled');
      } else {
        stickyCta.removeAttribute('href');
        stickyCta.setAttribute('aria-disabled', 'true');
        stickyCta.classList.add('is-disabled');
      }
    }
    // PG-PDP-1 (W4): a grade just picked may be what brings the bar up.
    this.syncSticky();
  };

  /* THE SINGLE VOICE OF THIS PAGE. 24 September 2026, UI/UX audit.

     Before this, choosing a grade silently rewrote the price and silently
     inserted an Add to cart button that did not previously exist. Nothing on
     any custom surface in the theme was announced at all.

     STABILITY. [data-ug-pdp-status] is Liquid markup inside .ug-buybox, and no
     render path on this page replaces it: renderStatic() rebuilds only the
     grade and storage button lists, and updateAll() writes the price element's
     content and inserts [data-ug-pdp-vsnew] inside .ug-pdp__price-row.
     renderColours() inserts [data-ug-pdp-colours] as a third row INSIDE the
     option block, before the price box, and never touches this node (colour
     variants, ported 27 Sep 2026; option block, 3 Oct 2026), and the option
     labels are written by text alone (setOptionValue). So the
     region is updated by TEXT and never destroyed -- the condition an
     announcement needs to be predictable. A live region that is removed and
     re-inserted with content is announced again by most assistive technology,
     which on this page would mean one announcement per arrow key.
     (renderNotFound() and renderError() do replace root.innerHTML, and both set
     state.painted = false, so nothing repaints after them and nothing is
     announced into detached DOM.)

     THE SALE PRICE IS NOT AN ESTIMATE and must never be softened into one. The
     estimate/guaranteed split governs the TRADE-IN valuation: the phone's value
     is an estimate, the bonus is guaranteed. The sale price on this page is
     what the courier collects on delivery, so it is stated plainly. Only the
     balance clause, which is built on the customer's own phone, carries the
     inspection caveat. */
  UgPdp.prototype.announceState = function (info, storage, shown, cartState, colour) {
    var el = qs(this.root, '[data-ug-pdp-status]');
    if (!el) { return; }

    var phrase = '';
    if (info) {
      // `colour` is the colour on screen (orderable, or the customer's own
      // sold-out choice) and exists only on a product with a Colour option,
      // so a Grade/Storage product says exactly the sentence it always has.
      // Naming it here means a colour the page pre-selected is heard, not
      // just seen, before anyone presses Add to cart. (Colour variants,
      // ported 27 Sep 2026.)
      var spokenGrade = gradeName(gradeKeyFrom(info.label));
      var selectedKey = (storage && colour) ? 'pdp.announce_selected_storage_colour'
        : storage ? 'pdp.announce_selected_storage'
          : colour ? 'pdp.announce_selected_colour' : 'pdp.announce_selected';
      var parts = [T(selectedKey, { grade: spokenGrade, storage: storage, colour: colour })];

      if (shown === null || shown === undefined) {
        parts.push(T('pdp.announce_no_price'));
      } else {
        parts.push(T('pdp.announce_price', { price: money(shown) }));
      }

      // One sentence per button state, in the button's own words.
      if (cartState === 'orderable') {
        parts.push(T('pdp.announce_orderable'));
      } else if (cartState === 'out-of-stock') {
        parts.push(T('pdp.announce_out_of_stock'));
      } else if (cartState === 'colour-sold-out') {
        parts.push(T('pdp.announce_colour_sold_out', { colour: colour }));
      } else if (cartState === 'no-grade') {
        parts.push(T('pdp.announce_no_grade'));
      } else if (cartState === 'loading') {
        // POST-LAUNCH 8 OCT (TC-15): nothing about the button yet; the
        // sentence that follows the product load says it.
      } else {
        parts.push(T('pdp.announce_call_to_order', { hotline: this.hotline() }));
      }

      /* The balance is said HERE and nowhere else. assets/upgrade-deal.js
         deliberately stays silent when only the target changed (see
         announceDeal there), because that change IS this click and this
         sentence already carries it. Two regions firing for one click is the
         spam this whole design exists to avoid. */
      try {
        var deal = window.UpgradeDeal;
        var t = (deal && deal.totals) ? deal.totals() : null;
        if (t && t.covered) {
          parts.push(T('pdp.announce_trade_covered'));
        } else if (t) {
          parts.push(T('pdp.announce_trade_balance', { balance: money(t.pay) }));
        }
      } catch (e) { /* shared state unavailable -- the price sentence still stands */ }

      phrase = parts.join(' ');
    }

    // A render that produces the same words says nothing. repaintPrices() and
    // the Shopify product fetch both land here routinely without changing what
    // the customer can perceive.
    if (phrase === this._lastAnnounced) { return; }
    this._lastAnnounced = phrase;

    // Primed, not spoken. Keeping _lastAnnounced honest before the first real
    // interaction means the customer's first action is not answered with a
    // repeat of what was already on screen when the page loaded.
    if (!this._userActed) { return; }

    /* One announcement per RESTING state. handleRadioKeydown() clicks a pill on
       every arrow key, so Gold -> Diamond is two complete updateAll()s inside
       200ms, and ug:prices:ready can land on top of a click. Without this timer
       the region speaks once per keypress. */
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(function () {
      el.textContent = phrase;   // textContent, never innerHTML
    }, 180);
  };

  // Lynkrs batch 2, PG-PDP-1 (W4, 6 Oct 2026). On a phone the buy box's own
  // Add to cart sits well below the first screen. The bar used to show only
  // once the buy box had scrolled away, so a customer who picked a grade in
  // the first screen saw neither a price nor a button to act on. It now also
  // shows as soon as a grade is picked while that button is not fully on
  // screen, and steps aside whenever it is. Before a grade is picked the
  // grade cards (with their prices) are the first action, as before. Phones
  // only: from 750px it never shows (upgrade-core.css hides it too).
  UgPdp.prototype.observeSticky = function () {
    var self = this;
    var sticky = qs(this.root, '[data-ug-pdp-sticky]');
    var buybox = qs(this.root, '[data-ug-pdp-buybox]');
    if (!sticky || !buybox || !('IntersectionObserver' in window)) { return; }
    var addcart = qs(this.root, '[data-ug-pdp-addcart]');
    this._sticky = { el: sticky, past: false, buyInView: true };
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === buybox) {
          self._sticky.past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else if (entry.target === addcart) {
          self._sticky.buyInView = entry.isIntersecting && entry.intersectionRatio >= 0.99;
        }
      });
      self.syncSticky();
    }, { threshold: [0, 1] });
    observer.observe(buybox);
    if (addcart) { observer.observe(addcart); }
    window.addEventListener('resize', function () { self.syncSticky(); });
    // The trade-in bar (upgrade-deal.js) comes and goes by toggling
    // html.ug-has-deal; re-check whenever that class list changes, so the
    // early bar never sits on top of it (checker C3, 6 Oct 2026).
    if ('MutationObserver' in window) {
      new MutationObserver(function () { self.syncSticky(); })
        .observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
  };

  UgPdp.prototype.syncSticky = function () {
    var s = this._sticky;
    if (!s || !s.el) { return; }
    // matchMedia, not innerWidth: innerWidth is the VISUAL viewport and
    // diverges from the layout viewport the `min-width: 750px` rules use
    // whenever the page has horizontal overflow. The CSS that hides this
    // bar and the JS that hides it must agree, or one of them acts on a
    // width the other never sees.
    var isMobile = !window.matchMedia('(min-width: 750px)').matches;
    var picked = isValidGrade(this.state.selectedGrade);
    // With a trade-in in session the deal bar already holds the foot of the
    // screen with a price and an action. The early (grade-picked) bar stays
    // off then, so the two never stack over the grade cards just tapped
    // (checker C3); once the buy box has scrolled past it shows as before W4.
    var de = document.documentElement;
    var hasDeal = !!(de && de.classList && de.classList.contains('ug-has-deal'));
    this.toggleSticky(s.el, !!(isMobile && (s.past || (picked && !s.buyInView && !hasDeal))));
  };

  // 2.0 (13 Oct 2026, D4). The bar used to appear and vanish by flipping
  // `hidden` (display:none), so it popped. `hidden` is still what assistive
  // technology and the tab order see: it is cleared BEFORE the bar slides up
  // (.is-shown, upgrade-pdp.css 24.4: transform + opacity) and set only AFTER
  // it has slid away. Same condition as before, only the timing is new.
  UgPdp.prototype.toggleSticky = function (el, show) {
    clearTimeout(this._stickyTimer);
    if (show) {
      if (el.hidden) { el.hidden = false; void el.offsetWidth; }
      el.classList.add('is-shown');
      return;
    }
    if (el.hidden) { el.classList.remove('is-shown'); return; }
    el.classList.remove('is-shown');
    this._stickyTimer = setTimeout(function () {
      if (!el.classList.contains('is-shown')) { el.hidden = true; }
    }, 180);
  };

  UgPdp.prototype.renderNotFound = function () {
    // "or message us on WhatsApp" was plain text with no link behind it, and
    // no WhatsApp number exists on this store: every whatsapp_number setting
    // in the theme is an empty string, which is why buildContact() falls back
    // to tel: everywhere else. So this sentence invited a customer who had
    // just hit a dead end to use a second channel that also does not exist.
    // It now routes through the same buildContact() as renderError below, so
    // it names WhatsApp only if a number is ever configured, and until then
    // names the one contact route that is confirmed to work.
    // This replaces root.innerHTML wholesale, so every data-ug-pdp-* hook the
    // repaint writes into is gone from here on. Stand the repaint down.
    this.state.painted = false;
    this.releasePending();
    var contact = buildContact(this.config, T('pdp.not_found_whatsapp_message'));
    var linkText = contact.isWhatsapp ? T('pdp.not_found_contact_whatsapp') : T('pdp.not_found_contact_call', { hotline: (this.config.hotlineNumber || '19759') });
    this.root.innerHTML =
      '<div class="ug-container ug-pdp__empty">' +
        '<h1>' + escapeHtml(T('pdp.not_found_heading')) + '</h1>' +
        '<p>' + T('pdp.not_found_body', {
          browse_link: '<a href="' + escapeHtml(this.config.collectionUrl) + '">' + escapeHtml(T('pdp.not_found_browse')) + '</a>',
          contact_link: '<a href="' + escapeHtml(contact.href) + '">' + escapeHtml(linkText) + '</a>'
        }) + '</p>' +
      '</div>';
  };

  UgPdp.prototype.renderError = function () {
    // Same reason as renderNotFound: the hooks are about to be destroyed.
    // This path is also reachable AFTER a successful paint (anything thrown
    // inside the .then chain lands in the same .catch), which is precisely
    // the case where a later repaint would write into detached DOM.
    this.state.painted = false;
    this.releasePending();
    var contact = buildContact(this.config, T('pdp.error_whatsapp_message'));
    var linkText = contact.isWhatsapp ? T('pdp.error_contact_whatsapp') : T('pdp.error_contact_call', { hotline: (this.config.hotlineNumber || '19759') });
    this.root.innerHTML =
      '<div class="ug-container ug-pdp__empty">' +
        '<h1>' + escapeHtml(T('pdp.error_heading')) + '</h1>' +
        '<p>' + T('pdp.error_body', {
          contact_link: '<a href="' + escapeHtml(contact.href) + '">' + escapeHtml(linkText) + '</a>'
        }) + '</p>' +
      '</div>';
  };

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-ug-pdp]');
    if (!root) { return; }
    new UgPdp(root).init();
  });

  // Batch E1 (6 Oct 2026). The theme editor re-renders a section in place
  // each time the content lead changes one of its fields or blocks, and
  // fires this event on it; without a fresh engine the preview kept only the
  // server render (no photo, no specs, no colours, no rail) until a reload.
  // Shopify fires it only inside the theme editor, never for a customer
  // (the same hook assets/upgrade-catalogue.js and upgrade-calc.js use).
  document.addEventListener('shopify:section:load', function (event) {
    var root = (event && event.target && typeof event.target.querySelector === 'function')
      ? event.target.querySelector('[data-ug-pdp]') : null;
    if (!root) { return; }
    new UgPdp(root).init();
  });
})();
